// ============================================
// 유틸리티 (AiManager)
// ============================================

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ERROR_CONFIG, FALLBACK_MESSAGES, SECRET_KEY, GCM_IV_LENGTH, TOOL_CALLING_LOG, APP_REGION } from '../Model/aiConfig.js';
import { FIELD_REGISTRY, getFieldNames, resolveToItemKey, getDisplayLabel, getFieldLabel, normalizeAvailableFields, isSystemPlaceholderValue } from './aiFieldConfig.js';
import { addAge, addRemainingDays, addRemainingDaysNoSort } from './aiComputeHandlers.js';
// TL-1 — placeholder substitution / column labels / unit dimensions / language / ambiguous count keys 자산은 Agent/Tools+Config로 이동.
// aiUtils는 backward-compat re-export로 호출자 (useAiChat/AgentSession/nodeAgent) import path 변경 0 유지.
import { LABEL_OVERRIDES } from '../../Agent/Config/columnLabels.js';
import { UNIT_BY_DIMENSION } from '../../Agent/Config/unitsByDimension.js';
import { detectResponseLanguage } from '../../Agent/Tools/responseLanguage.js';
import { AMBIGUOUS_COUNT_KEYS, isAmbiguousCountKey } from '../../Agent/Tools/ambiguousCountKeys.js';
import { substitutePlaceholders, stripPlaceholderArtifacts } from '../../Agent/Tools/placeholderSubstitute.js';
// [L3-COMPUTE-MIN-001] extreme intent ("최고/최저/가장/제일/최다/최소" + 단일 entity) — autoGenerateDisplay에서 cap-aware intro/outro 처리 분기용
// [L9a-MEMBER-005 cycle 1] explicit top-N intent ("상위/탑/top/먼저/처음 + N명/건/개") — 동일 분기 (intro inflation skip)
import { detectExtremeIntent, detectExplicitTopNIntent } from '../../Agent/Tools/extremeIntentGuide.js';
export { LABEL_OVERRIDES, UNIT_BY_DIMENSION, detectResponseLanguage, AMBIGUOUS_COUNT_KEYS, isAmbiguousCountKey, stripPlaceholderArtifacts };
// 기존 import 호환: substituteOutroPlaceholders 이름 보존 (운영 path 5곳 import 변경 0).
export { substitutePlaceholders as substituteOutroPlaceholders };

// JSON 파싱 — 텍스트 안에 JSON이 섞여 있어도 추출 //
export function parseJsonSafely(rawText) {
  if (typeof rawText !== 'string') return null;
  const trimmed = rawText.trim();

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try { return JSON.parse(trimmed); }
    catch { /* 아래로 */ }
  }

  const jsonStart = trimmed.indexOf('{');
  if (jsonStart >= 0) {
    const jsonCandidate = trimmed.slice(jsonStart);
    try { return JSON.parse(jsonCandidate); }
    catch { /* 아래로 */ }

    let depth = 0;
    for (let i = 0; i < jsonCandidate.length; i++) {
      if (jsonCandidate[i] === '{') depth++;
      if (jsonCandidate[i] === '}') depth--;
      if (depth === 0) {
        try { return JSON.parse(jsonCandidate.slice(0, i + 1)); }
        catch { return null; }
      }
    }
  }

  return null;
}

// OpenAI 에러 분류 //
export function classifyOpenAIError(error) {
  if (error.message === 'Timeout' || error.code === 'TIMEDOUT') return 'OPENAI_TIMEOUT';
  const status = error.status || error.statusCode;
  if (status === 504) return 'OPENAI_TIMEOUT';
  if (status === 429) return 'OPENAI_RATE_LIMIT';
  if (status === 400) return 'OPENAI_CONTEXT_LIMIT';
  if (status >= 500) return 'OPENAI_SERVER_ERROR';
  return 'OPENAI_SERVER_ERROR';
}

// ============================================
// 메시지 히스토리 관리 (atomic unit 기반)
// ============================================

// messages를 atomic unit으로 그룹핑 //
// tool_calls assistant + 모든 tool 응답은 하나의 unit으로 묶임
export function groupIntoUnits(messages) {
  const units = [];
  let current = [];
  let pendingToolCallIds = new Set();

  for (const msg of messages) {
    if (msg.role === 'system') {
      if (current.length > 0) { units.push([...current]); current = []; pendingToolCallIds.clear(); }
      units.push([msg]);
      continue;
    }

    if (msg.role === 'user') {
      if (current.length > 0) { units.push([...current]); current = []; pendingToolCallIds.clear(); }
      units.push([msg]);
      continue;
    }

    current.push(msg);

    if (msg.role === 'assistant' && msg.tool_calls) {
      pendingToolCallIds = new Set(msg.tool_calls.map(tc => tc.id));
    }

    if (msg.role === 'tool' && pendingToolCallIds.size > 0) {
      pendingToolCallIds.delete(msg.tool_call_id);

      if (pendingToolCallIds.size === 0) {
        units.push([...current]);
        current = [];
      }
    }

    // tool_calls 없는 assistant (텍스트만) → 단독 unit //
    if (msg.role === 'assistant' && !msg.tool_calls) {
      units.push([...current]);
      current = [];
    }
  }

  if (current.length > 0) units.push(current);

  return units;
}

// atomic unit 단위로 트리밍 + 오래된 tool output 축약 //
export function compactMessages(messages, maxMessages = 30, keepRecentUnits = 8) {
  if (messages.length <= maxMessages) return messages;

  const units = groupIntoUnits(messages);

  // system unit 분리 (처음 연속 system만) //
  const systemUnits = [];
  const nonSystemUnits = [];

  for (const unit of units) {
    if (unit.length === 1 && unit[0].role === 'system' && nonSystemUnits.length === 0) {
      systemUnits.push(unit);
    }
    else {
      nonSystemUnits.push(unit);
    }
  }

  if (nonSystemUnits.length <= keepRecentUnits) return messages;

  const oldUnits = nonSystemUnits.slice(0, -keepRecentUnits);
  const recentUnits = nonSystemUnits.slice(-keepRecentUnits);

  // oldUnits에서 가장 최근 Buffer State system 메시지 추출 //
  let latestBufferState = null;

  for (const unit of oldUnits) {
    if (unit.length === 1 && unit[0].role === 'system'
        && unit[0].content?.includes('[Buffer State]')) {
      latestBufferState = unit[0];
    }
  }

  // 오래된 unit의 tool result content를 축약 //
  const compactedOld = [];

  for (const unit of oldUnits) {
    // Buffer State unit은 별도 보존하므로 중복 제외 //
    if (latestBufferState && unit.length === 1 && unit[0] === latestBufferState) continue;

    for (const msg of unit) {
      if (msg.role === 'tool') {
        const parsed = parseJsonSafely(msg.content);
        let summary;

        if (parsed) {
          const count = parsed.count ?? parsed.mergedCount ?? null;
          const success = parsed.success ?? true;
          summary = JSON.stringify({ success, count: count ?? '?' });
        }
        else {
          summary = msg.content?.slice(0, 50) || '(result)';
        }

        compactedOld.push({ ...msg, content: summary });
      }
      else {
        compactedOld.push(msg);
      }
    }
  }

  const bufferStateArr = latestBufferState ? [latestBufferState] : [];

  const result = [
    ...systemUnits.flat(),
    ...bufferStateArr,
    ...compactedOld,
    ...recentUnits.flat(),
  ];

  // 여전히 초과하면 오래된 unit 자체를 제거 //
  if (result.length > maxMessages) {
    const recentFlat = recentUnits.flat();

    return sanitizeMessages([...systemUnits.flat(), ...bufferStateArr, ...recentFlat]);
  }

  return sanitizeMessages(result);
}

// orphan tool/tool_calls 제거 안전망 //
export function sanitizeMessages(messages) {
  // 1단계: orphan tool 제거 //
  const toolCallIds = new Set();
  const result = [];

  for (const msg of messages) {
    if (msg.role === 'assistant' && msg.tool_calls) {
      msg.tool_calls.forEach(tc => toolCallIds.add(tc.id));
    }

    if (msg.role === 'tool') {
      if (!toolCallIds.has(msg.tool_call_id)) continue;
    }

    result.push(msg);
  }

  // 2단계: orphan tool_calls 제거 //
  const toolResponseIds = new Set();

  for (const msg of result) {
    if (msg.role === 'tool') toolResponseIds.add(msg.tool_call_id);
  }

  for (let i = result.length - 1; i >= 0; i--) {
    const msg = result[i];

    if (msg.role === 'assistant' && msg.tool_calls) {
      const allResolved = msg.tool_calls.every(tc => toolResponseIds.has(tc.id));

      if (!allResolved) {
        if (msg.content) {
          result[i] = { role: 'assistant', content: msg.content };
        }
        else {
          result.splice(i, 1);
        }
      }
    }
  }

  return result;
}

// 백엔드 에러 분류 //
export function classifyBackendError(error, httpStatus) {
  if (error?.message === 'Timeout') return 'BACKEND_TIMEOUT';
  if (httpStatus === 504) return 'BACKEND_TIMEOUT';
  if (httpStatus === 401) return 'BACKEND_AUTH_ERROR';
  if (httpStatus === 403) return 'BACKEND_PERMISSION_ERROR';
  if (httpStatus === 404) return 'BACKEND_NOT_FOUND';
  if (httpStatus >= 500) return 'BACKEND_NETWORK_ERROR';
  return 'BACKEND_NETWORK_ERROR';
}

// 암호화/복호화 //
export async function decrypt(encryptedBase64) {
  const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
  const iv = combined.slice(0, GCM_IV_LENGTH);
  const ciphertextWithTag = combined.slice(GCM_IV_LENGTH);
  const encoder = new TextEncoder();
  const keyData = await window.crypto.subtle.digest('SHA-256', encoder.encode(SECRET_KEY));
  const cryptoKey = await window.crypto.subtle.importKey('raw', keyData, { name: 'AES-GCM' }, false, ['decrypt']);
  const decryptedBuffer = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, ciphertextWithTag);
  return new TextDecoder().decode(decryptedBuffer);
}

// 재시도 백오프 //
export function getBackoffDelay(retryCount) {
  return 1000 * Math.pow(2, retryCount);
}

// 폴백 메시지 //
export function getFallbackMessage(flag) {
  if (flag <= 1) return FALLBACK_MESSAGES.LEVEL_1;
  if (flag === 2) return FALLBACK_MESSAGES.LEVEL_2;
  return FALLBACK_MESSAGES.LEVEL_3;
}

// 대화 히스토리 포맷 //
export function formatRecentConversation(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return '';
  return messages.map(msg => `[${msg?.isAi ? 'assistant' : 'user'}] ${msg?.text ?? ''}`).join('\n');
}

// Base64 이미지 제거 //
export function stripBase64Values(obj) {
  if (Array.isArray(obj)) return obj.map(stripBase64Values);
  if (obj && typeof obj === 'object') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) result[key] = stripBase64Values(value);
    return result;
  }
  if (typeof obj === 'string' && obj.startsWith('data:image/')) return '';
  return obj;
}


// ============================================
// call_api post-tool action guide — itemsLen 분기 + visualizationOnly intention 분기
// ============================================
// [anchor 15] LLM이 받는 tool result message의 NEXT STEP 가이드.
// nodeAgent.mjs / AgentSession.js 양쪽에서 공통 호출 (dual sync class 차단)
//
// EMPTY_MEMBER_SEARCH (SYN-CHECK_MEMBER+REFUND_DETAIL-3 잔존 분리):
//   CHECK_MEMBER_SEARCH는 *_DETAIL intention의 prior step. 0건은 "데이터 부재"가 아니라
//   "회원을 못 찾음" 의미. 일반 EMPTY 가이드("해당 데이터가 없어요")는 사용자가
//   회원명을 지정해야 함을 인지하지 못하게 함 → 회원명 재요청 가이드로 분기.
//   harness-pattern (intention metadata 기반), 사용자 발화 regex 추격 X (§1.3 준수).
export const ACTION_GUIDE = Object.freeze({
  EMPTY: '데이터 부재. finish()로 "해당 데이터가 없어요" 자연어 안내. show_display 호출 금지 (빈 표 차단).',
  EMPTY_MEMBER_SEARCH: '회원 검색 결과 0건 = 해당 회원을 찾지 못함 (데이터 부재 아님). finish()로 "해당 회원을 찾지 못했어. 이름이나 전화번호를 다시 알려주면 바로 확인해줄게" 자연어 안내. show_display 호출 금지.',
  SINGLE: '단일 레코드. finish()로 자연어 응답 (단일 설정/상세/회원 카드 등) 또는 다중 컬럼 표가 의미 있을 때만 show_display.',
  // [DUPLICATE MEMBER SEARCH GUARD]
  //   CHECK_MEMBER_SEARCH는 *_DETAIL intention의 prior_endpoint (회원 특정 prerequisite).
  //   N(>=2)건 동명이인 발견 시 — 현재 단계에서 회원 검색 list만 보여주고 종료하면
  //   사용자가 요청한 원래 *_DETAIL 의도(예: 수업 참여 이력/회원권 정보 등)가 미달성.
  //   회원 disambiguation 안내 + 원래 의도 보존 가이드.
  //   harness-pattern (intention metadata 기반), case ID/한국어 input keyword 의존 X.
  MULTI_MEMBER_SEARCH: '회원 검색 결과 N건 (동명이인). CHECK_MEMBER_SEARCH는 *_DETAIL intention(회원권/출석/포인트/락커/수업 참여 등) 호출의 prior step이므로 N건 결과 자체를 최종 응답으로 끝내면 사용자가 원래 요청한 detail 정보가 미달성됩니다. show_display로 회원 목록을 보여주되, finish 또는 outro에 "동명이인 N명이 있어요. {원래 의도(예: 수업 참여 이력)}를 확인하려면 어느 회원인지 생년월일/전화번호로 알려주세요" 라고 회원 disambiguation 안내. show_display 단독으로 끝내지 마세요.',
  DEFAULT: '사용자 요청에 따라 filter/sort/compute/derive/customize_display로 가공하거나, 바로 display 응답하세요.',
});

export function selectActionGuide(itemsLen, intentionMeta, callApiGuide, intention) {
  if (itemsLen === 0) {
    if (intention === 'CHECK_MEMBER_SEARCH') return ACTION_GUIDE.EMPTY_MEMBER_SEARCH;
    return ACTION_GUIDE.EMPTY;
  }
  if (itemsLen === 1) return ACTION_GUIDE.SINGLE;
  // [DUPLICATE MEMBER SEARCH] CHECK_MEMBER_SEARCH N(>=2)건 — disambiguation 가이드 //
  //   구조 기반: intention 이름 정확 매칭. itemsLen >= 2 구조 조건만 사용.
  if (intention === 'CHECK_MEMBER_SEARCH') return ACTION_GUIDE.MULTI_MEMBER_SEARCH;
  if (intentionMeta?.visualizationOnly && callApiGuide) return callApiGuide;
  return ACTION_GUIDE.DEFAULT;
}

// ============================================
// SINGLE-PERIOD AGGREGATE GUARD — generic CALC_INCONSISTENT prevention
// ============================================
// items.length === 1 + temporal-period row + summary has `avg*`/`average*` keys
//   → these averages are degenerate (avg of 1 period = that single period's value)
//   → LLM must NOT label them "월평균/일평균/연평균/주평균" — say the period's value
// Generic: structural detection (field name pattern + key prefix), no intention/case hardcode.
// Dual-sync helper: nodeAgent.mjs + AgentSession.js both call.
const _TEMPORAL_PERIOD_FIELDS = new Set([
  'month', 'day', 'week', 'year', 'quarter', 'date', 'period', 'hour', 'weekday',
  'monthLabel', 'dayLabel', 'weekLabel', 'yearLabel'
]);
const _PERIOD_LABEL_KO = { month: '월', day: '일', week: '주', year: '연', quarter: '분기', date: '일자', period: '기간', hour: '시간', weekday: '요일' };
const _AVG_KEY_RE = /^(avg|average)([A-Z_]|$)/;

export function buildSinglePeriodAvgHint(items, summary) {
  if (!Array.isArray(items) || items.length !== 1) return '';
  if (!summary || typeof summary !== 'object') return '';
  const row = items[0];
  if (!row || typeof row !== 'object') return '';
  // detect temporal period field on row
  const periodField = Object.keys(row).find(k => _TEMPORAL_PERIOD_FIELDS.has(k));
  if (!periodField) return '';
  // detect avg/average summary keys with numeric values
  const avgKeys = Object.entries(summary)
    .filter(([k, v]) => typeof v === 'number' && _AVG_KEY_RE.test(k))
    .map(([k]) => k);
  if (avgKeys.length === 0) return '';
  const periodKo = _PERIOD_LABEL_KO[periodField] || periodField;
  const placeholders = avgKeys.map(k => `{{SUMMARY:${k}}}`).join(', ');
  const rowVal = row[periodField];
  return ` [SINGLE-PERIOD AGG GUARD] items.length=1 (단일 ${periodKo}: ${rowVal}). Summary ${avgKeys.join('/')} (avg/average prefix) equal that single ${periodKo}'s value — NOT a true multi-${periodKo} average. In intro/outro/finish text, DO NOT label ${placeholders} as "월평균/일평균/주평균/연평균/평균 ${periodKo} X" — describe as that single ${periodKo}'s actual figure (e.g. "${rowVal} ${periodKo} 매출은 ...원"). To compute a real multi-period average, request data spanning >1 ${periodKo}.`;
}


// ============================================
// Display 자동 생성
// ============================================
// 버퍼 데이터 + availableFields 기반으로 display 구성
// LLM은 intro/outro만 제공, 나머지는 코드가 결정

// availableFields 라벨에서 단위 토큰 추출 — string("재등록률(%)") 또는 object({ label, dimension }) 호환
// (a) label 후미 (...) 안 단위 토큰 우선 — backend 정의에 신뢰
// (b) 없으면 dimension 메타 ko 매핑 fallback
// returns '' if no unit (e.g. count fields, names). Known unit set: %/원/명/회/개/건/분/시간/KRW/円/件/回/個
const _UNIT_TOKEN_RE = /\((%|원|명|회|개|건|시간|분|KRW|円|件|回|個)\)\s*$/;
export function extractUnitFromFieldLabel(labelOrObj) {
  if (!labelOrObj) return '';
  const label = typeof labelOrObj === 'string' ? labelOrObj : (labelOrObj.label || '');
  if (label) {
    const m = label.match(_UNIT_TOKEN_RE);
    if (m) return m[1];
  }
  if (typeof labelOrObj === 'object' && labelOrObj.dimension) {
    const u = UNIT_BY_DIMENSION[labelOrObj.dimension]?.ko;
    if (u) return u.trim() || u;
  }
  return '';
}

// LABEL_OVERRIDES (컬럼 라벨 매핑) / detectResponseLanguage (응답 언어 감지)는
// TL-1에서 src/Agent/Config/columnLabels.js + src/Agent/Tools/responseLanguage.js로 이동.
// 파일 상단 import + re-export로 호환 유지.

// 표시하지 않을 필드 (ID, 내부용, 매장 정보, 메타) //
const EXCLUDED_FIELDS = [
  /^.*[Ii]d$/, /^.*Id$/, /userId/, /memberId/, /storeId/,
  /profile/i, /image/i, /photo/i, /thumbnail/i,
  /phone/i, /mobile/i, /tel$/i, /contactNumber/i,
  /password/, /token/, /secret/,
  /^raw/,
  /createdAt/, /updatedAt/, /deletedAt/,
  /^store/, /storeName/, /storeAddress/,
  /countryCode/, /redundancyDay/,
  /totalCount/, /^count$/,
];

// 우선 표시 필드 (앞에 배치) //
const PRIORITY_FIELDS = [
  ...getFieldNames('name'),
  'viewMembershipName', 'membershipName', 'memberShipName',
  'lessonName', 'className',
  'viewEndDate', 'endDate',
  'viewStartDate', 'startDate',
];

// 필수 필드 — aiFieldConfig의 aliases 기반 //
const NAME_FIELDS = getFieldNames('name');
const PHONE_FIELDS_LIST = getFieldNames('phone');
const PHONE_FIELDS_SET = new Set(PHONE_FIELDS_LIST);
const ESSENTIAL_FIELDS = [
  // 식별자: memberId(long PK)는 ALIVE_LIST 등에 있고, userId(linkCoachId, string)는 ATTENDANCE_LIST 등에 있음.
  // 멀티턴 cross-reference 매칭 시 양쪽 식별자 모두 lite items에 보존되어야 merge_slots 자동 매칭이 동작.
  'memberId', 'userId', 'teacherId', ...getFieldNames('profile'),
  ...NAME_FIELDS,
];

// [1-T1] 가격 field name pattern — formatValue 가격 콤마 자동 추가 룰 매칭용
//   매출/결제/회원권 등 numeric large int field 정밀 감지. raw ID/count field 영향 차단
//   sales$ 추가 (case-insensitive `i` flag로 totalSales/netSales 매칭) — L5-SALES-STATISTICS-001 회귀 잔존 보강
export const PRICE_FIELDS_RE = /price$|amount$|revenue$|cost$|fee$|sales$|paid|cash|card/i;

// [L4-DERIVE-ADD] price-like 판정 helper — fieldKey pattern 일치 또는 fieldMeta.unit === 'currency'
//   derive 필드 (LLM 한국어 명명) 가 operand price-like 필드 propagation 받은 경우, fieldKey regex 미일치라도
//   fieldMeta로 currency 인식 → comma format 적용. PRICE_FIELDS_RE 동일 패턴이라 dual sync 자동 유지.
export function isPriceLikeField(fieldKey, fieldMeta) {
  if (fieldKey && typeof fieldKey === 'string' && PRICE_FIELDS_RE.test(fieldKey)) return true;
  if (fieldMeta && typeof fieldMeta === 'object' && fieldMeta.unit === 'currency') return true;
  return false;
}

// 값 포맷팅 — fieldKey 컨텍스트로 phone 분기 결정 //
// phone 포맷은 fieldKey가 PHONE_FIELDS_SET에 포함될 때만 적용 (FIELD_REGISTRY.phone aliases 기반).
// libphonenumber-js의 한국 phone 룰이 02xxxxxxx 패턴(매출 8자리 등)도 valid 판정하므로 필드 메타 분기 필수.
// fieldMeta (선택): availableFields[fieldKey]의 메타 object — { label, unit } 등. derive 필드의 price-like
//   propagation 감지에 사용. fieldKey regex 미일치라도 fieldMeta.unit === 'currency' 면 comma format 적용.
export function formatValue(val, fieldKey, fieldMeta) {
  if (val == null) return '';
  if (typeof val === 'boolean') return val ? '예' : '아니오';
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) return val.split('T')[0];
  if (Array.isArray(val)) return `${val.length}개 항목`;
  if (typeof val === 'object') return '';
  if (fieldKey && PHONE_FIELDS_SET.has(fieldKey) && (typeof val === 'string' || typeof val === 'number')) {
    const s = String(val);
    if (/^[\d+\-\s()]+$/.test(s) && /\d{8,}/.test(s)) {
      const parsed = parsePhoneNumberFromString(s, APP_REGION);
      if (parsed && parsed.isValid()) return parsed.formatNational();
    }
  }
  // [P2-4 anchor 8] generic YYYYMMDD 8자리 numeric date → YYYY-MM-DD ISO 변환 (birthDay 등)
  // false positive 차단: 1900-2100 year + 01-12 month + 01-31 day 검증 통과 시만
  // fieldKey 무관 value-form 기반 — backend가 date 의도로 8자리 numeric 반환한 모든 필드 대응 (single source)
  if ((typeof val === 'string' || typeof val === 'number') && !Array.isArray(val)) {
    const s = String(val);
    if (/^\d{8}$/.test(s)) {
      const yyyy = parseInt(s.slice(0, 4), 10);
      const mm = parseInt(s.slice(4, 6), 10);
      const dd = parseInt(s.slice(6, 8), 10);
      if (yyyy >= 1900 && yyyy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
        return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
      }
    }
  }
  // [1-T1] 가격 콤마 자동 추가 — price-like field name + numeric ≥1000 → locale-aware 콤마
  //   field name pattern 정밀 매칭으로 raw int 다른 field (memberId/_count 등) 영향 차단
  //   단위 ('원' 등) 추가 X — 한국어 hard-code 회피, column 헤더(availableFields description) 또는 LLM outro 자율 영역
  // [L4-DERIVE-ADD] fieldMeta.unit === 'currency' propagation 도 인식 — Korean named derive field 일관
  if (isPriceLikeField(fieldKey, fieldMeta) && typeof val === 'number' && Math.abs(val) >= 1000) {
    return val.toLocaleString();
  }
  // [L4-DERIVE-PERCENT-FORMAT M5] percent_derive unit propagation 인식 — divide operation + percent semantic name 시 deriveField가 부여 //
  //   raw decimal ratio (0.09) → '9%' format. round(val*1000)/10 로 1 자릿수 소수점 보존 (0.09 → 9.0 → 9).
  //   numeric only, 일반 rate field (attendanceRate=85 같은 backend 0-100 정수) 는 unit='percent_derive' 받지 않으므로 영향 X.
  //   Math.abs check 없이 0 입력 시 '0%' 정상 표기.
  if (fieldMeta && typeof fieldMeta === 'object' && fieldMeta.unit === 'percent_derive' && typeof val === 'number' && Number.isFinite(val)) {
    const pct = Math.round(val * 1000) / 10; // 0.09 → 9, 0.156 → 15.6
    // 정수면 소수점 제거 ('9%' vs '9.0%')
    const display = Number.isInteger(pct) ? pct.toString() : pct.toFixed(1);
    return `${display}%`;
  }
  // URL → "(사진 등록됨)" 변환 — profileImage 등 raw URL 노출 차단 (도메인 무관)
  if (typeof val === 'string' && /^https?:\/\//.test(val)) {
    return '(사진 등록됨)';
  }

  // [Cat8-REPEAT] 표 셀 장문 truncation 안전망 — column drop을 통과한 개별 셀 값 200자 초과 시 말줄임
  const result = String(val);
  if (result.length > 200) {
    return result.slice(0, 197) + '...';
  }
  return result;
}

// dot path 경로로 값 조회 //
function getByPath(obj, path) {
  if (obj == null) return undefined;
  if (path in obj) return obj[path];
  if (!path.includes('.')) return undefined;
  const traversed = path.split('.').reduce((o, k) => o?.[k], obj);
  if (traversed !== undefined) return traversed;
  return obj[path.split('.').pop()];
}

// 객체 내부 깊이 탐색 //
function deepGet(obj, key) {
  if (obj == null || typeof obj !== 'object') return undefined;
  if (key in obj) return obj[key];
  for (const val of Object.values(obj)) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const found = deepGet(val, key);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

// 필드가 제외 대상인지 확인 //
function isExcluded(field) {
  return EXCLUDED_FIELDS.some(pattern => pattern.test(field));
}

// 긴 텍스트 필드 자동 감지 (테이블 셀 부적합) //
const MAX_CELL_LENGTH = 80;
export function isLongTextField(field, sampleItem) {
  if (!sampleItem) return false;
  const val = sampleItem[field];
  return typeof val === 'string' && val.length > MAX_CELL_LENGTH;
}

// userInput 키워드 매칭으로 자동 정렬 필드 결정 //
function findAutoSort(userInput, sampleItem) {
  if (!userInput || !sampleItem) return null;
  const keys = Object.keys(sampleItem);

  // 모든 매칭 후보 수집 (longest keyword first — substring 충돌 시 더 specific한 매칭 우선)
  // 예: "예약 출석률" 입력 → ['출석', '예약 출석률'] 중 '예약 출석률'(6자) 매칭 우선 → attendedReservationRatio
  const candidates = [];
  for (const [field, config] of Object.entries(FIELD_REGISTRY)) {
    if (!config.keywords || !config.defaultSort) continue;
    for (const kw of config.keywords) {
      if (userInput.includes(kw)) {
        candidates.push({ field, kw, len: kw.length, config });
      }
    }
  }
  candidates.sort((a, b) => b.len - a.len);

  for (const cand of candidates) {
    const allNames = [cand.field, ...(cand.config.aliases || [])];
    const matched = allNames.find(f => keys.includes(f));
    if (matched) return { field: matched, direction: cand.config.defaultSort };
  }
  return null;
}

// [SORT_INTENT_GUARD] userInput에 modifier(많은/적은/높은/낮은) 인식 + FIELD_REGISTRY keyword 매칭으로 의도 sort field+direction 결정 //
// findAutoSort보다 strict: keyword length 3 이상만 신뢰 (false positive 차단). modifier 없으면 defaultSort 사용. //
// 사용처: executeSort guard — LLM이 user keyword 무시하고 다른 field로 sort 호출 시 override 판정. //
// 본질: L31-INFORMAL-003 "참여 인원 많은 수업" + LLM sort(utilizationRate, asc) — keyword "참여 인원"이 totalReservations 의도 + "많은"이 desc 의도 명시 → override //
export function detectUserSortIntent(userInput, sampleItem) {
  if (!userInput || !sampleItem) return null;
  const keys = Object.keys(sampleItem);

  // 1. modifier(direction) 검출 — longest pattern first
  // desc: 많은/많이/많아/높은/높아/높이/큰/최다/최고/최상/상위/제일/가장 + asc 단어 부재
  // asc: 적은/적게/적어/낮은/낮아/낮이/작은/최저/하위/가장 적/제일 적
  let direction = null;
  const ascRe = /(적은|적게|적어|낮은|낮아|낮이|작은|최저|하위)/;
  const descRe = /(많은|많이|많아|높은|높아|높이|큰|최다|최고|최상|상위|TOP)/;
  if (ascRe.test(userInput)) direction = 'asc';
  else if (descRe.test(userInput)) direction = 'desc';

  // 2. keyword 매칭 (longest-first; length >= 3 strict)
  const candidates = [];
  for (const [field, config] of Object.entries(FIELD_REGISTRY)) {
    if (!config.keywords || !config.defaultSort) continue;
    for (const kw of config.keywords) {
      if (kw.length < 3) continue; // strict — 짧은 keyword false positive 차단
      if (userInput.includes(kw)) {
        candidates.push({ field, kw, len: kw.length, config });
      }
    }
  }
  candidates.sort((a, b) => b.len - a.len);

  for (const cand of candidates) {
    const allNames = [cand.field, ...(cand.config.aliases || [])];
    const matched = allNames.find(f => keys.includes(f));
    if (matched) {
      return {
        field: matched,
        direction: direction || cand.config.defaultSort,
        matchedKeyword: cand.kw,
        modifierDetected: !!direction,
      };
    }
  }
  return null;
}

// 현재 컨텍스트의 표시 필수 컬럼 집합 — sort/filter 동적 + columnPrefs.include 영구 의도 //
// selectColumns의 focus 우선순위 / autoGenerateDisplay의 uniform 제거 보호에 동일 사용 //
function getFocusFields(buffer) {
  const fields = new Set();
  if (buffer?.appliedSort?.field) fields.add(buffer.appliedSort.field);
  for (const f of buffer?.appliedFilters || []) {
    if (f.field) fields.add(f.field);
  }
  for (const f of buffer?.columnPrefs?.include || []) fields.add(f);
  return fields;
}

// 사용자 input keyword에 매칭하는 sampleItem 필드 추출 (longest first) //
// findAutoSort와 같은 알고리즘 — alias 풀어서 sampleItem에 존재하는 필드만 반환 //
// 반환은 flat 필드명 array — 단, 동일 alias 그룹 내 root + slot-label prefix 변형은 모두 반환되어
// 호출 측이 "LLM cols에 그 alias 그룹의 어떤 form이라도 포함되면 INJECT 생략" 판단 가능 (alias-group 만족 체크).
//
// [L21-CROSS-MERGE COL_MISSING] cross-merge slot-label prefix awareness:
//   본질: merge_slots 결과 buffer (intention='MERGED') 는 slot label prefix(예: "출석_", "만료임박_")로 충돌 회피.
//   root alias name 매칭만 하면 prefix 변형 필드를 keyword match 못해, KEYWORD_INJECT가 root alias를 강제 inject하면서
//   5cap slice로 LLM이 명시한 prefix 변형 필드(출석_attendanceRate 등)를 drop.
//   해소: prefix 변형도 같이 반환 → KEYWORD_INJECT에서 alias 그룹 만족 여부 체크 가능.
//
// 회귀 risk 최소화 — 다음 prefix는 제외:
//   - `_` 시작 키 (`_change_*`, `_changeRate_*`, `_count`, `_label` 등 내부 derived 필드): 사용자 mental model 아님.
//   - slot label prefix는 sampleItem 실제 키에서 추출 — 임의 합성 X.
export function findKeywordMatchedFields(userInput, sampleItem) {
  if (!userInput || !sampleItem) return [];
  const candidates = [];
  for (const [field, config] of Object.entries(FIELD_REGISTRY)) {
    if (!config.keywords) continue;
    for (const kw of config.keywords) {
      if (userInput.includes(kw)) candidates.push({ field, kw, len: kw.length, config });
    }
  }
  candidates.sort((a, b) => b.len - a.len);

  const sampleKeys = Object.keys(sampleItem);
  const matched = new Set();
  for (const cand of candidates) {
    const allNames = [cand.field, ...(cand.config.aliases || [])];
    // 1) root alias name 직접 매칭 — 있으면 추가 (계속 진행)
    const foundRoot = allNames.find(f => sampleItem[f] !== undefined);
    if (foundRoot) matched.add(foundRoot);

    // 2) slot-label prefix 변형 매칭 — root 유무와 무관하게 prefix 변형도 추가
    //   sampleItem 키들을 `^(.+)_(name)$` 형태로 검사 — alias가 suffix에 등장하면 prefix 변형 인정.
    //   `_change_attendedCount` 같은 underscore-prefixed derived key는 제외.
    for (const aliasName of allNames) {
      for (const key of sampleKeys) {
        if (key.startsWith('_')) continue;
        const idx = key.lastIndexOf(`_${aliasName}`);
        if (idx > 0 && key.length === idx + aliasName.length + 1) {
          matched.add(key);
        }
      }
    }
  }
  return [...matched];
}

// [L21-CROSS-MERGE COL_MISSING] alias-group 만족 체크용 helper —
// KEYWORD_INJECT 호출 측에서 "matched 필드의 alias 그룹 중 어떤 form이라도 LLM cols에 있으면 그 form을 missing 후보에서 제외" 사용.
// 예: matched=[endDate, 만료임박_remainingDays]가 같은 viewEndDate alias 그룹.
//     LLM cols에 만료임박_remainingDays 있으면 endDate inject 불필요 (alias 그룹 만족).
// 본 함수는 matched field가 sampleItem에 prefix 변형으로 존재하는지 체크 → 동일 alias 그룹 식별.
export function findKeywordAliasGroups(userInput, sampleItem) {
  if (!userInput || !sampleItem) return [];
  const candidates = [];
  for (const [field, config] of Object.entries(FIELD_REGISTRY)) {
    if (!config.keywords) continue;
    for (const kw of config.keywords) {
      if (userInput.includes(kw)) candidates.push({ field, kw, len: kw.length, config });
    }
  }
  candidates.sort((a, b) => b.len - a.len);

  const sampleKeys = Object.keys(sampleItem);
  // alias 그룹별로 sampleItem에 존재하는 모든 form 수집 (root + slot-label prefix 변형)
  const groups = [];
  const visited = new Set();
  for (const cand of candidates) {
    const groupKey = cand.field;
    if (visited.has(groupKey)) continue;
    visited.add(groupKey);

    const allNames = [cand.field, ...(cand.config.aliases || [])];
    const forms = new Set();
    for (const aliasName of allNames) {
      if (sampleItem[aliasName] !== undefined) forms.add(aliasName);
      for (const key of sampleKeys) {
        if (key.startsWith('_')) continue;
        const idx = key.lastIndexOf(`_${aliasName}`);
        if (idx > 0 && key.length === idx + aliasName.length + 1) {
          forms.add(key);
        }
      }
    }
    if (forms.size > 0) groups.push({ groupKey, forms: [...forms] });
  }
  return groups;
}

// availableFields 있으면 백엔드가 정해준 필드에서, 없으면 실제 데이터의 키에서 //
// PRIORITY_FIELDS 순으로 최대 5개 골라주는 함수 (최대 5개) //
function selectColumns(items, availableFields, buffer, userInput) {
  const MAX_COLUMNS = 5;
  const sampleItem = items?.[0];
  if (!sampleItem) return [];

  const allKeys = Object.keys(sampleItem);

  // aggregate 결과 감지: _count 필드 존재 + 전부 정수
  const isAggregated = '_count' in sampleItem && items.every(i => Number.isInteger(i._count));
  if (isAggregated) {
    // [1-J Layer A] groupKey 선택 시 NAME_FIELDS 우선 — raw PK (memberId 등) first-match 차단
    const groupKey = buffer?._aggregateKey
      || NAME_FIELDS.find(f => f in sampleItem && typeof sampleItem[f] === 'string'
        && new Set(items.map(i => i[f])).size === items.length)
      || allKeys.find(k => !k.startsWith('_') && typeof sampleItem[k] === 'string'
        && new Set(items.map(i => i[k])).size === items.length);
    const columns = [];
    if (groupKey) columns.push(groupKey);
    columns.push('_count');
    for (const k of allKeys) {
      if (columns.includes(k) || k.startsWith('_') || isExcluded(k)) continue;
      if (typeof sampleItem[k] === 'number') columns.push(k);
      if (columns.length >= 5) break;
    }
    if (TOOL_CALLING_LOG) console.log('[selectColumns] aggregate path:', columns);
    return columns;
  }

  // 비교(merged) 데이터 감지: _change_ 필드가 있으면 비교 전용 컬럼 선택
  const changeFields = allKeys.filter(k => k.startsWith('_change_') && !k.startsWith('_changeRate_'));
  if (changeFields.length > 0) {
    return selectComparisonColumns(sampleItem, changeFields, buffer, userInput);
  }

  // Detect aggregate-derived fields present in data (auto-append after column selection)
  const AGGREGATE_DERIVED = ['_count', '_lastDate', '_firstDate'];
  const presentDerived = AGGREGATE_DERIVED.filter(f => f in sampleItem);

  // 교차 merge 데이터 감지: intention이 'MERGED'이고 _change_ 없는 경우 (교차 merge만)
  if (buffer?.intention === 'MERGED' && changeFields.length === 0) {
    const prefixedFields = allKeys.filter(k => {
      if (k.startsWith('_')) return false;
      if (isExcluded(k)) return false;
      if (isLongTextField(k, sampleItem)) return false;
      const match = k.match(/^(.+?)_(.+)$/);
      return match && match[1].length > 0 && match[2].length > 1;
    });
    if (prefixedFields.length > 0) {
      return selectCrossMergeColumns(sampleItem, prefixedFields, buffer);
    }
  }

  // sort/filter/include 필드 추출 — 컬럼에 우선 포함 (getFocusFields 공유 정의)
  const focusFields = [...getFocusFields(buffer)];

  // 사용자 input keyword 매칭 필드를 focus에 추가 — "출석률" → attendanceRate 자동 포함
  // (algorithm 기반 — 도메인 enum 없음, FIELD_REGISTRY keywords만 활용)
  for (const f of findKeywordMatchedFields(userInput, sampleItem)) {
    if (!focusFields.includes(f)) focusFields.push(f);
  }

  // focus 필드를 보장하며 MAX_COLUMNS 내에서 선택
  function applyFocus(sorted) {
    const validFocus = focusFields.filter(f => sorted.includes(f));
    if (validFocus.length === 0) return sorted.slice(0, MAX_COLUMNS);
    const nonFocus = sorted.filter(f => !validFocus.includes(f));
    return [...nonFocus.slice(0, MAX_COLUMNS - validFocus.length), ...validFocus].slice(0, MAX_COLUMNS);
  }

  // 일반 데이터: 기존 로직
  // [1-D] 빈 availableFields ({}) 시 line 588+ items[0] keys fallback 진입 보장 — merged buffer (intention='MERGED') 가 availableFields 비어있는 case에서 selectColumns 빈 결과 차단. partner_gym 같은 cross-intention merge 후 show_display fail 회피.
  if (availableFields && typeof availableFields === 'object' && Object.keys(availableFields).length > 0) {
    const fieldKeys = Object.keys(availableFields)
      .map(f => resolveToItemKey(f, sampleItem) ?? f)
      .filter((f, i, arr) => arr.indexOf(f) === i)
      .filter(f => !isExcluded(f) && !isLongTextField(f, sampleItem) && (!sampleItem || f in sampleItem))
      .filter(f => !sampleItem || typeof sampleItem[f] !== 'object' || sampleItem[f] === null);

    const sorted = fieldKeys.sort((a, b) => {
      const ai = PRIORITY_FIELDS.indexOf(a);
      const bi = PRIORITY_FIELDS.indexOf(b);
      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return 0;
    });

    const result = applyFocus(sorted);
    if (presentDerived.length > 0) {
      for (const df of presentDerived) {
        if (!result.includes(df)) result.push(df);
      }
    }
    if (TOOL_CALLING_LOG) console.log('[selectColumns] availableFields path:', result, 'focus:', focusFields.filter(f => sorted.includes(f)));
    return result;
  }

  const keys = allKeys.filter(k =>
    !isExcluded(k) &&
    !isLongTextField(k, sampleItem) &&
    !k.startsWith('_') &&
    typeof sampleItem[k] !== 'object'
  );

  const sorted = keys.sort((a, b) => {
    const ai = PRIORITY_FIELDS.indexOf(a);
    const bi = PRIORITY_FIELDS.indexOf(b);
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0) return -1;
    if (bi >= 0) return 1;
    return 0;
  });

  const result = applyFocus(sorted);
  if (presentDerived.length > 0) {
    for (const df of presentDerived) {
      if (!result.includes(df)) result.push(df);
    }
  }
  if (TOOL_CALLING_LOG) console.log('[selectColumns] fallback path:', result, 'focus:', focusFields.filter(f => sorted.includes(f)));
  return result;
}

// 교차 merge 컬럼 자동 선택: 접두사별 대표 필드 //
function selectCrossMergeColumns(sampleItem, prefixedFields, buffer) {
  const allKeys = Object.keys(sampleItem);
  const nameCol = NAME_FIELDS.find(f => allKeys.includes(f)) || null;

  // 접두사별 그룹핑
  const prefixGroups = {};
  for (const key of prefixedFields) {
    const match = key.match(/^(.+?)_(.+)$/);
    if (!match) continue;
    const [, prefix, baseField] = match;
    if (!prefixGroups[prefix]) prefixGroups[prefix] = [];
    prefixGroups[prefix].push({ key, baseField });
  }

  const columns = nameCol ? [nameCol] : [];

  // matchKey가 name이 아니고 ID 필드가 아니면 자동 포함
  const mergeMatchKey = buffer?.matchKey;
  if (mergeMatchKey && mergeMatchKey !== nameCol && mergeMatchKey in sampleItem && !isExcluded(mergeMatchKey)) {
    columns.push(mergeMatchKey);
  }

  const available = buffer?.availableFields || {};
  const sortField = buffer?.appliedSort?.field;
  const bufItems = buffer?.items || [];

  for (const [prefix, fields] of Object.entries(prefixGroups)) {
    // null-only 필드 제외
    const nonNullFields = bufItems.length > 0
      ? fields.filter(f => bufItems.some(i => i[f.key] != null))
      : fields;
    if (nonNullFields.length === 0) continue;

    // sort 필드 우선
    const sortMatch = nonNullFields.find(f => f.key === sortField);
    if (sortMatch) { columns.push(sortMatch.key); continue; }
    // availableFields에 있는 필드 우선
    const meaningful = nonNullFields.filter(f => f.baseField in available);
    if (meaningful.length > 0) { columns.push(meaningful[0].key); }
    else { columns.push(nonNullFields[0].key); }
  }

  if (TOOL_CALLING_LOG) console.log('[selectColumns] crossMerge path:', columns.slice(0, 5));
  return columns.slice(0, 5);
}

// 비교 데이터 컬럼 자동 선택: 핵심 지표의 이전/최근/변화량만 //
function selectComparisonColumns(sampleItem, changeFields, buffer, userInput) {
  const focusField = findFocusChangeField(changeFields, buffer, userInput);
  const baseField = focusField.replace('_change_', '');

  const allKeys = Object.keys(sampleItem);
  const prefixed = allKeys.filter(k => k.endsWith(`_${baseField}`) && !k.startsWith('_'));

  const columns = ['name'];

  // matchKey가 name이 아니고 ID 필드가 아니면 자동 포함
  const mergeMatchKey = buffer?.matchKey;
  if (mergeMatchKey && mergeMatchKey !== 'name' && mergeMatchKey in sampleItem && !isExcluded(mergeMatchKey)) {
    columns.push(mergeMatchKey);
  }

  // null-only 컬럼 제외
  const bufItems = buffer?.items || [];
  for (const p of prefixed) {
    if (bufItems.length > 0 && !bufItems.some(i => i[p] != null)) continue;
    columns.push(p);
  }
  columns.push(focusField);

  return columns;
}

function findFocusChangeField(changeFields, buffer, userInput) {
  if (buffer?.appliedSort?.field?.startsWith('_change_')) {
    return buffer.appliedSort.field;
  }
  const changeFilter = buffer?.appliedFilters?.find(f => f.field?.startsWith('_change_'));
  if (changeFilter) return changeFilter.field;

  // 사용자 입력 × availableFields 라벨 매칭
  if (userInput && buffer?.availableFields && changeFields.length > 1) {
    const af = buffer.availableFields;
    for (const cf of changeFields) {
      const baseField = cf.replace('_change_', '');
      const label = af[baseField] || LABEL_OVERRIDES[baseField] || '';
      const keywords = label.replace(/[()%]/g, '').split(/\s+/).filter(w => w.length >= 2);
      if (keywords.some(kw => userInput.includes(kw))) {
        if (TOOL_CALLING_LOG) console.log(`[selectColumns] focusField 키워드 매칭: "${cf}" ← "${label}" matches "${userInput}"`);
        return cf;
      }
    }
  }

  return changeFields[0];
}

// availableFields → 컬럼 라벨 매핑 //
function buildColumnLabels(columns, availableFields) {
  // 값이 string("이름") 또는 object({ label, enums }) — getFieldLabel로 통일
  const fieldList = Object.entries(availableFields || {}).map(([field, meta]) => ({
    field,
    description: getFieldLabel(meta) || '',
  }));
  const labels = {};
  for (const col of columns) {
    const seg = col.includes('.') ? col.split('.').pop() : null;

    // availableFields description이 field name과 동일하면 백엔드 ai_intention 라벨 누락으로 추정
    // → LABEL_OVERRIDES fallback 시도 후, 그래도 없으면 availableFields description(영문)을 사용
    const exact = fieldList.find(f => f.field === col);
    if (exact?.description && exact.description !== exact.field) { labels[col] = exact.description; continue; }

    const segment = seg || col;
    const bySegment = fieldList.find(f => f.field === segment || f.field.endsWith('.' + segment));
    if (bySegment?.description && bySegment.description !== bySegment.field) { labels[col] = bySegment.description; continue; }

    if (LABEL_OVERRIDES[col]) { labels[col] = LABEL_OVERRIDES[col]; continue; }
    if (seg && LABEL_OVERRIDES[seg]) { labels[col] = LABEL_OVERRIDES[seg]; continue; }

    // LABEL_OVERRIDES 매핑이 없을 때만 availableFields의 영문 라벨을 최종 fallback으로 사용
    if (exact?.description) { labels[col] = exact.description; continue; }
    if (bySegment?.description) { labels[col] = bySegment.description; continue; }

    // _change_ 필드: "_change_attendedCount" → "출석 차이"
    const changeMatch = col.match(/^_change_(.+)$/);
    if (changeMatch) {
      const baseField = changeMatch[1];
      const baseEntry = fieldList.find(f => f.field === baseField);
      const baseLabel = (baseEntry?.description || LABEL_OVERRIDES[baseField] || baseField).replace(/\s*\(.*?\)\s*/g, '').replace(/\s*(횟수|빈도|건수)\s*$/, '').trim();
      labels[col] = `${baseLabel} 차이`;
      continue;
    }
    // [MONEY_SUM_AGGREGATE] _sum_ 필드: "_sum_totalPrice" → "총 금액 합계", "_sum_cardPrice" → "카드 결제액 합계"
    //   computeAggregate가 raw monetary 필드(*Price/*Amount/*Fee/*Revenue) 그룹별 합계를 derive할 때 생성.
    //   base label 단위 토큰(원/명/회) 보존 — sanitizeFieldLabel 처리된 결과 사용. 중복 "합계 합계" 방지.
    const sumMatch = col.match(/^_sum_(.+)$/);
    if (sumMatch) {
      const baseField = sumMatch[1];
      const baseEntry = fieldList.find(f => f.field === baseField);
      const rawBase = baseEntry?.description || LABEL_OVERRIDES[baseField] || baseField;
      const baseLabel = String(rawBase).replace(/\s*합계\s*$/, '').trim();
      labels[col] = `${baseLabel} 합계`;
      continue;
    }
    // _changeRate_ 필드: "_changeRate_attendedCount" → "출석 변동(%)"
    const rateMatch = col.match(/^_changeRate_(.+)$/);
    if (rateMatch) {
      const baseField = rateMatch[1];
      const baseEntry = fieldList.find(f => f.field === baseField);
      const baseLabel = (baseEntry?.description || LABEL_OVERRIDES[baseField] || baseField).replace(/\s*\(.*?\)\s*/g, '').replace(/\s*(횟수|빈도|건수)\s*$/, '').trim();
      labels[col] = `${baseLabel} 변동(%)`;
      continue;
    }

    // 멀티슬롯 프리픽스 필드: "previous_attendedCount" → "이전 출석 횟수", "recent_attendedCount" → "최근 출석 횟수"
    // prefix가 description에 이미 포함되면 중복 방지: "출석" + "출석 횟수" → "출석 횟수"
    // English merge prefixes → Korean translation (mergeSlots label='previous'/'recent')
    // [L21-MERGE-SLOT-NAME-001 cycle 1] pure-ASCII unknown prefix (LLM이 slot.label로 slotName 그대로 사용한 leak — 예: "att", "expire", "hist")
    //   - "att_attendanceRate" → "att 출석률(%)" 같은 한영 혼합 표 헤더 차단
    //   - 단, 같은 columns 내 다른 prefix가 동일 baseField를 참조하면 (예: "att_count" + "expire_count") 라벨 충돌 → prefix 유지
    //   - Korean prefix ("만료", "1월")는 영향 X (isPureAsciiIdent=false)
    //   - MERGE_PREFIX_KO 등록된 ASCII prefix ("previous"/"recent")는 한국어 매핑 우선
    const MERGE_PREFIX_KO = { previous: '이전', recent: '최근' };
    const isPureAsciiIdent = (s) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(s);
    const prefixMatch = col.match(/^(.+?)_(.+)$/);
    if (prefixMatch) {
      const [, prefix, baseField] = prefixMatch;
      const baseEntry = fieldList.find(f => f.field === baseField);
      const baseDesc = baseEntry?.description || LABEL_OVERRIDES[baseField];
      if (baseDesc) {
        const isUnknownAsciiPrefix = isPureAsciiIdent(prefix) && !(prefix in MERGE_PREFIX_KO);
        if (isUnknownAsciiPrefix) {
          // 같은 baseField를 가진 다른 prefixed column이 존재하는지 검사 (라벨 충돌 회피용)
          const baseFieldCollision = columns.some(other => {
            if (other === col) return false;
            const otherMatch = other.match(/^(.+?)_(.+)$/);
            return otherMatch && otherMatch[2] === baseField;
          });
          if (!baseFieldCollision) {
            // ASCII prefix leak — prefix drop 후 base label만 사용
            labels[col] = baseDesc;
            continue;
          }
        }
        const displayPrefix = MERGE_PREFIX_KO[prefix] || prefix;
        labels[col] = baseDesc.startsWith(displayPrefix) ? baseDesc : `${displayPrefix} ${baseDesc}`;
        continue;
      }
    }

    // _ 접두사 필드: derive/aggregate 등에서 생성된 필드 → _ 제거 후 라벨 시도 //
    if (col.startsWith('_')) {
      const stripped = col.slice(1);

      if (LABEL_OVERRIDES[stripped]) {
        labels[col] = LABEL_OVERRIDES[stripped];
        continue;
      }

      const strippedEntry = fieldList.find(f => f.field === stripped);

      if (strippedEntry?.description) {
        labels[col] = strippedEntry.description;
        continue;
      }

      labels[col] = stripped;
      continue;
    }

    labels[col] = seg || col;
  }
  return labels;
}

// 숫자 컬럼 감지 //
const NUMERIC_EXCLUDE_PATTERNS = [
  /phone/i, /tel/i, /mobile/i, /fax/i,
  /id$/i, /Id$/,
  /zip/i, /code$/i,
  /date/i, /time/i,
];

function detectNumericColumns(items, columns) {
  if (items.length === 0) return [];
  return columns.filter(col => {
    if (NUMERIC_EXCLUDE_PATTERNS.some(p => p.test(col))) return false;
    const sample = items.slice(0, 5);
    return sample.every(item => {
      const val = getByPath(item, col) ?? item[col] ?? deepGet(item, col);
      if (val == null || typeof val === 'boolean') return false;
      const str = String(val);
      if (str.length >= 8) return false;
      return !isNaN(Number(val));
    });
  });
}

// 숫자 컬럼 통계 //
function computeStats(items, numericColumns) {
  const stats = {};
  for (const col of numericColumns) {
    const values = items.map(item => Number(getByPath(item, col) ?? item[col] ?? deepGet(item, col))).filter(v => !isNaN(v));
    if (values.length === 0) continue;
    stats[col] = {
      sum: values.reduce((a, b) => a + b, 0),
      avg: Math.round(values.reduce((a, b) => a + b, 0) / values.length * 10) / 10,
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }
  return stats;
}

// 만료일 D-day 계산 //
const DATE_REMAINING_FIELDS = ['endDate', 'viewEndDate', 'expiryDate'];

// memberId 또는 teacherId 기준 그룹핑 //
function groupByMember(items) {
  if (items.length === 0) return items;

  const sample = items[0];
  const groupKey = sample?.memberId != null ? 'memberId'
    : sample?.teacherId != null ? 'teacherId'
    : null;
  if (!groupKey) return items;

  const grouped = [];
  const memberMap = new Map();

  for (const item of items) {
    if (!item[groupKey]) {
      grouped.push({ ...item, _isFirstOfGroup: true });
      continue;
    }
    if (!memberMap.has(item[groupKey])) {
      memberMap.set(item[groupKey], grouped.length);
      grouped.push({ ...item, _isFirstOfGroup: true });
    } else {
      const insertIdx = memberMap.get(item[groupKey]);
      let lastIdx = insertIdx;
      for (let i = insertIdx + 1; i < grouped.length; i++) {
        if (grouped[i][groupKey] === item[groupKey]) lastIdx = i;
        else break;
      }
      grouped.splice(lastIdx + 1, 0, { ...item, _isFirstOfGroup: false });
      for (const [key, val] of memberMap) {
        if (val > lastIdx) memberMap.set(key, val + 1);
      }
    }
  }

  return grouped;
}


// ============================================
// 현재 설정 기준 표시될 컬럼 라벨 미리보기 (GPT 검증용) //
export function previewDisplayColumns(session) {
  const buffer = session.getBuffer();
  if (!buffer.items || buffer.items.length === 0) return null;

  let columns = buffer.resolvedColumns
    ? [...buffer.resolvedColumns]
    : selectColumns(buffer.items, buffer.availableFields, buffer);

  const prefs = buffer.columnPrefs;
  if (prefs) {
    if (prefs.only?.length) {
      const nameCol = columns.find(c => NAME_FIELDS.includes(c));
      columns = nameCol ? [nameCol] : [];
      for (const col of prefs.only) {
        if (!columns.includes(col)) columns.push(col);
      }
    } else {
      if (prefs.include?.length) {
        for (const col of prefs.include) {
          if (!columns.includes(col)) columns.push(col);
        }
      }
      if (prefs.exclude?.length) {
        const normalize = s => s.replace(/\s+/g, '').replace(/[을를이가은는도의로만에서와과]$/g, '');
        const excludeFields = new Set(prefs.exclude);
        const excludeNorms = prefs.exclude.map(normalize);
        columns = columns.filter(col => {
          if (excludeFields.has(col)) return false;
          const label = LABEL_OVERRIDES[col] || col;
          const labelNorm = normalize(label);
          for (const kwNorm of excludeNorms) {
            if (labelNorm === kwNorm || labelNorm.includes(kwNorm) || kwNorm.includes(labelNorm)) return false;
          }
          return true;
        });
      }
    }
  }

  return columns.map(col => LABEL_OVERRIDES[col] || col);
}

// 컬럼별 포맷 타입 매핑 (FIELD_REGISTRY compute 기반) //
function buildColumnFormats(columns) {
  const formats = {};
  for (const col of columns) {
    const entry = FIELD_REGISTRY[col];
    if (entry?.computes) {
      const firstCompute = Object.keys(entry.computes)[0];
      if (firstCompute) formats[col] = firstCompute;
    }
  }
  return formats;
}

// [UNDISPLAYED METRIC AUTO-APPEND] 이번 turn call_api로 적재됐지만 표(활성 슬롯)/성공 merge에 미사용된 슬롯의 //
// backend summary 스칼라를 outro에 결정적으로 보강. 본질: per-axis 다지표 질문(예: 수업별 참여율+매출)에서 두 번째
// 지표 슬롯은 join 불가로 표에 못 들어가고, LLM finish 인용은 stochastic + iteration cap 도달 시 기회 자체가 없음 (DEMO-37).
// 코드가 backend 수치 그대로 부착 — 계산/추정 없음 (augmentOutroWithChange와 동일 harness 패턴). 1 turn 1회. //
// 공유 함수 — useAiChat(라이브)/nodeAgent(하니스) 모두 autoGenerateDisplay 경유라 dual sync 불필요. //
function buildUndisplayedSlotSummaryOutro(session) {
  const calledSlots = session?._turnCallApiSlots;

  if (!calledSlots || calledSlots.size === 0 || session._undisplayedCitedThisTurn) return null;

  const mergedSources = session._turnMergedSourceSlots || new Set();
  const displayedSlots = session._turnDisplayedSlots || new Set();
  const activeIntention = session.buffers?.get?.(session.activeSlot)?.intention;

  for (const slotName of calledSlots) {
    if (slotName === session.activeSlot) continue;
    if (mergedSources.has(slotName)) continue;
    if (displayedSlots.has(slotName)) continue;  // 이미 표로 표시된 슬롯(다축) — 재요약은 사족

    const buf = session.buffers?.get?.(slotName);
    const summary = buf?.summary;
    if (!summary || typeof summary !== 'object') continue;
    // same-intention 다축(groupBy 변형 — 요일/시간대 등)은 별도 지표가 아님 — 곧 표로 표시될 슬롯의 summary 선부착 차단 (DEMO-12 회귀)
    if (activeIntention && buf.intention === activeIntention) continue;

    const afields = buf.availableFields || {};
    let periodPart = '';

    if (typeof summary.startDate === 'string' && typeof summary.endDate === 'string') {
      periodPart = ` (${summary.startDate.split('T')[0]}~${summary.endDate.split('T')[0]} 기준)`;
    }

    const entries = [];

    for (const [k, v] of Object.entries(summary)) {
      if (k === 'startDate' || k === 'endDate') continue;
      if (typeof v !== 'number' || !isFinite(v) || v === 0) continue;  // 0값 나열은 노이즈 (포인트 0 등)
      if (isAmbiguousCountKey(k)) continue;

      // 라벨 미해소 키(summary 전용 키 등)는 raw 키 노출 금지 — entry 제외
      const label = getFieldLabel(afields[k]);
      if (!label) continue;

      const unit = extractUnitFromFieldLabel(afields[k]);
      const formatted = formatValue(v, k, afields[k]) || String(v);
      entries.push(`${label} ${formatted}${unit && !label.includes(`(${unit})`) ? unit : ''}`);

      if (entries.length >= 4) break;
    }
    if (entries.length === 0) continue;

    session._undisplayedCitedThisTurn = true;

    return `함께 조회한 요약${periodPart}: ${entries.join(', ')}`;
  }

  return null;
}

// autoGenerateDisplay — 메인 함수
// ============================================
// 버퍼 데이터(filter/sort 적용 완료) + availableFields로 display 자동 생성
// LLM은 intro/outro만 전달하면 됨

export function autoGenerateDisplay(displayConfig, session) {
  const { intro, outro, max_count, columns: llmColumns, columnLabels: llmColumnLabels } = displayConfig || {};
  const buffer = session.getBuffer();

  if (!buffer.items || buffer.items.length === 0) {
    return null;
  }

  let items = [...buffer.items];

  // SUMMARY-OVERRIDE 비교 표 감지 — 컬럼 순서/_change_ prepend 분기에서 공유
  // (정렬은 mergeSlots에서 periodStart asc로 미리 적용 + appliedSort 설정됨)
  const hasChangeFields = items.some(it => it && Object.keys(it).some(k => k.startsWith('_change_')));

  // appliedSort가 없으면 userInput 키워드 매칭으로 자동 정렬
  if (!buffer.appliedSort) {
    const userInput = session.getUserInput?.() || '';
    if (userInput) {
      const autoSort = findAutoSort(userInput, items[0]);
      if (autoSort) {
        session.applySort(autoSort);
        items = [...session.getBuffer().items];
        if (TOOL_CALLING_LOG) console.log('[autoGenerateDisplay] auto-sort:', autoSort.field, autoSort.direction);
      }
    }
  }

  if (TOOL_CALLING_LOG) console.log('[autoGenerateDisplay] appliedSort:', session.getBuffer().appliedSort);

  // 컬럼 선택 우선순위: LLM 지정 칼럼 > 이전 display 확정 컬럼 > availableFields 기반 칼럼
  let columns;
  // LLM이 한국어 라벨이나 키워드를 보낸 경우 실제 필드명으로 변환
  if (llmColumns?.length > 0) {
    const dataFields = items[0] ? Object.keys(items[0]) : [];
    columns = llmColumns.map(col => {
      // 이미 데이터에 존재하는 필드명이면 그대로 사용 (필드 → 필드)
      if (items[0] && col in items[0]) return col;
      // LABEL_OVERRIDES 역방향 매칭 (한국어 라벨 → 필드)
      for (const [field, label] of Object.entries(LABEL_OVERRIDES)) {
        if (label === col && items[0] && field in items[0]) return field;
      }
      // FIELD_REGISTRY alias 매칭 (유사 필드 → 필드)
      for (const [field, config] of Object.entries(FIELD_REGISTRY)) {
        const allNames = [field, ...(config.aliases || [])];
        if (allNames.includes(col)) {
          const found = allNames.find(f => items[0] && f in items[0]);
          if (found) return found;
        }
      }
      // resolveToItemKey — dot-prefix recovery ("sales.name" → "name") + canonical/alias 통합 처리
      const _resolved = items[0] ? resolveToItemKey(col, items[0]) : null;
      if (_resolved) {
        if (TOOL_CALLING_LOG && col !== _resolved) console.log(`[autoGenerateDisplay] dot-prefix resolved: "${col}" → "${_resolved}"`);
        return _resolved;
      }
      // 매칭 실패 — meta prefix (_change_/_changeRate_/period/_label/_count + N월_ stack) 검사 후 통과,
      // 그 외는 auto-drop (1-D fix: raw 유지 시 빈 셀 발생)
      if (col === 'period' || col === '_label' || col === '_count' || col === 'periodStart' || col === 'periodEnd') return col;
      if (typeof col === 'string' && (col.startsWith('_change_') || col.startsWith('_changeRate_'))) return col;
      const _periodPrefixMatch = typeof col === 'string' && col.match(/^(\d{1,2}월|[QqTtFf]\d|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|base|compare_\d+)_(.+)$/i);
      if (_periodPrefixMatch && items[0] && _periodPrefixMatch[2] in items[0]) return col;
      if (TOOL_CALLING_LOG) {
        console.log(`[autoGenerateDisplay] ⚠️ 컬럼 매핑 실패: "${col}" → 데이터 필드: [${dataFields.join(', ')}] (auto-drop)`);
      }
      return null;
    }).filter(c => {
      if (c == null) return false;
      // [1-O] LLM이 명시한 column이 intention의 availableFields에 정의되어 있으면 entity 식별 의도 — EXCLUDED_FIELDS regex 우회
      //   예: partner_gym intention의 storeName/storeTel/storeAddress는 EXCLUDED_FIELDS의 /^store/, /tel$/, /storeName/ 패턴 매칭이지만 backend 메타에 정의된 entity 식별 field
      //   회귀 risk: backend가 의도적으로 노출한 field만 우회 — LLM 임의 raw PK/메타 명시는 isExcluded 그대로 차단
      const _allowedByMeta = !!(buffer?.availableFields && c in buffer.availableFields);
      // [1-N] LLM이 명시한 long-text/URL 필드 표 셀 차단 (selectColumns availableFields path:575와 정합)
      //   예: partner_gym profiles (S3 raw URL 200+자) 표 셀 노출 차단. finalText/outro 자연어 인용은 LLM 자율 (별도 case)
      return (!isExcluded(c) || _allowedByMeta) && (!isLongTextField(c, items[0]) || _allowedByMeta);
    });
    // [1-E] LLM이 columns 모두 invalid → 1-D auto-drop 후 빈 array → buffer 기반 자동 fallback (빈 표 차단)
    if (columns.length === 0) {
      if (buffer.resolvedColumns?.length) {
        columns = [...buffer.resolvedColumns];
      } else {
        const _userInputForFallback = session.getUserInput?.() || '';
        columns = selectColumns(items, buffer.availableFields, buffer, _userInputForFallback);
      }
      if (TOOL_CALLING_LOG) {
        console.log(`[autoGenerateDisplay] 1-E fallback: LLM columns 모두 invalid → buffer 기반 auto-select → [${columns.join(', ')}]`);
      }
    }
    // [L23-FILTER-FOCUS] filter 후 LLM columns 명시 시에도 이전 turn 컬럼 보충 — 정보 손실 방지
    if (buffer._preFilterColumns && buffer._preFilterColumns.length > 0 && buffer.appliedFilters?.length > 0) {
      const dataKeys = items[0] ? new Set(Object.keys(items[0])) : new Set();
      const _missing = buffer._preFilterColumns.filter(c => dataKeys.has(c) && !columns.includes(c) && !isExcluded(c));
      if (_missing.length > 0) {
        columns = [...columns, ..._missing];
        if (TOOL_CALLING_LOG) {
          console.log(`[autoGenerateDisplay] _preFilterColumns augment (LLM path): added [${_missing.join(', ')}]`);
        }
      }
    }
  } else if (buffer.resolvedColumns) {
    columns = [...buffer.resolvedColumns];
  } else if (buffer._preFilterColumns && buffer._preFilterColumns.length > 0 && buffer.appliedFilters?.length > 0) {
    // [L23-FILTER-FOCUS] filter 후 LLM columns 미명시 시 이전 turn 컬럼 유지 + filter 필드 포함 보장
    const dataKeys = items[0] ? new Set(Object.keys(items[0])) : new Set();
    columns = buffer._preFilterColumns.filter(c => dataKeys.has(c));
    // filter에 사용된 필드가 컬럼에 없으면 추가
    for (const f of buffer.appliedFilters) {
      if (f.field && dataKeys.has(f.field) && !columns.includes(f.field)) {
        columns.push(f.field);
      }
    }
    if (TOOL_CALLING_LOG) {
      console.log(`[autoGenerateDisplay] _preFilterColumns path: [${columns.join(', ')}]`);
    }
  } else {
    const userInputForColumns = session.getUserInput?.() || '';
    columns = selectColumns(items, buffer.availableFields, buffer, userInputForColumns);
  }
  if (columns.length === 0) return null;

  // [COL_MISSING P3] DETAIL intention identity-prefix auto-prepend.
  //   단일 entity 다행 detail(회원권/수업/클래스 등) 표에서 LLM이 columns 명시 시 identity field(memberShipName/lessonName/className/name)를 누락하는 패턴 차단.
  //   조건: DETAIL intention + multi-row + availableFields에 식별 field 정의 + 실데이터(다행 sample 합집합)에 존재 + 현재 columns 미포함 → prepend.
  //   범용적 접근: case ID 하드코딩 X. PRIORITY_FIELDS 헤드(이미 name+membership+lesson+class로 정의됨) + availableFields 메타 lookup 조합.
  //   회귀 risk 최소화: DETAIL intention + availableFields 정의된 field만 — 임의 prepend X.
  //   items[0]만 검사 X: 회원권 detail 같은 혼합 form 응답(예: items[0]=holding, items[1..]=extension+memberShipName) 시 items[0] 미포함이어도 다른 행에 memberShipName이 있으면 표시 — 다행 sample(최대 10건) 합집합으로 식별 field 존재 검출.
  const _isDetailIntentionForIdentity = typeof buffer?.intention === 'string' && buffer.intention.includes('DETAIL');
  if (_isDetailIntentionForIdentity && items.length > 1) {
    const IDENTITY_PREFIX_FIELDS = [
      ...NAME_FIELDS,
      'viewMembershipName', 'membershipName', 'memberShipName',
      'lessonName', 'className',
    ];
    const _availableSet = new Set(Object.keys(buffer.availableFields || {}));
    // 다행 sample 합집합 (mixed-form 대응: items[0] holding이어도 items[1..]에 memberShipName 있으면 표시)
    const _sampleSizeForKeys = Math.min(items.length, 10);
    const _dataKeysUnion = new Set();
    for (let i = 0; i < _sampleSizeForKeys; i++) {
      const it = items[i];
      if (it && typeof it === 'object') {
        for (const k of Object.keys(it)) _dataKeysUnion.add(k);
      }
    }
    const _currentSet = new Set(columns);
    const _toPrepend = [];
    for (const f of IDENTITY_PREFIX_FIELDS) {
      if (_availableSet.has(f) && _dataKeysUnion.has(f) && !_currentSet.has(f)) {
        _toPrepend.push(f);
        _currentSet.add(f);
      }
    }
    if (_toPrepend.length > 0) {
      columns = [..._toPrepend, ...columns];
      if (TOOL_CALLING_LOG) {
        console.log(`[autoGenerateDisplay] DETAIL identity-prefix prepend: [${_toPrepend.join(', ')}]`);
      }
    }
  }

  // [Cat5-INFO_LOSS] LLM이 명시적으로 요청한 컬럼 Set — uniform drop 보호용
  // LLM이 show_display.columns에 의도적으로 포함한 필드는 uniform value여도 제거하지 않음
  const _llmRequestedCols = (llmColumns?.length > 0) ? new Set(columns) : new Set();

  // [L23-FILTER-FOCUS] _preFilterColumns 보충 컬럼도 uniform drop 보호 — 이전 turn에서 표시된 컬럼 유지
  const _preFilterProtectedCols = (buffer._preFilterColumns && buffer.appliedFilters?.length > 0)
    ? new Set(buffer._preFilterColumns)
    : new Set();

  // 컬럼 커스터마이징 적용 (only/include/exclude)
  const prefs = buffer.columnPrefs;
  if (prefs) {
    // only 모드: 지정된 컬럼만 표시 (이름은 항상 포함)
    if (prefs.only?.length) {
      const nameCol = columns.find(c => NAME_FIELDS.includes(c));
      columns = nameCol ? [nameCol] : [];
      for (const col of prefs.only) {
        if (!columns.includes(col)) columns.push(col);
      }
    } else {
      if (prefs.include?.length) {
        for (const col of prefs.include) {
          if (!columns.includes(col)) columns.push(col);
        }
      }
      if (prefs.exclude?.length) {
        // 필드명 직접 매칭 + 라벨 기반 매칭 (공백/조사 무시)
        const normalize = s => s.replace(/\s+/g, '').replace(/[을를이가은는도의로만에서와과]$/g, '');
        const excludeFields = new Set(prefs.exclude);
        const excludeNorms = prefs.exclude.map(normalize);
        columns = columns.filter(col => {
          if (excludeFields.has(col)) return false;
          const label = LABEL_OVERRIDES[col] || col;
          const labelNorm = normalize(label);
          for (const kwNorm of excludeNorms) {
            if (labelNorm === kwNorm || labelNorm.includes(kwNorm) || kwNorm.includes(labelNorm)) return false;
          }
          return true;
        });
      }
    }

    // 계산 컬럼 처리 (나이 등)
    if (prefs.computedColumns?.length) {
      for (const cc of prefs.computedColumns) {
        if (cc.compute === 'age' && columns.includes('age')) {
          items = addAge(items);
        }
      }
    }
  }

  // 데이터에 실제 존재하는 컬럼만 유지
  //   다행 sample 합집합 검사 — items[0] 단일 검사 시 mixed-form 응답(예: items[0]=holding form, items[1..]=membership form)에서
  //   items[0]에 없는 필드(memberShipName 등)가 잘못 drop되어 표 식별 불가하는 회귀 차단 (DETAIL intention COL_MISSING P3와 정합)
  // [L9c-COMPARE-006 — STOCHASTIC_FINALTEXT_TABLE_DROP] sample expanded from first-10 to full items (cap 200).
  //   본질: merge_slots outer 결과 items 순서가 한쪽 period 편향 (예: 1월-only 29 row → 2월-only 16 row → 3월-only 36 row).
  //     첫-10 sample이 한 period 영역에 갇히면 그 외 period의 prefix field(예: 3월_totalPrice)가 dataKeys 미포함 → column 결정적 drop.
  //   fix: full items sample (cap 200). LLM이 명시한 multi-period prefix column이 어느 위치 row에 존재해도 보존.
  let excludedColumns = [];
  if (items[0]) {
    const _sampleForDataKeys = Math.min(items.length, 200);
    const dataKeys = new Set();
    for (let i = 0; i < _sampleForDataKeys; i++) {
      const it = items[i];
      if (it && typeof it === 'object') {
        for (const k of Object.keys(it)) dataKeys.add(k);
      }
    }
    excludedColumns = columns.filter(c => !dataKeys.has(c));
    if (excludedColumns.length > 0) {
      columns = columns.filter(c => dataKeys.has(c));
    }
  }

  // [anchor 15] array/object field column auto-drop — raw 노출 차단 (visualizationOnly intention 명단 표시 허용 + reserveAttendances 같은 raw array는 표에서 자동 제외)
  //   sample 기반 type 검출, intention key 하드코딩 X
  if (items[0]) {
    const droppedArrayCols = [];
    columns = columns.filter(col => {
      const v = items[0][col];
      if (Array.isArray(v) || (v && typeof v === 'object')) {
        droppedArrayCols.push(col);
        return false;
      }
      return true;
    });
    if (droppedArrayCols.length > 0 && TOOL_CALLING_LOG) {
      console.log(`[autoGenerateDisplay] array/object column auto-drop: [${droppedArrayCols.join(', ')}]`);
    }
  }

  // [Cat8-REPEAT] long-text column auto-drop — 다수 행에서 표 셀 부적합 장문 컬럼 자동 제거
  //   items[0] 단일 샘플이 아닌 최대 10개 행 검사 → 과반(50%+) 초과 시 drop
  //   content/body/notice/message 등 문장형 필드가 표 셀에 dump되는 반복 노출 차단
  //   DISPLAY_CELL_THRESHOLD (60자): 표 셀 가독성 기준 — MAX_CELL_LENGTH (80자, selectColumns 단일샘플)보다 보수적
  //   availableFields에 등록된 필드는 보호 — 의도적으로 사용자에게 보여주려고 정의된 핵심 필드 (Cat5-INFO_LOSS 방지)
  const DISPLAY_CELL_THRESHOLD = 60;
  const _availableFieldKeys = new Set(Object.keys(buffer.availableFields || {}));

  if (items.length > 0) {
    const sampleSize = Math.min(items.length, 10);
    const droppedLongTextCols = [];

    columns = columns.filter(col => {
      if (NAME_FIELDS.includes(col)) return true;
      if (_availableFieldKeys.has(col)) return true;
      let longCount = 0;

      for (let i = 0; i < sampleSize; i++) {
        const v = items[i]?.[col];
        if (typeof v === 'string' && v.length > DISPLAY_CELL_THRESHOLD) {
          longCount++;
        }
      }

      if (longCount > sampleSize / 2) {
        droppedLongTextCols.push(col);
        return false;
      }

      return true;
    });

    if (droppedLongTextCols.length > 0 && TOOL_CALLING_LOG) {
      console.log(`[autoGenerateDisplay] long-text column auto-drop: [${droppedLongTextCols.join(', ')}]`);
    }
  }

  // 전체 행 동일값 컬럼 제거 — 필터 후 의미없는 컬럼 방지 (name + focus 필드 + userInput keyword 매칭 필드 보호)
  // userInput keyword 매칭 필드를 protectedCols에 추가 — "출석률" 입력 시 attendanceRate가 모두 100%여도 보호
  const userInputForProtect = session.getUserInput?.() || '';
  const protectedCols = new Set([
    ...getFocusFields(buffer),
    ...findKeywordMatchedFields(userInputForProtect, items[0]),
  ]);
  const removedUniformColumns = [];

  // [1-M] detail intention + LLM 명시 columns → uniform drop 우회.
  // 단일 entity multi-row detail (회원권 11건 등) 응답에서 LLM이 의도적으로 명시한 식별 field (memberShipName/categoryName/status)가 uniform value일 때 drop되어 사용자가 어떤 회원권/카테고리/상태인지 식별 불가하는 회귀 차단.
  // 조건 교집합 (detail intention + LLM 명시) — 회귀 risk 최소화: 자동 column path (LLM 미명시)는 기존 noise reduction 유지.
  const _isDetailIntention = typeof buffer?.intention === 'string' && buffer.intention.includes('DETAIL');
  const _isLlmColumnsList = !!llmColumns && Array.isArray(llmColumns) && llmColumns.length > 0;
  const _uniformDropEnabled = !(_isDetailIntention && _isLlmColumnsList);

  // [COL_MISSING P3] DETAIL intention identity-prefix fields uniform-drop 보호 Set
  //   자동 column path(LLM 미명시) + auto-prepend된 identity field가 uniform value(단일 entity 다행 detail에서 흔함)일 때 drop되면 사용자가 row 식별 불가 → 보호.
  const _detailIdentityProtected = new Set();
  if (_isDetailIntention) {
    const IDENTITY_PREFIX_FIELDS_PROTECT = [
      ...NAME_FIELDS,
      'viewMembershipName', 'membershipName', 'memberShipName',
      'lessonName', 'className',
    ];
    const _availableSetProtect = new Set(Object.keys(buffer.availableFields || {}));
    for (const f of IDENTITY_PREFIX_FIELDS_PROTECT) {
      if (_availableSetProtect.has(f)) _detailIdentityProtected.add(f);
    }
  }

  // [L9b-CROSS-002 cycle 1 / DEGENERATE_COLUMN_T0] multi-period prefix uniform drop —
  //   본질: merge_slots / multi-period comparison 결과 column이 `<prefix>_<suffix>` 형태로 sibling variant 공존
  //     (예: previous_attendanceRate + recent_attendanceRate). 같은 suffix를 공유하는 sibling 중 일부가
  //     비-uniform이면 그 비-uniform variant가 "변화/차이" 의미를 충족 (사용자 keyword "출석률" 의도 충족),
  //     uniform variant는 정보 0 (모든 row 동일 값 → 비교 의미 없음).
  //   uniform variant는 _llmRequestedCols / protectedCols 보호 우회하여 drop:
  //     "출석률" keyword가 attendanceRate alias 그룹을 보호하더라도, 그 그룹 내 sibling variant
  //     (recent_attendanceRate)가 정보 보유 → 사용자 의도 충족. uniform variant (previous_attendanceRate=100 전부)는
  //     단순 noise → drop 시 정보 손실 없음.
  //   조건 (모두 만족 시 보호 무시):
  //     (1) column 형태 `<prefix>_<suffix>` (1 underscore, 양측 비어있지 않음)
  //     (2) `_` 로 시작하는 내부 derived key (`_change_*` 등) 아님
  //     (3) 동일 suffix를 공유하는 sibling variant가 columns에 존재
  //     (4) sibling 중 적어도 1개가 비-uniform (variance > 0)
  //     (5) 현재 column 자체는 uniform
  //   case ID/intention/도메인 하드코딩 X. column 패턴 + value variance만 의존.
  //   회귀 risk 최소화: DETAIL identity-prefix는 _detailIdentityProtected 별도 보호 유지.
  //   [L9c-COMPARE-006 — STOCHASTIC_FINALTEXT_TABLE_DROP] uniformity sample expanded from first-20 to full items (cap 200).
  //     본질: merge_slots outer + sort by _change_* 결과 top-20 rows가 한쪽 period side로 편향 (positive change=모두 base side, negative=모두 compare side).
  //       작은 첫-20 sample에서는 반대 side period column이 0/null uniform으로 잘못 검출 → degenerate variant drop 발동 → finalText 표 헤더에서 해당 period 열 누락.
  //       sibling variant 비-uniform이라서 _llmRequestedCols 보호도 우회 → 결정적 column drop.
  //     fix: items 전체(cap 200) sample. 200은 typical merge 결과 size 충분 cover + pathological large dataset 성능 보호.
  //       L9b-CROSS-002 회귀: previous_attendanceRate=100 전 row 동일 케이스 — 전체 sample에서도 uniform → drop 유지 (회귀 X).
  //       L9c-COMPARE-006: 1월_totalPrice가 49 item 중 24개 non-zero → 전체 sample에서 unique > 1 → 보존 (fix 발동).
  const _uniformCache = new Map();
  const _isUniform = (col) => {
    if (_uniformCache.has(col)) return _uniformCache.get(col);
    if (!items[0] || items[0][col] === undefined) {
      _uniformCache.set(col, false);
      return false;
    }
    const vals = items.slice(0, 200).map(i => i[col]);
    const unique = new Set(vals.map(v => JSON.stringify(v)));
    const result = unique.size <= 1;
    _uniformCache.set(col, result);
    return result;
  };
  const _hasNonUniformSibling = (col) => {
    if (col.startsWith('_')) return false;
    const idx = col.indexOf('_');
    if (idx <= 0 || idx >= col.length - 1) return false;
    const suffix = col.slice(idx + 1);
    if (!suffix || suffix.includes('_')) return false;
    for (const other of columns) {
      if (other === col) continue;
      if (other.startsWith('_')) continue;
      const otherIdx = other.indexOf('_');
      if (otherIdx <= 0 || otherIdx >= other.length - 1) continue;
      const otherSuffix = other.slice(otherIdx + 1);
      if (otherSuffix === suffix && !_isUniform(other)) return true;
    }
    return false;
  };

  if (items.length > 1 && _uniformDropEnabled) {
    columns = columns.filter(col => {
      // [L9b-CROSS-002 cycle 1] multi-period prefix variant uniform-drop — 보호 우회 분기 (sibling variant 비-uniform 존재 시)
      const _isDegenerateVariant = _isUniform(col) && _hasNonUniformSibling(col);
      if (NAME_FIELDS.includes(col)) return true;
      if (!_isDegenerateVariant && protectedCols.has(col)) return true;
      // [Cat5-INFO_LOSS] LLM이 명시 요청한 컬럼은 uniform이어도 보호 — 사용자 질의 의도 반영
      if (!_isDegenerateVariant && _llmRequestedCols.has(col)) return true;
      // [L23-FILTER-FOCUS] 이전 turn에서 표시된 컬럼은 filter 후에도 보호 — 정보 연속성
      if (!_isDegenerateVariant && _preFilterProtectedCols.has(col)) return true;
      // [COL_MISSING P3] DETAIL intention identity-prefix field — uniform이어도 row 식별용 보호
      if (_detailIdentityProtected.has(col)) return true;
      // [L9c-COMPARE-006 — STOCHASTIC_FINALTEXT_TABLE_DROP] sample full items (cap 200) — 첫-20 편향 false uniform 회피 (_isUniform 동일 정합)
      const vals = items.slice(0, 200).map(i => i[col]);
      const unique = new Set(vals.map(v => JSON.stringify(v)));
      if (unique.size <= 1) {
        removedUniformColumns.push(col);
        if (_isDegenerateVariant && TOOL_CALLING_LOG) {
          console.log(`[autoGenerateDisplay] [L9b-CROSS-002] degenerate variant drop: "${col}" (sibling variant non-uniform)`);
        }
        return false;
      }
      return true;
    });

    // 균일값 제거로 컬럼이 1개 이하 남은 극단 케이스: 정보 손실 방지로 균일값 컬럼 1개 복원
    if (columns.length < 2 && removedUniformColumns.length > 0) {
      columns.push(removedUniformColumns[0]);
    }
  }

  // 유저가 정렬을 적용하지 않은 경우만 자동 그룹핑/정렬
  const userSorted = !!buffer.appliedSort;

  if (!userSorted) {
    // rank 필드가 있으면 API가 이미 정렬한 것으로 간주 → 폴백 스킵
    const sample = items[0];
    const hasRankField = sample && ('rank' in sample);

    if (!hasRankField && sample) {
      // 폴백 기본 정렬: 날짜 → 숫자 → 이름
      const dateCol = columns.find(c =>
        /date|visit|created|modified/i.test(c) &&
        typeof sample[c] === 'string' && /^\d{4}-\d{2}-\d{2}/.test(sample[c])
      );
      if (dateCol) {
        items.sort((a, b) => {
          const da = a[dateCol] || '';
          const db = b[dateCol] || '';
          return db.localeCompare(da);  // desc (최근순)
        });
      } else {
        const numCol = columns.find(c =>
          typeof sample[c] === 'number' && !/phone|id|code/i.test(c)
        );
        if (numCol) {
          items.sort((a, b) => (b[numCol] ?? 0) - (a[numCol] ?? 0));  // desc (높은 순)
        } else {
          items.sort((a, b) => {
            const na = a.name || '';
            const nb = b.name || '';
            return na.localeCompare(nb, 'ko');  // asc (가나다순)
          });
        }
      }
    }

    // memberId 그룹핑 (유저 정렬 없을 때만)
    items = groupByMember(items);
  }

  // D-day 계산 (만료일 컬럼이 있으면) — 값만 계산하고, 정렬은 유저 정렬 없을 때만
  const { items: ddayItems, hasRemainingDays } = userSorted
    ? addRemainingDaysNoSort(items, columns)
    : addRemainingDays(items, columns);
  items = ddayItems;

  // remainingDays 컬럼이 생겼으면 columns에 추가 (only 모드이거나 exclude 된 경우 제외)
  if (hasRemainingDays && !columns.includes('remainingDays')) {
    if (!prefs?.only && !prefs?.exclude?.includes('remainingDays')) {
      columns.push('remainingDays');
    }
  }

  // 필터에서 사용된 compute 필드를 자동으로 컬럼에 추가
  const computesFromFilters = new Set();
  for (const filter of buffer.appliedFilters) {
    if (filter.compute) {
      computesFromFilters.add(filter.compute);
    }
  }

  for (const computeType of computesFromFilters) {
    // FIELD_REGISTRY에서 해당 compute를 가진 필드 찾기
    for (const [field, config] of Object.entries(FIELD_REGISTRY)) {
      const computeConfig = config.computes?.[computeType];
      if (computeConfig?.handler && computeConfig?.virtual) {
        const columnName = computeConfig.virtual;
        if (!columns.includes(columnName)) {
          const result = computeConfig.handler(items, columns);
          items = result.items || result;
          columns.push(columnName);
        }
        break;
      }
    }
  }

  // SUMMARY-OVERRIDE 비교 표 — period를 columns[0]에 강제 prepend (비교 표는 기간이 첫 컬럼)
  // 변화량/변화율은 표에 자동 추가하지 않고 outro 자연어로만 표현 (augmentOutroWithChange)
  if (hasChangeFields) {
    if (columns.includes('period') && columns[0] !== 'period') {
      columns = ['period', ...columns.filter(c => c !== 'period')];
    }
  }

  // max_count 적용
  // [L3-COMPUTE-MIN-001] EXTREME LIMIT CAP — 사용자 input이 "최고/최저/가장/제일/최다/최소 + 단일 entity" 지정 시
  //   max_count를 limit로 강제 cap. extremeIntentGuide의 retryAttempted=true bypass 후에도 (LLM이 max_count=10 등 명시) display 단계에서 사용자 명시 limit 준수.
  //   본질: 사용자가 1건/N건 정확히 명시한 intent를 표시 단계에서 절대 위반하지 않음 → SELF_CONTRADICTION ("최소 회원 알려줘"인데 10건 표시) 차단.
  //   generic: extremeIntentGuide의 regex만 의존 (case ID/intention/도메인 무참조). 사용자 input keyword detection만 사용.
  const _userInputForCap = (typeof session?.getUserInput === 'function') ? (session.getUserInput() || '') : '';
  const _extremeIntentForCap = detectExtremeIntent(_userInputForCap);
  const _rawMaxCount = max_count || 150;
  const maxCount = _extremeIntentForCap
    ? Math.min(_rawMaxCount, _extremeIntentForCap.limit)
    : _rawMaxCount;
  if (_extremeIntentForCap && maxCount < _rawMaxCount && TOOL_CALLING_LOG) {
    console.log(`[autoGenerateDisplay] [L3-COMPUTE-MIN-001] EXTREME LIMIT CAP: max_count ${_rawMaxCount} → ${maxCount} (user input "${_userInputForCap.slice(0,40)}" extreme limit=${_extremeIntentForCap.limit})`);
  }
  const sliced = items.slice(0, maxCount);

  // items 경량화 — 표시 컬럼 + 렌더링 필수 필드만 추림 (columns 확장 후 keepFields 결정)
  const keepFields = [...new Set([...columns, ...ESSENTIAL_FIELDS])];
  const liteItems = sliced.map(item => {
    const lite = {};
    for (const field of keepFields) {
      if (item[field] !== undefined) lite[field] = item[field];
    }
    return lite;
  });

  // 컬럼 수 제한 — name/phone은 "회원" 컬럼으로 별도 렌더링되므로 제외하고 데이터 컬럼만 제한
  // LLM이 명시한 columns는 인텐션+사용자 질문+availableFields를 본 의도로 신뢰 (HARD_CAP 이내)
  // 자동 생성(LLM 미명시)인 경우만 5개 한도로 절단.
  // 절단 시 userInput keyword 매칭 필드(FIELD_REGISTRY) 우선 포함 — "이탈률" 입력 시 churnRate 보호
  const HARD_CAP = 12;
  const AUTO_MAX = 5;
  const isLlmColumns = !!llmColumns && Array.isArray(llmColumns) && llmColumns.length > 0;
  const essentialSet = new Set([
    ...NAME_FIELDS,
    ...PHONE_FIELDS_LIST,
    ...(hasChangeFields ? ['period'] : []),
  ]);
  const dataColumns = columns.filter(c => !essentialSet.has(c));
  const essentialColumns = columns.filter(c => essentialSet.has(c));
  // LLM 명시 columns가 HARD_CAP 이내면 LLM 의도 보존, 그 외(자동 생성 또는 비정상 다수)는 절단
  const shouldTruncate = isLlmColumns
    ? columns.length > HARD_CAP
    : dataColumns.length > AUTO_MAX;
  if (shouldTruncate) {
    const limit = isLlmColumns ? HARD_CAP : AUTO_MAX;
    const _uiLim = session.getUserInput?.() || '';
    const _kwLim = new Set(findKeywordMatchedFields(_uiLim, liteItems[0] || items[0] || {}));
    const _kwCols = dataColumns.filter(c => _kwLim.has(c));
    const _otherCols = dataColumns.filter(c => !_kwLim.has(c));
    const _limited = [..._kwCols, ..._otherCols].slice(0, limit);
    columns = [...essentialColumns, ..._limited];
  }

  // 라벨: LLM 지정 라벨 우선, 없으면 자동 생성
  const autoLabels = buildColumnLabels(columns, buffer.availableFields);
  const columnLabels = llmColumnLabels
    ? { ...autoLabels, ...llmColumnLabels }
    : autoLabels;
  const numericColumns = detectNumericColumns(liteItems, columns);
  const stats = computeStats(liteItems, numericColumns);

  // [RANK RECOMPUTE] sort 적용 시 rank-like 필드를 표시 순서대로 1..N 재계산
  // 백엔드 원본 rank(다른 기준)가 클라이언트 sort 결과와 불일치 시 사용자 혼란 → 표 순서 == rank 일관성 보장
  // 구조 기반: 필드명 패턴(rank/ranking/rankNumber) 또는 라벨(순위/순서/랭킹) 매칭. sort 기준 자체는 제외 (의미 보존)
  if (buffer.appliedSort?.field) {
    const sortField = buffer.appliedSort.field;
    const RANK_FIELD_RE = /^rank(ing|Number|s)?$/i;
    const RANK_LABEL_RE = /^(순위|순서|랭킹)$/;
    const rankLikeCols = columns.filter(c => {
      if (c === sortField) return false;
      if (RANK_FIELD_RE.test(c)) return true;
      const label = columnLabels[c] || '';
      return RANK_LABEL_RE.test(label);
    });
    if (rankLikeCols.length > 0) {
      for (let i = 0; i < liteItems.length; i++) {
        for (const rc of rankLikeCols) {
          if (rc in liteItems[i]) liteItems[i][rc] = i + 1;
        }
      }
    }
  }

  const totalCount = liteItems.length;
  const remaining = items.length - totalCount;

  // 확정된 컬럼 저장 (다음 display 호출 시 재사용)
  session.saveResolvedColumns(columns);
  // [P1-D3] outro {{TABLE_COLS}} 치환용 — 한국어 라벨 map도 같이 저장
  if (typeof session.saveResolvedColumnLabels === 'function') {
    session.saveResolvedColumnLabels(columnLabels);
  }

  // [P0-1] show_display intro/outro placeholder — source 통일된 {{COUNT}} {{REMAINING}} 우선 처리 + legacy {count} {remaining} fallback (LLM token cache/혼동 대응)
  const _substDisplayVars = (s) => {
    if (!s) return s;
    return s
      .replace(/\{\{\s*COUNT\s*\}\}/gi, String(totalCount))
      .replace(/\{\s*count\s*\}/gi, String(totalCount))
      .replace(/\{\{\s*REMAINING\s*\}\}/gi, String(remaining))
      .replace(/\{\s*remaining\s*\}/gi, String(remaining));
  };

  // [L3-COMPUTE-MIN-001] extreme intent satisfaction signal — 사용자가 "최고/최저/가장/제일/최다/최소 + 단일 entity"를 명시하고
  //   표시 건수가 그 limit 이하면 user intent 100% 충족 상태. 이 경우 아래 cap notice / intro count inflation은 의미 반대:
  //   사용자는 "1건"을 원했고 1건을 받음 → "(전체 173건 중 상위 1건 표시)" 같은 부가 안내는 마치 "잘려서 1건만 표시한 거야"로 오인 + intro "1명이야" → "173명이야" 변환은 사용자 input 정의역과 충돌 (SELF_CONTRADICTION A-RED-2).
  //   본질: extreme single entity intent는 "list cap 안내" 시맨틱과 직교 — 1건이 정답이지 cap의 부산물 아님.
  //   generic: extremeIntentGuide의 regex만 의존 (case ID/intention 무참조). max_count는 caller가 EXTREME LIMIT GUARD 후 1로 명시한 신호. 또는 위 EXTREME LIMIT CAP에서 강제 cap 적용 신호.
  // 위 EXTREME LIMIT CAP에서 동일 detect 수행했으므로 동일 시그널 재사용.
  const _extremeSatisfied = !!(_extremeIntentForCap && totalCount <= _extremeIntentForCap.limit);

  // [L9a-MEMBER-005 cycle 1] explicit top-N request satisfaction signal — 사용자가 "상위 5명/탑 3개/top 10/먼저 5건/처음 N명"
  //   명시적 top-N limit을 요청하고 표시 건수가 그 limit 이하면 user intent 100% 충족.
  //   본질 (FALSE_COUNT bug): intro inflation guard (L23-FILTER-FOCUS-001 line 1670)는 cap 발동 시 "5명" → "285명" 토큰 대체.
  //   user가 "상위 5명만" 발화 + intro에 "상위 {{COUNT}}명만" placeholder → 치환 후 "상위 5명만" → inflation guard가
  //   "5명" 토큰을 totalCount+remaining=285로 대체 → "상위 285명만" 사용자 입력 직접 모순.
  //   _extremeSatisfied와 동일 시맨틱 (사용자 explicit limit + 표 동일 N) — 동일 skip 분기 적용.
  //   generic: detectExplicitTopNIntent regex만 의존 (intention/case ID 무참조). cluster cover: 상위/탑/top/먼저/처음 + 명/건/개/회/위.
  const _topNIntentForCap = detectExplicitTopNIntent(_userInputForCap);
  const _topNRequestSatisfied = !!(_topNIntentForCap && totalCount <= _topNIntentForCap.limit);
  const _userExplicitLimitSatisfied = _extremeSatisfied || _topNRequestSatisfied;

  // [Weak6-NO_CAP_INFO] cap notice — 데이터가 max_count로 잘렸을 때 사용자에게 전체 건수 대비 표시 건수 명시
  // outro에 이미 cap 관련 표현이 없을 때만 append (LLM이 직접 작성했으면 중복 방지)
  // [L3-COMPUTE-MIN-001] extreme intent 충족 시 skip — 사용자가 명시적으로 1건 요청 → cap 안내가 오히려 "잘림" 오인
  let _finalOutro = _substDisplayVars(outro) || null;
  if (remaining > 0 && !_extremeSatisfied) {
    const total = totalCount + remaining;
    const capNotice = `(전체 ${total}건 중 상위 ${totalCount}건 표시)`;
    const CAP_ALREADY_RE = /전체\s*\d+\s*건\s*중|상위\s*\d+\s*건/;
    if (!_finalOutro) {
      _finalOutro = capNotice;
    }
    else if (!CAP_ALREADY_RE.test(_finalOutro)) {
      _finalOutro = `${_finalOutro}\n${capNotice}`;
    }
  }

  // [Cat5-INFO_LOSS] intro 자동 생성 — LLM이 intro 미제공 시 건수 + 컨텍스트 자동 생성 (dual sync with nodeAgent.mjs)
  // generic: intention/도메인 하드코딩 X, columnLabels 기반 컨텍스트 구성
  // [L3-COMPUTE-MIN-001] extreme intent 충족 시 displayed count 사용 (1건 등) — 사용자 intent와 일치
  let _finalIntro = _substDisplayVars(intro) || null;
  if (!_finalIntro) {
    const _total = _extremeSatisfied ? totalCount : (totalCount + remaining);
    _finalIntro = `총 ${_total}건이야`;
  }

  // [L23-FILTER-FOCUS-001 / FALSE_COUNT_TRANSPARENCY] intro count integrity under cap truncation
  //   본질: remaining > 0 (max_count cap 적용) 시 intro에 "총 150명" (=totalCount displayed) 표시되면
  //   사용자는 데이터가 절단된 사실을 모르고 150명이 전체라고 인식. outro에 cap notice "(전체 N건 중 상위 M건 표시)"가
  //   부가되긴 하지만 intro의 misleading count가 first-read 인상으로 남음 → CALC_INCONSISTENT/FALSE_COUNT 본질.
  //   universal fix: cap 활성 시 intro 내 "<digit>+<count classifier>" 패턴 중 totalCount 정수와 정확히 일치하는 토큰을
  //   total (totalCount+remaining) 으로 대체. classifier set은 한국어 noun-classifier 표준 (명/건/개/회/마리/장/곳/원/분/시간/팀/곡).
  //   conservative: totalCount와 정확히 match된 숫자만 대체 — 다른 metric(평균/등) 보존. 도메인/intention/case ID 의존 없음.
  // [L3-COMPUTE-MIN-001] extreme intent 충족 시 skip — "1명" → "173명" 변환이 사용자 singular intent와 충돌 (A-RED-2 SELF_CONTRADICTION)
  // [L9a-MEMBER-005 cycle 1] explicit top-N intent 충족 시 동일 skip — "상위 5명" → "상위 285명" 변환이 사용자 발화 직접 모순 (FALSE_COUNT)
  // [L22-DATERANGE-002 / F14 FALSE_COUNT_TRANSPARENCY] inflation 단독 시 intro 자체 transparency 보강
  //   본질 (M14 CAP_DISCLOSURE_OMISSION): inflation 후 intro가 "총 265명이야"로 끝나면 first-read 시 사용자는 265건 전체 가시 인식.
  //   outro의 "(전체 N건 중 상위 M건 표시)" 안내가 부가되지만 intro 자체 self-contained transparency 부재 → glance 시 misleading.
  //   universal fix: inflation 발생 (≥1 토큰 대체) 시 intro에 이미 "표시" / "상위" / "중 N건" 같은 cap-aware 키워드가
  //   없으면 " (상위 totalCount{classifier} 표시)" 토큰 부가. classifier는 inflation 위치 직후 captured classifier 재사용.
  //   conservative: inflation 발생 + cap-already 키워드 부재 + extreme/topN-skip 분기 외 — 도메인/intention/case ID 무참조.
  if (remaining > 0 && _finalIntro && !_userExplicitLimitSatisfied) {
    const _total = totalCount + remaining;
    const _CLASSIFIER_INTRO_RE = new RegExp(`(?<![\\d.])${totalCount}(\\s*(?:명|건|개|회|마리|장|곳|원|분|시간|팀|곡))(?![\\d])`, 'g');
    let _capturedClassifier = null;
    const _afterInflation = _finalIntro.replace(_CLASSIFIER_INTRO_RE, (_m, cls) => {
      if (!_capturedClassifier) _capturedClassifier = (cls || '').trim();
      return `${_total}${cls}`;
    });
    if (_afterInflation !== _finalIntro) {
      _finalIntro = _afterInflation;
      // intro 자체 transparency 보강 — cap-already 키워드 부재 시만
      const _INTRO_CAP_ALREADY_RE = /상위\s*\d+\s*[명건개회마리장곳원분시간팀곡]|중\s*\d+\s*[명건개회마리장곳원분시간팀곡]|표시(?:\s*돼|\s*했|\s*했어|\s*된|\s*됨)?/;
      if (_capturedClassifier && !_INTRO_CAP_ALREADY_RE.test(_finalIntro)) {
        _finalIntro = `${_finalIntro} (상위 ${totalCount}${_capturedClassifier} 표시)`;
      }
    }
  }

  // [EXCLUDED_RECONCILE] excludedColumns reconciliation — line 1192 이후 columns에 추가된 derived 필드
  //   (remainingDays / computesFromFilters virtual 필드 등) 가 excludedColumns에 잔존하지 않도록 최종 columns 기준 재계산.
  //   본질: raw items에 없지만 display path에서 augment된 필드는 사용자 입장 "표시됨" — excluded 대상 X.
  //   기존 버그: T1에서 saved resolvedColumns에 remainingDays 포함 → T2에서 LLM이 columns 미명시 시
  //   addRemainingDays가 columns 끝에 다시 append하지만 excludedColumns에는 그대로 남아 prevColumns/displayResult.columns
  //   "same" 매칭 시 false rejection (사용자가 39명 명단 못 봄). 구조 기반 generic fix — case ID 하드코딩 X.
  if (excludedColumns.length > 0) {
    const _finalColSet = new Set(columns);
    excludedColumns = excludedColumns.filter(c => !_finalColSet.has(c));
  }

  // [UNDISPLAYED METRIC AUTO-APPEND] 미표시 슬롯 backend summary를 outro에 결정적 보강 (1 turn 1회) //
  const _undisplayedOutro = buildUndisplayedSlotSummaryOutro(session);

  if (_undisplayedOutro) {
    _finalOutro = _finalOutro ? `${_finalOutro}\n${_undisplayedOutro}` : _undisplayedOutro;
    if (TOOL_CALLING_LOG) console.log('[autoGenerateDisplay] UNDISPLAYED AUTO-APPEND:', _undisplayedOutro.slice(0, 100));
  }

  return {
    intro: _finalIntro,
    outro: _finalOutro,
    items: liteItems,
    columns,
    columnLabels,
    columnFormats: buildColumnFormats(columns),
    totalCount,
    maxCount,
    remaining,
    numericColumns,
    stats,
    appliedFilters: buffer.appliedFilters,
    appliedSort: buffer.appliedSort,
    availableFields: normalizeAvailableFields(buffer.availableFields),
    // [P2-1 anchor 7] renderDisplayAsText substitute path 보조 — LLM이 show_display 후 finish() 없이 outro 자체 append 시 substitute 누락 차단
    // shallow copy buffer는 saveResolvedColumnLabels mutation 미반영 → local vars로 fresh build
    _buffer: {
      items: liteItems,
      summary: buffer.summary,
      availableFields: buffer.availableFields,
      resolvedColumns: columns,
      resolvedColumnLabels: columnLabels,
    },
    // [L3-COMPUTE-MIN-001] extreme intent 충족 신호 — renderDisplayAsText에서 "... 외 N건" suffix 차단용
    _extremeSatisfied,
    ...(excludedColumns.length > 0 && { excludedColumns }),
  };
}

// display 응답을 텍스트로 렌더링 (DB 저장용) //
export function renderDisplayAsText(displayResult) {
  if (!displayResult?.items || displayResult.items.length === 0) return null;

  let { intro, outro } = displayResult;
  const { items, columns } = displayResult;

  // [P2-1+P2-2 anchor 7] outro append path substitute — LLM이 show_display 후 finish() 없이 text content로 outro append 시
  // substitutePlaceholders 누락 → {{COUNT}}/{{TABLE_COLS}} raw 잔존 + column enum drop 미발동. _buffer 통해 멱등 substitute.
  if (displayResult._buffer) {
    if (intro) {
      const _r = substitutePlaceholders(intro, displayResult._buffer);
      intro = _r.text;
    }
    if (outro) {
      const _r = substitutePlaceholders(outro, displayResult._buffer);
      outro = _r.text;
    }
  }
  const fieldsMeta = normalizeAvailableFields(displayResult.availableFields);

  // [Cat5-INFO_LOSS] column label header — 텍스트 렌더링 시 컬럼 라벨 헤더 행 추가 (dual sync with nodeAgent.mjs)
  // displayResult.columnLabels 존재 시 각 컬럼의 한국어 라벨을 헤더로 표시 → raw data dump 방지
  const columnLabels = displayResult.columnLabels || {};
  const headerValues = columns.map(col => columnLabels[col] || col);
  const headerLine = `[${headerValues.join(' | ')}]`;

  const lines = items.map((item, i) => {
    const values = columns.map(col => {
      const val = getByPath(item, col) ?? item[col] ?? deepGet(item, col);
      // [NICKNAME_RAW_LITERAL] backend가 미가입/미등록 회원에 일괄로 채우는 system placeholder
      // string(예: nickName="미가입")이 finalText 표 cell에 그대로 노출되는 leak 차단.
      // SYSTEM_PLACEHOLDER_VALUES는 per-field whitelist — false-positive 방지.
      if (isSystemPlaceholderValue(col, val)) return '-';
      const label = getDisplayLabel(col, val, fieldsMeta[col]);
      // [L4-DERIVE-ADD] fieldsMeta[col] 전달 — derive 필드의 currency unit propagation 인식 (Korean derive field name 일관 comma format)
      return label ?? formatValue(val, col, fieldsMeta[col]);
    });
    return `${i + 1}. ${values.join(' | ')}`;
  });

  // [L3-COMPUTE-MIN-001] extreme intent 충족 시 "... 외 N건" suffix 차단 — 사용자가 명시한 1건이 정답, 잘림 안내는 SELF_CONTRADICTION 유발
  if (displayResult.remaining > 0 && !displayResult._extremeSatisfied) {
    lines.push(`... 외 ${displayResult.remaining}건`);
  }

  const parts = [];
  if (intro) parts.push(intro);
  parts.push(headerLine + '\n' + lines.join('\n'));
  if (outro) parts.push(outro);

  return parts.join('\n\n');
}

// finish response outro의 공손 재제안 문장 자동 제거 //
// - 매칭1: (원하시면|원하면|필요하시면|필요하면 등) + 0~80자 + (드릴게요|보여줄게|정리해줄게|해줄게 등) — 존댓말+반말 커버
// - 매칭2: (다음으로는?|이어서|추가로|이번엔|이번에는?|다음에는?) + 0~80자 + (드릴게요|보여줄게|정리해줄게 등) — prefix 없는 변형 차단
// - sentence-level 제거 → 정당한 후속 안내("추가로 X도 가능합니다") 보존
export function stripPoliteReproposal(responseText) {
  if (!responseText) return responseText;
  const PATTERN_1 = /(원하시면|원하신다면|원하면|필요하시면|필요하다면|필요하면)[^.!?\n]{0,80}(드릴게요|드릴까요|드릴까\?|해\s*드릴|보여\s*드릴|정리\s*해\s*드릴|정리\s*해|확인\s*해|조회\s*해|추려\s*드릴|이어서\s*볼|이어서\s*드릴|설명\s*드릴|이어서\s*설명|볼\s*수\s*도?\s*있어요|볼게요|보여\s*줄게|보여\s*줄까|정리\s*해\s*줄게|정리\s*해\s*줄까|해\s*줄게|해\s*줄까|줄게요?|줄까요?)/;
  const PATTERN_2 = /(다음으로는?|이어서|추가로|이번엔|이번에는?|다음에는?)[^.!?\n]{0,80}(드릴게요|드릴까요|드릴\s*수\s*도?\s*있어요|보여\s*드릴|정리\s*해\s*드릴|확인\s*해\s*드릴|추려\s*드릴|이어서\s*볼|볼\s*수\s*도?\s*있어요|볼게요|보여\s*줄게|보여\s*줄까|정리\s*해\s*줄게|정리\s*해\s*줄까|해\s*줄게|해\s*줄까|줄게요?|줄까요?)/;
  // FOLLOWUP_OUTRO_DECIMAL_SPLIT: digit-flanked period sentence delim 오인 차단
  const sentences = responseText.split(/(?<=\n|(?<!\d)[.!?](?!\d))/);
  const filtered = sentences.filter(s => !PATTERN_1.test(s) && !PATTERN_2.test(s));
  return filtered.join('').replace(/\n{3,}/g, '\n\n').replace(/[ \t]+$/gm, '').trim();
}

export function stripMarkdownBold(text) {
  if (!text) return text;
  return text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/__([^_]+)__/g, '$1');
}

// [RAW_DATETIME] finalText/intro/outro에 남은 raw ISO datetime → 날짜만 추출 (dual sync with nodeAgent.mjs) //
// pattern: YYYY-MM-DDThh:mm:ss[.fractional] 또는 YYYY-MM-DD hh:mm:ss[.fractional]
// year 1900-2099 범위 검증으로 false positive 차단
export function stripRawIsoDatetime(text) {
  if (!text) return text;
  return text.replace(/(\d{4})-(\d{2})-(\d{2})[T ]\d{2}:\d{2}:\d{2}(?:\.\d+)?/g, (match, yyyy) => {
    const year = parseInt(yyyy, 10);

    if (year < 1900 || year > 2099) return match;

    return `${yyyy}-${match.slice(5, 7)}-${match.slice(8, 10)}`;
  });
}

// [Cat8-REPEAT] finish response에서 intro와 겹치는 prefix 문장 전부 제거 (dual sync with nodeAgent.mjs) //
// intro에 placeholder({{COUNT}} 등)가 포함된 상태에서도 매칭 가능하도록
// placeholder를 와일드카드로 변환 후 문장 단위 비교
// L9a-MEMBER-005 cycle 3: word trailing 구두점 strip + 한국어 stem-prefix 매칭 (isIntroDuplicate와 동일)
// ROOT_CAUSE_10B_DUP_HEADER (2026-05-30): placeholder strip을 양쪽(intro/finish) 모두 적용 + leading-bigram head-noun 일치 검출 추가
//   - 기존: introNorm만 placeholder strip → "총 {{COUNT}}건이에요" vs "총 건이에요" 비매칭
//   - 기존: 시제/어미 변형 minLen<4 reject로 "목록을"(3 char) 같은 단어 미매칭
//   - 수정: 양쪽 placeholder strip + leading 2 word stem-equality 시 duplicate (DUP_HEADER 본질)
export function stripIntroOverlap(introText, finishText) {
  if (!introText || !finishText) return finishText || '';

  // placeholder를 제거한 intro 문장 목록 생성 — "총 {{COUNT}}명이야" -> "총 명이야" (core words)
  const introNorm = introText.replace(/\{\{[^}]+\}\}/g, '').replace(/\s+/g, ' ').trim();
  const introSentences = introNorm.split(/\n|(?<!\d)[.!?](?!\d)/).map(s => s.trim()).filter(s => s.length > 3);

  // finish response를 문장 단위 분리 — decimal 보호 (한국어 31.9세 같은 digit-flanked period가 sentence delim으로 잘못 분리되지 않도록)
  const finishSentences = finishText.split(/(?<=(?<!\d)[.!?](?!\d))\s*/);
  let stripCount = 0;

  // 단어 단위 분리 + trailing 구두점 strip — 시제/어미 변형 흡수용
  const _splitWords = (s) => s
    .split(/\s+/)
    .map(w => w.replace(/[.,!?;:、]+$/, '').replace(/^[.,!?;:、]+/, ''))
    .filter(w => w.length > 1);

  // 한국어 stem-prefix 매칭 (isIntroDuplicate와 동일)
  const _wordsMatch = (a, b) => {
    if (a === b) return true;
    const minLen = Math.min(a.length, b.length);
    if (minLen < 4) return false;
    let prefix = 0;
    while (prefix < minLen && a[prefix] === b[prefix]) prefix++;
    return prefix >= 3 && prefix >= Math.floor(minLen * 0.5);
  };

  // [DUP_HEADER] leading head-noun 일치 (isIntroDuplicate와 동기): 첫 단어 stem-동등 또는 head bigram 일치
  //   ROOT_CAUSE_10B_DUP_HEADER (2026-05-30):
  //     - 첫 단어 ≥ 3 char prefix stem-동등 → head noun 반복 (조사/어미 변형 흡수, 예: "유효회원"/"유효회원은")
  //     - 첫 단어 정확 2 char + 동일 + 두번째 단어 ≥ 2 char prefix stem-동등 → bigram head 반복 (예: "코치 목록")
  //   false positive 차단: 첫 단어 길이 ≥ 2 강제 + 긴 문장은 두번째 단어 stem-동등 강제
  const _stemPrefix = (a, b) => {
    if (!a || !b) return 0;
    const minLen = Math.min(a.length, b.length);
    let prefix = 0;
    while (prefix < minLen && a[prefix] === b[prefix]) prefix++;
    return prefix;
  };
  const _leadingHeadMatch = (aArr, bArr) => {
    if (aArr.length < 1 || bArr.length < 1) return false;
    const a0 = aArr[0], b0 = bArr[0];
    if (!a0 || !b0 || a0.length < 2 || b0.length < 2) return false;
    const headPrefix = _stemPrefix(a0, b0);
    if (headPrefix >= 3) {
      if (aArr.length <= 2 || bArr.length <= 2) return true;
      const a1 = aArr[1], b1 = bArr[1];
      if (a1 && b1 && a1.length >= 2 && b1.length >= 2 && _stemPrefix(a1, b1) >= 2) return true;
      return false;
    }
    if (headPrefix >= 2 && a0.length === headPrefix && b0.length === headPrefix) {
      const a1 = aArr[1], b1 = bArr[1];
      if (a1 && b1 && a1.length >= 2 && b1.length >= 2 && _stemPrefix(a1, b1) >= 2) return true;
    }
    return false;
  };

  // [DUP_HEADER] intro 전체 단어 set (모든 sentence 통합) — post-subset 검사용 (ROOT_CAUSE_10B_DUP_HEADER 2026-05-30)
  const _allIntroWords = (() => {
    const set = [];
    for (const s of introSentences) {
      const cleaned = s.replace(/\d+/g, '').replace(/\s+/g, ' ').trim();
      for (const w of _splitWords(cleaned)) set.push(w);
    }
    return set;
  })();

  // [ROOT_CAUSE_INTRO_OUTRO_COUNT_DUP] 2026-05-30 (L10-INFRA-MEMBER-001 + L10-MEMBERSHIP-SCHEDULE-001):
  //   intro 어딘가에 numeric+classifier count phrase (예: "총 21건이에요", "총 {{COUNT}}명입니다") 가 있을 때,
  //   post-display 또는 finish 후속 sentence가 동일 classifier로 끝나는 "count 안내" 문장이면 새로운 정보 없음 → strip.
  //   본질: show_display intro에 count 안내가 이미 들어갔는데, LLM이 후속 stop/finish text에서 "총 N건입니다" 식으로 다시 안내 반복.
  //   - 기존 _wordsMatch는 한국어 한 글자 단어 ('총') filter로 강조 패턴 미인식 ('건이에요' vs '건입니다' prefix 1만 공유 → fail)
  //   - 기존 stripCountContradictionSentences는 count != displayCount(overshoot) 조건이라 count == displayCount 정상 중복 못 잡음
  //   universal: classifier set은 한국어 noun-classifier 표준 (명/건/개/회/마리/장/곳/원/분/시간), case ID/한국어 input keyword 의존 X
  //   intro check는 placeholder/digit 보존한 raw introText로 수행 (digit strip된 introSentences는 classifier 매칭 불가)
  //   conservative: outro와 intro classifier가 SAME인 경우만 strip (다른 metric: "평균 60분" 같은 follow-up 정보 보존)
  const _COUNT_CLASSIFIER_RE_G = /(?:\d+|\{\{[^}]+\}\})\s*(명|건|개|회|마리|장|곳|원|분|시간)/g;
  const _extractClassifiers = (s) => {
    const set = new Set();
    const re = new RegExp(_COUNT_CLASSIFIER_RE_G.source, 'g');
    let m;
    while ((m = re.exec(s)) !== null) set.add(m[1]);
    return set;
  };
  const _introClassifiers = _extractClassifiers(introText);
  const _introHasCountPhrase = _introClassifiers.size > 0;
  // count-phrase-only sentence 판정: digit 또는 placeholder + classifier + 짧음 (≤ 5단어, 한국어 한 글자 단어 포함)
  //   intro에 등장한 동일 classifier를 사용한 경우만 (다른 단위 follow-up 정보 보존)
  // 예: "총 21건입니다" / "21명이에요" / "{{COUNT}}개입니다" / "21건이에요" / "총 3명이야"
  const _isCountPhraseOnly = (s) => {
    const stripped = s.replace(/[.!?]+$/, '').trim();
    if (!stripped) return false;
    if (stripped.length > 16) return false;
    // 한국어 한 글자 단어 포함하여 raw word count 검사
    const rawWords = stripped.split(/\s+/).filter(w => w.length >= 1);
    if (rawWords.length > 5) return false;
    // sentence에서 classifier 추출 → intro classifier set과 intersection
    const sClassifiers = _extractClassifiers(stripped);
    if (sClassifiers.size === 0) return false;
    for (const c of sClassifiers) {
      if (_introClassifiers.has(c)) return true;
    }
    return false;
  };

  for (const fSentence of finishSentences) {
    const fNorm = fSentence.replace(/[.!?]+$/, '').trim();

    if (fNorm.length <= 3) {
      stripCount++;
      continue;
    }

    // intro 문장과 겹치는지 확인 — 숫자/placeholder 제거 후 core 단어 비교 (양쪽 normalize 통일)
    const fWords = fNorm.replace(/\{\{[^}]+\}\}/g, '').replace(/\d+/g, '').replace(/\s+/g, ' ').trim();
    let isOverlap = false;

    // [ROOT_CAUSE_INTRO_OUTRO_COUNT_DUP] count-phrase-only sentence + intro에 동일 classifier count phrase 존재 → overlap
    //   conservative: intro가 count phrase 보유 + finish sentence가 count-phrase-only인 경우만
    if (_introHasCountPhrase && _isCountPhraseOnly(fSentence)) {
      isOverlap = true;
    }

    // [DUP_HEADER] post-subset-of-intro: fSentence 단어 모두 intro 전체에 stem-동등 매칭 + 짧음 (≤ 4 단어)
    //   예: intro "코치 목록을 확인했어요. 총 명입니다" / fSentence "코치 명입니다" → 모두 intro에 존재 → strip
    const fWordArrForSubset = _splitWords(fWords);
    if (fWordArrForSubset.length >= 1 && fWordArrForSubset.length <= 4 && _allIntroWords.length >= 1) {
      const allMatched = fWordArrForSubset.every(fw =>
        _allIntroWords.some(iw =>
          _wordsMatch(iw, fw) || (
            iw.length >= 2 && fw.length >= 2 &&
            ((iw.length === fw.length && iw === fw) ||
             (Math.min(iw.length, fw.length) >= 2 && _stemPrefix(iw, fw) >= 2))
          )
        )
      );
      if (allMatched) {
        isOverlap = true;
      }
    }

    if (!isOverlap) for (const iSentence of introSentences) {
      const iWords = iSentence.replace(/\d+/g, '').replace(/\s+/g, ' ').trim();

      if (iWords.length < 3 || fWords.length < 3) continue;

      // substring 비교 (양방향) — 숫자/placeholder 제거 후 core words 매칭
      if (fWords.includes(iWords) || iWords.includes(fWords)) {
        isOverlap = true;
        break;
      }

      // 단어 겹침률 비교 — stem-prefix 매칭으로 시제/어미 변형 흡수
      const iWordArr = _splitWords(iWords);
      const fWordArr = _splitWords(fWords);

      if (iWordArr.length > 0 && fWordArr.length > 0) {
        const overlapRatio = iWordArr.filter(iw => fWordArr.some(fw => _wordsMatch(iw, fw))).length / iWordArr.length;

        if (overlapRatio >= 0.6) {
          isOverlap = true;
          break;
        }

        // [DUP_HEADER] leading-bigram head-noun 일치 추가 검출
        if (_leadingHeadMatch(iWordArr, fWordArr)) {
          isOverlap = true;
          break;
        }
      }
    }

    if (!isOverlap) break;

    stripCount++;
  }

  if (stripCount === 0) return finishText;

  const remaining = finishSentences.slice(stripCount).join('').trim();

  return remaining;
}

// [Cat8-REPEAT] intro-finish 중복 감지 (digit-agnostic + stem-aware) — production useAiChat.js + nodeAgent.mjs canonical impl //
// - intro와 finish 첫 문장 비교, 숫자 제거 후 substring/word-overlap 판정
// - 숫자 normalize으로 LLM이 동일 문장에서 다른 숫자(상위 5명 vs 상위 285명) 사용해도 duplicate 검출 → stripIntroOverlap 경로로 라우팅
// - placeholder ({{COUNT}} 등) 와일드카드 처리
// - L9a-MEMBER-005 cycle 3: 단어 trailing 구두점 strip + 한국어 stem-prefix 매칭 (보여드릴게요 vs 보여드렸어요 동일 stem 인식)
//   ← 시제/어미 변형(보여드릴게요/보여드렸어요/보여드려요)으로 word overlap < 0.6 떨어져 duplicate 감지 실패하던 본질 해소
export function isIntroDuplicate(introText, postText) {
  if (!introText || !postText) return false;

  // placeholder + digit strip — stripIntroOverlap과 동일 normalize (양쪽 통일)
  const introNorm = introText.replace(/\{\{[^}]+\}\}/g, '').replace(/\d+/g, '').replace(/\s+/g, ' ').trim();

  const postFirstSentence = (postText || '').split(/\n|(?<!\d)[.!?](?!\d)/)[0].trim();
  if (postFirstSentence.length <= 5) return false;
  // ROOT_CAUSE_10B_DUP_HEADER (2026-05-30): post에도 placeholder strip 적용 — "총 {{COUNT}}건이에요" vs "총 건이에요" 매칭
  const postNorm = postFirstSentence.replace(/\{\{[^}]+\}\}/g, '').replace(/\d+/g, '').replace(/\s+/g, ' ').trim();
  if (postNorm.length <= 3) return false;

  // substring 비교 (양방향) — digit/placeholder 제거 후 core 텍스트 매칭
  const introClean = introNorm.replace(/[.!?]$/, '').trim();
  const isSubstringMatch = introClean.includes(postNorm) || postNorm.includes(introClean);

  // 단어 단위 분리 후 trailing 구두점 strip — "보여드릴게요." → "보여드릴게요" (동일 stem과 비교 가능하게)
  const _splitWords = (s) => s
    .split(/\s+/)
    .map(w => w.replace(/[.,!?;:、]+$/, '').replace(/^[.,!?;:、]+/, ''))
    .filter(w => w.length > 1);
  const introWords = _splitWords(introNorm);
  const postWords = _splitWords(postNorm);

  // 한국어 stem-prefix 매칭: 두 단어가 길이≥3 prefix 공유 시 같은 stem (시제/어미 변형 흡수)
  //   예: 보여드릴게요 ↔ 보여드렸어요 ↔ 보여드려요 → 공통 prefix "보여드" (3 char) 매칭
  //       정리했어요 ↔ 정리해요 → 공통 prefix "정리" (2 char) — 너무 짧음, 미매칭 (보수적)
  //   prefix length ≥ 3 강제로 false positive 차단 (예: 상위/상위권 매칭은 OK, 상위/상품 매칭 X — 한글자만 공유면 패스)
  const _wordsMatch = (a, b) => {
    if (a === b) return true;
    const minLen = Math.min(a.length, b.length);
    if (minLen < 4) return false;  // 짧은 단어는 정확 매칭만 (4글자 미만 단어는 stem 매칭 false positive 위험)
    let prefix = 0;
    while (prefix < minLen && a[prefix] === b[prefix]) prefix++;
    return prefix >= 3 && prefix >= Math.floor(minLen * 0.5);  // prefix ≥3 + 둘 다 50% 이상 공유
  };

  const overlap = introWords.length > 0 && postWords.length > 0
    ? introWords.filter(iw => postWords.some(pw => _wordsMatch(iw, pw))).length / introWords.length
    : 0;

  // [DUP_HEADER] leading head-noun 일치: 두 문장의 첫 단어가 stem-동등(≥2 char prefix) → head noun phrase 반복
  //   예 A: "코치 목록이에요" vs "코치 목록을 보여드렸어요" → 첫 단어 "코치" exact + 두번째 "목록을"/"목록이에요" prefix "목록" 공유
  //   예 B: "유효회원 목록을 확인했어요" vs "유효회원은 총 명입니다" → 첫 단어 "유효회원"/"유효회원은" prefix 공유 + 동일 head noun 반복
  //   ROOT_CAUSE_10B_DUP_HEADER (2026-05-30): verb 어미 변형 + 조사 변형으로 word overlap < 0.6 fail하는 head-noun 반복 catch
  //   conservative:
  //     - 양쪽 양 단어 길이 ≥ 2
  //     - 첫 단어 stem-equivalent (prefix ≥ 2 char + 양쪽 ≥ 2 char) — 흔한 첫 단어 false positive 차단 위해 길이 ≥ 3 강제
  //     - 또는: 첫 단어 stem-equivalent + 두번째 단어도 stem-equivalent (≥ 2 prefix)
  const _stemPrefix = (a, b) => {
    if (!a || !b) return 0;
    const minLen = Math.min(a.length, b.length);
    let prefix = 0;
    while (prefix < minLen && a[prefix] === b[prefix]) prefix++;
    return prefix;
  };
  const _hasLeadingHeadMatch = (() => {
    if (introWords.length < 1 || postWords.length < 1) return false;
    const i0 = introWords[0], p0 = postWords[0];
    if (!i0 || !p0 || i0.length < 2 || p0.length < 2) return false;
    const headPrefix = _stemPrefix(i0, p0);
    // 첫 단어가 길이 ≥ 3 stem-equivalent (head noun 자체)
    if (headPrefix >= 3) {
      // 두 단어만 있는 짧은 문장끼리는 head noun 일치만으로 duplicate 판정 (예: 명사+조사+verb)
      if (introWords.length <= 2 || postWords.length <= 2) return true;
      // 긴 문장의 경우: 두번째 단어도 stem-equivalent (≥ 2 prefix) 강제 → 길이가 길수록 추가 신호 요구
      const i1 = introWords[1], p1 = postWords[1];
      if (i1 && p1 && i1.length >= 2 && p1.length >= 2 && _stemPrefix(i1, p1) >= 2) return true;
      return false;
    }
    // 첫 단어 prefix 2 char (예: "코치"+"목록" 패턴) + 두번째 단어 stem ≥ 2 → bigram 일치
    if (headPrefix >= 2 && i0.length === headPrefix && p0.length === headPrefix) {
      // 첫 단어 자체가 짧고(=2글자) 완전 일치 → 두번째 단어 stem 일치 강제
      const i1 = introWords[1], p1 = postWords[1];
      if (i1 && p1 && i1.length >= 2 && p1.length >= 2 && _stemPrefix(i1, p1) >= 2) return true;
    }
    return false;
  })();

  // [DUP_HEADER] post-subset-of-intro: post의 모든 의미 단어가 intro에 stem-동등 매칭 + post 짧음 (≤ 4 단어)
  //   예: intro "코치 목록을 확인했어요. 총 명입니다" / post "코치 명입니다" — post의 '코치' + '명입니다' 모두 intro에 존재
  //   → 새로운 정보 없는 순수 restatement → duplicate
  //   ROOT_CAUSE_10B_DUP_HEADER (2026-05-30): leading head match가 catch 못하는 head+count 분리 restatement catch
  //   conservative: post ≤ 4 단어 + post 단어 ≥ 2 + 모든 post 단어가 intro 어딘가에 stem-동등 매칭
  const _isPostSubsetOfIntro = (() => {
    if (postWords.length < 1 || postWords.length > 4) return false;
    if (introWords.length < 1) return false;
    return postWords.every(pw => introWords.some(iw => _wordsMatch(iw, pw) || _stemPrefix(iw, pw) >= 2 && Math.min(iw.length, pw.length) >= 2));
  })();

  return isSubstringMatch || overlap >= 0.6 || _hasLeadingHeadMatch || _isPostSubsetOfIntro;
}

// [Cat8-REPEAT count-contradiction] L9a-MEMBER-005 cycle 3 — structural safety net
// finish text에서 displayCount와 모순되는 count(N명/N건/N개/N회) 포함 sentence drop
// - 조건: displayCount 알려져 있음 + finish sentence가 N+(명|건|개|회|마리|장|곳) 패턴 + N ≠ displayCount + N > displayCount (overshoot — 사용자 표 cap 무시한 하루치 false claim)
// - 보수적: N == displayCount인 sentence는 유지. drop은 explicit 모순(displayCount 5인데 285명/총 N건 류) 한정.
// - 다국어 호환: 숫자+classifier 패턴만 사용 (한국어 동사/어미 의존 X). classifier는 noun(명/건/개/회/마리/장/곳)만.
// - 사용처: nodeAgent.mjs + AgentSession.js — isIntroDuplicate가 missing한 케이스 safety net.
//   (L9a-MEMBER-005 Run 3: intro "상위 5명만 보여드릴게요. 총 5명입니다" / finish "상위 5명만 보여드렸어요. 표에 이름,..." — 둘 다 5명이지만 verb 어미가 달라 isIntroDuplicate=false였음.
//    하지만 finish에 "285명" 같은 explicit overshoot가 들어오는 케이스는 본 guard가 잡음.)
export function stripCountContradictionSentences(text, displayCount) {
  if (!text || typeof text !== 'string') return text;
  if (typeof displayCount !== 'number' || !Number.isFinite(displayCount) || displayCount <= 0) return text;

  // 숫자 + classifier 패턴 — 콤마 포함 (천단위 콤마 "1,200" 매칭)
  const COUNT_RE = /(\d{1,3}(?:,\d{3})*|\d+)\s*(명|건|개|회|마리|장|곳)/g;

  // FOLLOWUP_OUTRO_DECIMAL_SPLIT: digit-flanked period sentence delim 오인 차단
  const finishSentences = text.split(/(?<=\n|(?<!\d)[.!?](?!\d))\s*/);
  const filtered = finishSentences.filter(s => {
    if (!s || s.length < 4) return true;

    // sentence 내 모든 count 추출
    const matches = [...s.matchAll(COUNT_RE)];
    if (matches.length === 0) return true;

    // 각 count parse — comma strip 후 number
    const counts = matches.map(m => parseInt(m[1].replace(/,/g, ''), 10)).filter(n => Number.isFinite(n));
    if (counts.length === 0) return true;

    // 모순 검출 — count 중 displayCount보다 명확히 큰 값 (≥ displayCount * 2 또는 displayCount > 5인 경우 단순 mismatch)
    // 보수: displayCount=5, count=10 같은 borderline은 drop X (정당 비교일 수도). displayCount * 2 이상 overshoot만 drop.
    // 단 displayCount < 10인 경우는 count > displayCount면 drop (작은 cap은 사용자가 명확히 인지)
    const hasOvershoot = counts.some(c => {
      if (c === displayCount) return false;
      if (displayCount < 10) return c > displayCount;  // 작은 cap (5명만, 3건만 등) 명확
      return c >= displayCount * 2;  // 큰 cap은 2배 이상 차이만 모순으로 간주
    });

    return !hasOvershoot;
  });

  return filtered.join('');
}

// [L9c-COMPARE-006 cycle 1] PERIOD COUNT MISMATCH — multi-period merge 상태에서 intro/outro 내 "{periodLabel} ... N{classifier}" mismatch 차단
// 본질: PERIOD COUNTS HINT는 outro에 per-period count 명시 강제하지만, LLM이 intro에 잘못된 single count phrase
// (예: 1월 데이터는 29건인데 "1월 거래는 39건" — 39=outer merge total) 넣을 때 차단 없음. self-contradiction
// (intro 39 vs outro 29 같은 period) 사용자 입장에서 명백한 혼란.
//
// 룰 (generic — language-agnostic 숫자+classifier 패턴):
// - perPeriod = [{ periodLabel: "1월", count: 29 }, ...] (merge_slots에서 set한 _periodCountHintMeta.perPeriod)
// - text의 각 sentence에 대해, 각 period의 (periodLabel) 등장 시 그 sentence 내 (N)(classifier) 매칭
// - matched N ≠ expected count + matched N == 다른 period의 expected count 또는 == sum(all periods) 또는 outer merge total
//   → 명백한 mis-association → sentence drop
// - matched N ≠ expected이고 explanation 모호하면 보존 (false positive 회피)
//
// 사용처: AgentSession.executeShowDisplay + nodeAgent.executeShowDisplay (dual sync) — show_display intro/outro 인자 sanitize.
// 또한 nodeAgent finish/stop path 합성 후도 same (intro+table+outro 합성 시점).
export function stripPeriodCountMismatchSentences(text, perPeriod) {
  if (!text || typeof text !== 'string') return text;
  if (!Array.isArray(perPeriod) || perPeriod.length < 2) return text;

  // expected count map: periodLabel -> count
  const expectedByLabel = new Map();
  const expectedCountSet = new Set();
  for (const p of perPeriod) {
    if (!p?.periodLabel || typeof p.count !== 'number' || !Number.isFinite(p.count)) continue;
    expectedByLabel.set(p.periodLabel, p.count);
    expectedCountSet.add(p.count);
  }
  if (expectedByLabel.size < 2) return text;

  // 숫자+classifier 패턴 (콤마 포함)
  const COUNT_RE = /(\d{1,3}(?:,\d{3})*|\d+)\s*(건|명|개|회|마리|장|곳)/g;

  // sentence 분리 — 마침표/물음표/느낌표/개행 기준
  const sentences = text.split(/(?<=[.!?\n])\s*/);

  const filtered = sentences.filter(sentence => {
    if (!sentence || sentence.length < 4) return true;

    // 이 sentence에 등장하는 period 검출
    const mentionedPeriods = [];
    for (const [label, expectedCount] of expectedByLabel.entries()) {
      if (sentence.includes(label)) {
        mentionedPeriods.push({ label, expectedCount });
      }
    }

    if (mentionedPeriods.length === 0) return true;  // period 미등장 sentence는 무관

    // sentence 내 모든 count 추출
    const matches = [...sentence.matchAll(COUNT_RE)];
    if (matches.length === 0) return true;  // count phrase 없음 → 무관

    // mismatch 검출:
    //   single period mention + single count match + count ≠ expected (그 period의) + count == 다른 expected 또는 모순 명백
    const totalExpected = [...expectedCountSet].reduce((a, b) => a + b, 0);
    const maxExpected = Math.max(...expectedCountSet);

    if (mentionedPeriods.length === 1 && matches.length === 1) {
      const { expectedCount } = mentionedPeriods[0];
      const foundCount = parseInt(matches[0][1].replace(/,/g, ''), 10);
      if (!Number.isFinite(foundCount)) return true;

      if (foundCount === expectedCount) return true;  // 정합

      // foundCount가 expected set의 다른 값 → 잘못된 매칭 (mis-association)
      if (expectedCountSet.has(foundCount)) return false;

      // foundCount === sum of all expected (총합을 single period에 매칭) → 명백한 잘못
      if (foundCount === totalExpected) return false;

      // foundCount > all expected (overshoot) — outer merge total 추정 (각 expected의 합보다 작아도 union이면 가능)
      // 보수: foundCount > max(expected) * 2 시 drop. 그 외는 보존 (false positive 회피)
      if (foundCount >= maxExpected * 2) return false;

      // 모호 → 보존
      return true;
    }

    // multi-period mention + multi-count — period-count pair 별 검증
    //   "1월은 39건, 2월은 39건" 같이 둘 다 같은 잘못된 값 mismatch도 catch
    //   "1월은 29건, 2월은 16건" 같은 정합 case는 보존
    //   "3월 거래 62건, 4월 거래 62건" 같이 두 period 모두 outer-merge total로 잘못 매칭된 case catch
    if (mentionedPeriods.length >= 2 && matches.length >= 2) {
      // sentence를 period mention 기준으로 split하지 않고, 각 period-count 인접 쌍 분석
      //   pattern: "{label1} ... {count1}{cls} ... {label2} ... {count2}{cls}"
      //   각 label 위치에서 가장 가까운 count(label 이후 첫 count) 매칭
      const labelPositions = mentionedPeriods.map(({ label, expectedCount }) => ({
        label,
        expectedCount,
        position: sentence.indexOf(label),
      })).sort((a, b) => a.position - b.position);

      const matchPositions = matches.map(m => ({
        index: m.index,
        count: parseInt(m[1].replace(/,/g, ''), 10),
        text: m[0],
      })).filter(m => Number.isFinite(m.count));

      // 각 label에 가장 가까운 (label 이후, 다음 label 전) count 매칭
      let mismatchCount = 0;
      let matchCount = 0;
      for (let i = 0; i < labelPositions.length; i++) {
        const lp = labelPositions[i];
        const nextLabelPos = i + 1 < labelPositions.length ? labelPositions[i + 1].position : Infinity;
        const nearestMatch = matchPositions.find(m => m.index > lp.position && m.index < nextLabelPos);
        if (!nearestMatch) continue;

        if (nearestMatch.count === lp.expectedCount) {
          matchCount++;
        }
        else {
          // mismatch — 잘못된 count 매칭. drop 조건:
          //   (a) count가 다른 period의 expected와 매칭 (mis-association)
          //   (b) count === sum of all expected
          //   (c) count > max(expected) * 2 (overshoot)
          //   (d) count가 multi-period에서 같은 값 반복 (둘 다 잘못된 동일 값) — 자기모순 패턴
          if (expectedCountSet.has(nearestMatch.count)) mismatchCount++;
          else if (nearestMatch.count === totalExpected) mismatchCount++;
          else if (nearestMatch.count >= maxExpected * 2) mismatchCount++;
        }
      }

      // mismatchCount >= 1 + 정합 match가 모든 mention을 cover하지 못함 → drop
      // 보수: matchCount >= mentionedPeriods.length / 2 (대다수 정합)이면 보존
      if (mismatchCount > 0 && matchCount < mentionedPeriods.length / 2) {
        return false;
      }

      // 추가: 모든 mention이 동일한 잘못된 count로 표현 (예: "3월 62건, 4월 62건") → 자기모순 명백
      //   각 period가 각자의 expected와 다른데 모두 동일 값이면 잘못된 일괄 매칭
      const allFoundCounts = labelPositions
        .map((lp, i) => {
          const nextLabelPos = i + 1 < labelPositions.length ? labelPositions[i + 1].position : Infinity;
          const nm = matchPositions.find(m => m.index > lp.position && m.index < nextLabelPos);
          return nm ? { count: nm.count, expected: lp.expectedCount } : null;
        })
        .filter(Boolean);

      if (allFoundCounts.length === mentionedPeriods.length) {
        const allMismatch = allFoundCounts.every(({ count, expected }) => count !== expected);
        const distinctCounts = new Set(allFoundCounts.map(c => c.count));
        if (allMismatch && distinctCounts.size === 1) {
          // 모든 period에 동일한 잘못된 count → 일괄 mis-attribution
          return false;
        }
      }
    }

    return true;
  });

  return filtered.join('');
}

// [L9c-COMPARE-006 F13 Issue C cycle 1] PERIOD COUNTS INTRO ENFORCEMENT — multi-period merge 상태에서 intro에 per-period count 누락 시 결정적 inject (dual sync with nodeAgent.mjs + AgentSession.js)
// 본질: PERIOD COUNTS HINT prompt가 outro 강제 + (F13 fix로) intro 강제 추가됐지만, LLM stochastic으로 여전히 intro에 per-period count 누락 case 존재.
//   - 누락 case 1: intro에 "총 N건" 같은 single global count만 (outer merge total, per-period 분리 안 함)
//   - 누락 case 2: intro에 count 자체 없음 ("각 기간별 결제 금액을 정리했어")
//   - 누락 case 3: intro에 일부 period count만 (예: 3 period 중 2개만)
// 룰 (generic — language-agnostic 패턴):
//   - perPeriod = [{ periodLabel, count }, ...] (merge_slots에서 set한 _periodCountHintMeta.perPeriod)
//   - 각 perPeriod entry에 대해 "{periodLabel}" 등장 + 그 근처 {count} 매칭 검사
//   - 모든 period가 intro에 count와 함께 매칭되지 않으면 → canonical 문장 ("1월은 N건, 2월은 M건, 3월은 K건이야") inject
//   - 보수: intro 이미 정합 (모든 period count 명시) → no-op
//   - 단위 classifier: 한국어 "건"이 default (sales/comparison context — items per period). LLM이 다른 classifier 사용 시 매칭 X — 보수적으로 inject (false-negative 회피).
//
// 사용처: AgentSession.executeShowDisplay + nodeAgent.executeShowDisplay (dual sync) — intro/outro strip pipeline 직후.
export function ensurePeriodCountsInIntro(intro, perPeriod) {
  if (typeof intro !== 'string') intro = intro || '';
  if (!Array.isArray(perPeriod) || perPeriod.length < 2) return intro;

  const validEntries = perPeriod.filter(p =>
    p?.periodLabel && typeof p.count === 'number' && Number.isFinite(p.count) && p.count >= 0
  );
  if (validEntries.length < 2) return intro;

  // [SAFETY] 보수적 scope — false positive 회피:
  //   (a) periodLabel 깨끗한 calendar 형식만 (예: "1월", "2026년") inject. raw date (2026-04-02~2026-05-02) → skip
  //       이유: rolling window/필터 결과 표시 시 raw date label은 사용자 입장 awkward + 의미 mismatch
  //   (b) 모든 period의 count가 동일 → skip (per-period 의 정보 가치 없음, 사용자에게 mis-leading)
  //       이유: 동일 count는 보통 "active roster total" — 비교 의미 없는 메타 정보
  const CLEAN_LABEL_RE = /^(?:\d{1,2}월|\d{4}년)$/;
  const allCleanLabels = validEntries.every(({ periodLabel }) => CLEAN_LABEL_RE.test(periodLabel));
  if (!allCleanLabels) return intro;
  const countSet = new Set(validEntries.map(e => e.count));
  if (countSet.size < 2) return intro;  // 모든 count 동일 시 skip

  // 각 period가 intro에 (label + 인접 count) 매칭되는지 검사
  // 인접 = label 등장 위치 ± 30 chars 내에 그 period의 expected count가 숫자로 등장
  const PROXIMITY = 40;
  const allPresent = validEntries.every(({ periodLabel, count }) => {
    const labelIdx = intro.indexOf(periodLabel);
    if (labelIdx < 0) return false;
    const windowStart = Math.max(0, labelIdx - PROXIMITY);
    const windowEnd = Math.min(intro.length, labelIdx + periodLabel.length + PROXIMITY);
    const window = intro.slice(windowStart, windowEnd);
    // 숫자 패턴 (콤마 포함, 정수만)
    const countStr = String(count);
    const countWithComma = count.toLocaleString('en-US');
    // boundary: 앞뒤 digit이 아닌 환경
    const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const countRe = new RegExp(`(?<![\\d.,])(${escapeRe(countStr)}|${escapeRe(countWithComma)})(?![\\d])`);
    return countRe.test(window);
  });

  if (allPresent) return intro;  // 이미 정합

  // canonical 문장 생성 — "1월은 N건, 2월은 M건, 3월은 K건이야"
  // 한국어 종결자 — intro 마지막 종결자 감지 후 동조 (보수: default "이야")
  const sentenceParts = validEntries.map(({ periodLabel, count }) =>
    `${periodLabel}은 ${count.toLocaleString('en-US')}건`
  );
  const canonical = `${sentenceParts.join(', ')}이야.`;

  // intro 빈 경우 → canonical만
  if (!intro || intro.trim().length === 0) return canonical;

  // intro 비어있지 않으면 → 끝에 공백 + canonical append (마침표 보강)
  const trimmed = intro.trim();
  const needsPeriod = !/[.!?\n]$/.test(trimmed);
  return `${trimmed}${needsPeriod ? '.' : ''} ${canonical}`;
}

// [L3-COMPUTE-GROUP-002] grouped buffer headline unit normalization (dual sync with nodeAgent.mjs + AgentSession.js)
// 본질: compute(operation='group') 후 buffer.items는 group rows ({groupField, _count}).
//   사용자 input "X별 분포/구성/비율" 의 headline은 group 수 (예: "회원권 12종류") — entity 수 (93명) 아님.
//   LLM은 stochastic하게 unit을 "명/건/회" 등 entity classifier로 선택 ("총 12명이야" / "총 93명이야") → 의미 mismatch.
// 룰 (generic — case ID/도메인 keyword 없음):
//   - 신호: buffer._aggregateMetaFields 에 '_count' 포함 (compute group 적용 신호)
//   - text 내 패턴: `<digit>(명|건|회|마리|장|곳|원|분|시간|팀|곡)` 에서 digit이 group count (= buffer.items.length) 와 일치
//   - 일치 시 classifier 를 '종류' 로 교체 ("총 12명이야" → "총 12종류야")
//   - 보수: '개/가지' classifier 는 group context 에서 정합이므로 미변경. '개' 는 entity 단위로도 흔하지만 회귀 risk 최소화 위해 보존.
//   - boundary: digit 앞뒤 숫자가 있으면 매칭 X (false positive 차단). digit 매칭은 group count 또는 group count 의 부분 매칭이 아닌 정확 일치만.
//   - 다국어 호환: 한국어 classifier 만 처리. 영문/일본어는 LLM 자율.
//   - 종결자 particle 정합: 한국어 종결자 "이야/이에요/이었/입니다/이다/이고" 는 받침 있는 noun 뒤에만 정합 ("명이야"/"건이야") .
//     "종류" (받침 X — 류 = 'ㅠ' vowel) 뒤에서 "이야" → "야" 로 strip 필요. 종결자 화이트리스트 한정 보수.
export function normalizeGroupedUnits(text, buffer) {
  if (!text || typeof text !== 'string') return text;
  if (!buffer || !Array.isArray(buffer._aggregateMetaFields) || !buffer._aggregateMetaFields.includes('_count')) {
    return text;
  }
  if (!Array.isArray(buffer.items) || buffer.items.length === 0) return text;

  const groupCount = buffer.items.length;
  // entity classifier set — '종류' 로 교체할 대상. '개/가지' 는 group 의미와 양립 가능하여 제외.
  // 종결자 정합: classifier 다음 "이야/이에요/이었/입니다/이다/이고" 가 오면 함께 매칭 → "야/예요/였/입니다/다/고" 로 변환 (종류는 받침 X)
  //   "이다/입니다" 같은 동일 종결자 변형은 그대로 유지하되 leading "이" 제거.
  const ENTITY_CLASSIFIER_RE = new RegExp(
    `(?<![\\d.])${groupCount}(\\s*)(명|건|회|마리|장|곳|원|분|시간|팀|곡)(이야|이에요|이었|입니다|이다|이고|이며|이라|이라서|이라고|이지|이지요|이죠|입니까)?(?![\\d])`,
    'g',
  );
  return text.replace(ENTITY_CLASSIFIER_RE, (match, sp, classifier, trailing) => {
    if (!trailing) return `${groupCount}${sp}종류`;
    // 받침 없는 noun "종류" 뒤에서 "이X" 종결자의 leading "이" strip
    const dropI = trailing.startsWith('이') ? trailing.slice(1) : trailing;
    return `${groupCount}${sp}종류${dropI}`;
  });
}

// [Cat8-REPEAT] finish response 내 중복 문장 strip — 동일 문장 2회 이상 반복 시 첫 등장만 유지 (dual sync with nodeAgent.mjs) //
// [Group B compute/clarification dedup] 2026-05-30: decimal-protected split + 표(`[헤더 |`) 영역 보존 + 연속(adjacent) dup 추가 검출
//   본질: L17-DEEP-5TURN-001 T4 compute / L9a-SCHEDULE-005 T2 clarification path에서 LLM이 동일 문장 줄바꿈 후 반복 emit하는 stochastic 패턴.
//   기존 set-based dedup만으로는 universal final-pass에 안전하지 않음 (decimal split 위험 → 4.3 같은 값 잘림). //
export function stripDuplicateSentences(text) {
  if (!text) return text;

  // [URL_LINEBREAK_GUIDE F5 fix 2026-05-31] URL token 보호 — 본 함수의 sentence split regex가
  //   URL 내부 `.`(예: linko.my, link-zone.io)를 sentence delimiter로 오인 → join('\n') 시 URL 깨짐.
  //   범용: protocol-URL (https?://...) 또는 bare-domain (xxx.tld) 식별 후 placeholder 치환 → split → restore.
  //   case ID/도메인 hardcode X. 모든 finalText에 동일 적용. (dual sync via nodeAgent.mjs + useAiChat.js final-pass).
  // pattern: protocol URL — `https?://` 시작, whitespace/한국어/일반 punctuation까지
  //          bare domain — `[a-z0-9][a-z0-9-]+\.[a-z]{2,}(?:/...)?` (소문자 도메인 — 한글 문장 끝 false-positive 차단)
  // protocol URL 우선 매칭, 그다음 bare domain. 매칭 토큰을 -prefixed placeholder로 치환 (Unicode private use area — 사용자 입력 충돌 없음).
  const _urlTokens = [];
  const _URL_PLACEHOLDER_PREFIX = 'URL';
  const _protectUrls = (s) => {
    if (!s) return s;
    let out = s;
    // (0) pre-join — LLM 또는 이전 strip 단계에서 이미 URL 내부에 \n이 삽입된 경우 복구
    //     pattern: https?://chars 직후 \n + alphanumeric chars 연속 (URL continuation)
    //     반복 적용 — 여러 \n 분할도 커버.
    let _prev;
    do {
      _prev = out;
      out = out.replace(/(https?:\/\/[^\s<>()[\]{}'"。、，！？]*)\n([a-zA-Z0-9][^\s<>()[\]{}'"。、，！？]*)/g, '$1$2');
    } while (out !== _prev);
    // bare domain 분할 복구 (`linko.\nmy` 형태) — TLD 허용 목록 한정으로 false-positive 차단
    do {
      _prev = out;
      out = out.replace(/([a-z0-9][a-z0-9-]{1,62})\.\n(my|io|com|net|org|kr|co|app|dev|ai|me)(\b|\/[^\s<>()[\]{}'"。、，！？]*)/g, '$1.$2$3');
    } while (out !== _prev);

    // (1) protocol URL — https?://chars 까지 (whitespace/Korean/comma/괄호 정지)
    out = out.replace(/https?:\/\/[^\s<>()[\]{}'"。、，！？]+/g, (m) => {
      const idx = _urlTokens.length;
      _urlTokens.push(m);
      return `${_URL_PLACEHOLDER_PREFIX}${idx}`;
    });
    // (2) bare domain — TLD whitelist (false-positive 차단: 일반 한국어 단어 끝 점/영문 약어 제외)
    //     소문자 영문 + 길이 ≥ 2 + 끝 영문/숫자 only. TLD는 자주 등장하는 것 enumerate (확장 가능).
    out = out.replace(/\b([a-z0-9][a-z0-9-]{1,62})\.(my|io|com|net|org|kr|co|app|dev|ai|me)(\b|\/[^\s<>()[\]{}'"。、，！？]*)/g, (m) => {
      const idx = _urlTokens.length;
      _urlTokens.push(m);
      return `${_URL_PLACEHOLDER_PREFIX}${idx}`;
    });
    return out;
  };
  const _restoreUrls = (s) => {
    if (!s) return s;
    return s.replace(/URL(\d+)/g, (_, idx) => _urlTokens[parseInt(idx, 10)] ?? '');
  };

  text = _protectUrls(text);

  // 표/리스트 영역(첫 `[헤더 | ...]` 블록 이후)은 dedup 대상 제외 — table row 반복 보존, intro/outro 영역만 처리 //
  // 표 마커 인덱스: 첫 `\n[xxx | yyy ...]` 패턴(컬럼 헤더). 없으면 전체 적용.
  const tableHeaderIdx = text.search(/\n\[[^\]\n]*\|[^\]\n]*\]/);
  const head = tableHeaderIdx >= 0 ? text.slice(0, tableHeaderIdx) : text;
  const tail = tableHeaderIdx >= 0 ? text.slice(tableHeaderIdx) : '';

  // [L21-COMPARE-BASIC-002 cycle 1] post-table outro 영역 cross-region dedup
  //   본질: PERIOD_COUNTS_MISSING retry로 LLM이 outro 위치에 intro와 동일한 문장 재emit 시 universal final-pass의 head-only dedup이 outro 영역 미커버 → 사용자 가시 중복 노출 (예: 표 위 "지난달보다 출석 47명이야" + 표 아래 "조건에 맞는 회원 정보를 다시 정리할게! 지난달보다 출석 47명이야.")
  //   해소: tail을 table body(numbered rows)와 outro 영역으로 분리 → outro 영역만 head sentences와 cross-check + 자체 dedup.
  //   범용: case ID/한국어 keyword 의존 X. table body는 `N. ...` 패턴(line start digit+dot+space)으로 식별. outro 영역은 마지막 numbered row 이후 잔존 텍스트.
  //   dual coverage: 본 final-pass + line 10617 outro-only dedup의 안전망.
  let tailHead = '';
  let tailBody = tail;
  let tailOutro = '';
  if (tail) {
    // table body 끝 위치 검출 — 마지막 `\nN. ` (1+ digit + dot + space) 라인의 끝 (다음 newline 또는 string 끝)
    const rowMatches = [...tail.matchAll(/\n\s*\d+\.\s/g)];
    if (rowMatches.length > 0) {
      const lastRowMatch = rowMatches[rowMatches.length - 1];
      const lastRowStartIdx = lastRowMatch.index;
      // 마지막 row의 line 끝 (다음 newline 또는 EOS)
      const afterLastRowIdx = tail.indexOf('\n', lastRowStartIdx + 1);
      if (afterLastRowIdx === -1) {
        // outro 없음 — tail 전체가 table
        tailBody = tail;
        tailOutro = '';
      } else {
        tailBody = tail.slice(0, afterLastRowIdx);
        tailOutro = tail.slice(afterLastRowIdx);
      }
      // tailHead = table header line (intact)
    }
  }

  // decimal 보호 split: `.`, `!`, `?` (digit-flanking 제외) 또는 newline. //
  // cedd69f8 패턴과 일관 — "4.3", "31.9세" 같은 값 보호.
  // [L11-SMS-FULL-001 cycle 2] whitespace-less concat 대응 — stripCountContradictionSentences가 `filtered.join('')`로
  //   문장 사이 whitespace 손실 시키는 경우(예: "...어요!...어요.") 대비해 \s+ optional로 변경.
  //   digit-flanking 보호는 그대로 유지 — "4.3" 같은 값 split 차단.
  const sentences = head.split(/(?<=(?<!\d)[.!?](?!\d))\s*|\n+/).filter(s => s.trim().length > 0);

  // [L21-COMPARE-BASIC-002 cycle 1] head sentences가 1개 이하여도 outro cross-dedup은 필요 (intro 1줄 + 표 + outro 중복 케이스)
  // [URL_LINEBREAK_GUIDE F5 fix] early-return path도 URL placeholder restore (split 안 거쳐도 token 치환된 상태)
  if (sentences.length <= 1 && !tailOutro) return _restoreUrls(text);

  const seen = new Set();
  const result = [];
  let prevNormalized = null;

  // [L11-SMS-FULL-001 cycle 2] punctuation-insensitive comparison key
  //   본질: LLM이 동일 문장을 "...어요!\n...어요." 같이 종결 punctuation만 다르게 stochastic emit (!/./?/등)
  //   해소: 비교 시 trailing punctuation 제거된 key 사용 (preserve original on output)
  const _normalizeForCompare = (s) => s.trim().replace(/\s+/g, ' ').replace(/[!?.。]+\s*$/g, '').toLowerCase();

  for (const sentence of sentences) {
    const normalized = sentence.trim().replace(/\s+/g, ' ');
    const compareKey = _normalizeForCompare(sentence);

    if (normalized.length < 8) {
      result.push(sentence);
      prevNormalized = compareKey;
      continue;
    }

    // 연속(adjacent) duplicate — 직전 sentence와 동일하면 drop (case-insensitive + punctuation-insensitive)
    if (prevNormalized && compareKey === prevNormalized) {
      continue;
    }

    // 비연속 duplicate (전역 set) — 동일 문장이 이전에 등장했으면 drop
    if (seen.has(compareKey)) {
      continue;
    }

    seen.add(compareKey);
    result.push(sentence);
    prevNormalized = compareKey;
  }

  // [L21-COMPARE-BASIC-002 cycle 1] outro 영역 cross-region dedup —
  //   head의 seen set 재사용해 outro에서 동일 문장 strip. outro 자체도 adjacent/global dup 처리.
  let dedupedOutro = tailOutro;
  if (tailOutro && tailOutro.trim().length > 0) {
    const outroSentences = tailOutro.split(/(?<=(?<!\d)[.!?](?!\d))\s*|\n+/).filter(s => s.trim().length > 0);
    if (outroSentences.length > 0) {
      const outroResult = [];
      let outroPrev = prevNormalized;
      for (const sentence of outroSentences) {
        const normalized = sentence.trim().replace(/\s+/g, ' ');
        const compareKey = _normalizeForCompare(sentence);
        if (normalized.length < 8) {
          outroResult.push(sentence);
          outroPrev = compareKey;
          continue;
        }
        // 연속 dup (head 마지막 또는 outro 직전 sentence와 동일)
        if (outroPrev && compareKey === outroPrev) continue;
        // 전역 dup (head의 seen 또는 이전 outro sentences)
        if (seen.has(compareKey)) continue;
        seen.add(compareKey);
        outroResult.push(sentence);
        outroPrev = compareKey;
      }
      // outro 영역의 leading newline 보존 — table body 뒤 \n\n 구분자 형태로
      const outroLeadingNewlines = tailOutro.match(/^\n+/)?.[0] || '\n\n';
      dedupedOutro = outroResult.length > 0
        ? outroLeadingNewlines + outroResult.join('\n')
        : '';
    }
  }

  // [URL_LINEBREAK_GUIDE F5 fix 2026-05-31] URL placeholder restore — split/join 단계에서 URL 토큰이 분리되지 않은 상태로 통과한 후 원래 텍스트로 복원
  return _restoreUrls(result.join('\n') + tailBody + dedupedOutro);
}

// [ROOT_CAUSE_7_META_LEAK] LLM이 OpenAI harness format (`response to=functions.X{...}` / `finish to=functions.X{...}`) 또는 corrupted token stream을 assistantMessage.content 안에 직접 출력하는 경우 finalText에서 strip (dual sync with nodeAgent.mjs + useAiChat.js) //
// 14차 baseline 본질:
//   - `response to=functions.finish 招商总代{"response":"...","hint":"..."}` (#797, #798)
//   - `finish to=functions.finish 招商总代{"response":"..."}` (#056)
//   - `to=functions.finish 招商总代 to=functions.finish ...` (반복 corrupted, #729)
//   - 비라틴 spam tokens (招商总代/াধ্যম/േണ്ട/中央値との差) 동반
// 본 함수는 language-independent regex strip — case ID literal 없음, 한국어 keyword 의존 X //
// 적용 전략:
//   1) `(response|finish)?\s*to=functions\.\w+` 패턴 발견 시, 그 위치부터 string 끝까지 잘라냄 (corrupted tail).
//      이후 자연어 응답이 다시 나올 가능성은 매우 낮음(harness leak는 항상 tail). intro/table 영역은 보존.
//   2) trailing `,"hint":"<...>"}` JSON close 토큰만 leak된 경우 단독 strip.
//   3) bare JSON `{"response":"...","hint":"..."}` 만 남은 경우 inner response 추출 시도.
export function stripAssistantHarnessLeak(text) {
  if (!text || typeof text !== 'string') return text;

  let result = text;

  // (1) bare JSON shell — text 전체가 `{"response":"<core>","hint":"<...>"}` 형태인 경우 core 추출
  //   - 가장 먼저 처리: 다른 패턴이 leak 토큰을 cut하기 전에 whole-JSON 감지
  //   - LLM이 tool_calls 대신 content에 JSON 통째로 emit한 케이스
  const bareJsonMatch = result.match(/^\s*\{\s*"response"\s*:\s*"((?:[^"\\]|\\.)*)"(?:\s*,\s*"hint"\s*:\s*"(?:[^"\\]|\\.)*"\s*)?\}\s*$/);
  if (bareJsonMatch) {
    try {
      const core = JSON.parse(`"${bareJsonMatch[1]}"`);
      if (typeof core === 'string' && core.length > 0) {
        return core.replace(/[\s\n]+$/, '');
      }
    } catch (_e) { /* keep result as-is on parse failure */ }
  }

  // (2) `(response|finish)? to=functions.X ...` — leak 위치부터 끝까지 strip
  //   - 다중 발생 / 비라틴 spam / hint JSON 모두 tail에 위치 → 단일 cut으로 처리
  //   - 단어 boundary 확보 위해 `\b` 사용. 시작 prefix 단어(response|finish)는 optional, 공백 흡수
  const leakIdx = result.search(/(?:\b(?:response|finish)\s+)?to\s*=\s*functions\.[a-zA-Z_]\w*/);
  if (leakIdx !== -1) {
    result = result.slice(0, leakIdx).replace(/[\s\n]+$/, '');
  }

  // (3) 단독 trailing JSON tail — `","hint":"<...>"}` 또는 `,"hint":"<...>"}` 처럼 끝에 매달린 hint 잔존
  //   - 일반 prose에서 `,"hint":` literal이 등장할 가능성은 거의 0 (JSON token이라 quote pattern strict)
  result = result.replace(/[,\s]*"hint"\s*:\s*"[^"]*"\s*\}?\s*$/, '');

  // trailing whitespace cleanup
  return result.replace(/[\s\n]+$/, '');
}

// SUMMARY-OVERRIDE 비교 표(items.length===2 + _change_*)일 때, LLM finish outro에 변화량 자연어 부재 시 자동 보강 //
// - 사용자 질문 "얼마나 변했어?" 직접 응답 보장 (LLM 가이드 무시 fallback)
// - 검사 패턴: 변화량 숫자(+/- 원/% 등) 또는 키워드(변화량/증가/감소 등) 존재 시 보강 skip
// - 보강 순서: buffer.items[currItem]의 _change_* 키 순서 (백엔드 SUMMARY 응답 결정적, LLM columns 비결정 의존 폐기)
// - 최대 5개 (outro 과도한 길이 방지). 1회 한정 자동 적용 — finish는 turn당 1회 호출이라 중복 risk 없음
export function augmentOutroWithChange(responseText, buffer) {
  if (!responseText || !buffer?.items || buffer.items.length !== 2) return responseText;
  // 정렬에 의한 swap 영향 — items[0]/[1] 둘 다 검사해서 _change_* 보유한 row를 currItem으로 사용
  const hasChange = it => it && Object.keys(it).some(k => k.startsWith('_change_') && !k.startsWith('_changeRate_'));
  const currItem = hasChange(buffer.items[1]) ? buffer.items[1] : (hasChange(buffer.items[0]) ? buffer.items[0] : null);
  if (!currItem) return responseText;
  const prevItem = currItem === buffer.items[1] ? buffer.items[0] : buffer.items[1];
  const changeKeys = Object.keys(currItem).filter(k => k.startsWith('_change_') && !k.startsWith('_changeRate_'));
  if (changeKeys.length === 0) return responseText;
  // buffer items[currItem]의 _change_* 키 순서대로 보강 (백엔드 결정적). 최대 5개
  const targetBases = changeKeys.slice(0, 5).map(k => k.slice('_change_'.length));
  if (targetBases.length === 0) return responseText;
  // 정량 표현만 매칭 (정성 단독 키워드는 매칭 X) — Run 1 정성 단독 outro 보강 발동을 위해 정밀화
  // 1) 부호 있는 수치+단위 (`+5,080,000원`)  2) 수치+단위+증감동사 (`5,080,000원이 늘었습니다`)  3) %+증감 (`152.9% 증가`)
  const CHANGE_PATTERN = /[+\-]\s*[\d,]+\s*(?:원|만원|개|건|명|회|%)|[\d,]+\s*(?:원|만원|개|건|명|회)\s*[가이]?\s*(?:늘었|줄었|증가|감소|상승|하락|올랐|떨어졌)|[\d.]+\s*%\s*(?:증가|감소|상승|하락)/;

  // Fix 3/3a: LLM outro에 수치+단위 명시 sentence를 buffer 정답 셋과 산수 검증 → hallucination sentence strip
  // - 정답 셋 = _change_/_changeRate_ + buffer items 각 row의 base 컬럼 raw 값 (월별 수치 false positive 차단)
  // - sentence 단위 분리 (한국어 종결어/줄바꿈) → 수치+단위 포함 sentence 내 추출 수치 중 1개라도 정답 셋에 없으면 sentence 전체 strip
  // - 변화 sentence/raw 서술 sentence 둘 다 검증 (Fix 3a: CHANGE_VERB_RE 검증 제거 — 변화 동사 없이 raw 값 hallucinate 케이스도 잡음)
  // - strip 후 CHANGE_PATTERN 미매칭이면 기존 보강 흐름 자연 진입, 매칭(정확 sentence 잔존)이면 keep
  if (CHANGE_PATTERN.test(responseText)) {
    const validAbsSet = new Set();
    for (const k of changeKeys) {
      const v = currItem[k];
      if (typeof v === 'number') validAbsSet.add(Math.abs(v));
    }
    for (const k of Object.keys(currItem).filter(k => k.startsWith('_changeRate_'))) {
      const v = currItem[k];
      if (typeof v === 'number') validAbsSet.add(Math.abs(v));
    }
    for (const item of buffer.items) {
      for (const base of targetBases) {
        const v = item?.[base];
        if (typeof v === 'number') validAbsSet.add(Math.abs(v));
      }
    }
    const NUM_UNIT_RE = /([+\-]?\s*\d{1,3}(?:,\d{3})*|\d+(?:\.\d+)?)\s*(?:원|만원|개|건|명|회|%)/g;
    const sentences = responseText.split(/(?<=[.!?다요])\s+|\n+/);
    const keepSentences = [];
    let anyStripped = false;
    for (const sent of sentences) {
      const nums = [...sent.matchAll(NUM_UNIT_RE)].map(m => {
        const raw = m[1].replace(/[\s,+\-]/g, '');
        return raw ? Math.abs(parseFloat(raw)) : null;
      }).filter(n => n != null);
      if (nums.length === 0) { keepSentences.push(sent); continue; }
      const hasHallucination = nums.some(n => !validAbsSet.has(n));
      if (hasHallucination) { anyStripped = true; continue; }
      keepSentences.push(sent);
    }
    if (!anyStripped) return responseText;
    responseText = keepSentences.join(' ').replace(/\s+/g, ' ').trim();
    if (CHANGE_PATTERN.test(responseText)) return responseText;
    // else fall-through → 기존 보강 발동
  }

  const meta = buffer.availableFields || {};
  const prevPeriod = prevItem?.period || prevItem?._label || '이전';
  const currPeriod = currItem.period || currItem._label || '최근';
  const unitOf = (key) => /Price|Revenue|Amount/i.test(key) ? '원' : (/Rate$/i.test(key) ? '%p' : '');
  // outro 보강은 사용자 친화 자연어 — LABEL_OVERRIDES(한국어) 우선, 백엔드 description은 영문일 수 있어 fallback
  const labelOf = (key) => LABEL_OVERRIDES[key] || (typeof meta[key] === 'string' ? meta[key] : meta[key]?.label) || key;

  const phrases = [];
  for (const base of targetBases) {
    const changeVal = currItem[`_change_${base}`];
    if (typeof changeVal !== 'number') continue;
    const rateVal = currItem[`_changeRate_${base}`];
    const sign = changeVal >= 0 ? '+' : '';
    const direction = changeVal >= 0 ? '증가' : '감소';
    const unit = unitOf(base);
    const rateText = (typeof rateVal === 'number') ? `(${sign}${rateVal}%)` : '';
    phrases.push(`${labelOf(base)} ${sign}${changeVal.toLocaleString()}${unit}${rateText} ${direction}`);
  }
  if (phrases.length === 0) return responseText;
  return responseText + ` ${currPeriod}은 ${prevPeriod} 대비 ${phrases.join(', ')}했어요.`;
}

// substituteOutroPlaceholders / stripPlaceholderArtifacts 본체는 TL-1에서
// src/Agent/Tools/placeholderSubstitute.js로 이동. 파일 상단 import + re-export 유지.

// stripRawNumberArtifacts — clause-level drop of raw numeric patterns //
// - Last-resort cleanup after retry exhaustion: removes any clause containing comma/Korean-scale/% numbers
// - L4 "총 261명" patterns are unaffected (no comma, no Korean scale, no %)
export function stripRawNumberArtifacts(text) {
  if (!text || typeof text !== 'string') return text;
  const STRICT_NUM_RE = /[+\-−]?\s*\d+(?:\.\d+)?\s*%|[+\-−]?\s*\d{1,3}(?:,\d{3})+\s*(?:원|건|명|회|개)?|[+\-−]?\s*\d+(?:\.\d+)?\s*(?:만|억)\s*(?:원|건|명|회|개)?/;
  if (!STRICT_NUM_RE.test(text)) return text;
  // FOLLOWUP_OUTRO_DECIMAL_SPLIT: digit-flanked period/comma sentence delim 오인 차단 (decimal "41.3" / thousands "1,200" 보호)
  const segments = text.split(/(?<=\n|(?<!\d)[,.!?](?!\d))/);
  const filtered = segments.filter(s => !STRICT_NUM_RE.test(s));
  return filtered.join('')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .replace(/^[\s,.;]+/, '')
    .replace(/[\s,.;]+$/, '')
    .trim();
}

// stripColumnEnumLeak — resolvedColumnLabels 라벨 3+개 연속 나열 문장 제거 (결정성) //
// LLM이 "표에는 X, Y, Z가 들어가 있어" 식으로 컬럼명 직접 나열 시 해당 문장 제거
// {{TABLE_COLS}} placeholder 포함 문장은 보존 (코드 치환 대상)
export function stripColumnEnumLeak(text, buffer) {
  if (!text || typeof text !== 'string') return text;
  if (!buffer?.resolvedColumnLabels) return text;

  const labels = Object.values(buffer.resolvedColumnLabels)
    .filter(l => typeof l === 'string' && l.length >= 2 && !/^[a-zA-Z]/.test(l));

  if (labels.length < 3) return text;

  const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const sep = '(?:\\s*[,、·/]\\s*|\\s*(?:과|와|이나|하고|및)\\s*)';
  const enumRe = new RegExp(
    `(?:${labels.map(esc).join('|')})(?:${sep}(?:${labels.map(esc).join('|')})){2,}`
  );

  if (!enumRe.test(text)) return text;

  // FOLLOWUP_OUTRO_DECIMAL_SPLIT: digit-flanked period sentence delim 오인 차단
  const sentences = text.split(/(?<=\n|(?<!\d)[.!?](?!\d))/);
  const filtered = sentences.filter(s => {
    if (/\{\{TABLE_COLS\}\}/.test(s)) return true;
    return !enumRe.test(s);
  });

  return filtered.join('').replace(/\n{3,}/g, '\n\n').replace(/[ \t]+$/gm, '').trim();
}

// stripFalseChangeDirectionIntro — chain follow-up에서 LLM이 intro/outro에 사용자 input 부재 변화-방향 (줄어든/늘어난/감소한/증가한) 주장 시 strip //
//   [L9c-COMPARE-004 fix] 본질:
//     chain follow-up (예: 출석 줄어든 회원 → "출석률도 보여줘")에서 LLM이 prior turn framing
//     ("줄어든")을 보존하면서 현재 turn keyword ("출석률")로 subject 갈아치움 →
//     "출석률이 줄어든 회원 51명" (fact-false). 실제 buffer는 attendance-count-decreased,
//     attendance-rate는 100→100 unchanged.
//   해소:
//     1) buffer가 _change_* fields 보유 (multi-period merge 결과)
//     2) user input에 변화 방향 키워드 부재 (단순 "도 보여줘" 류)
//     3) intro/outro 문장에 "<word>(이|가) (줄어든|...|늘어난|...|감소한|증가한|올라간|떨어진|적어진|많아진|높아진|낮아진)" 패턴
//     4) 해당 문장 strip (전체 sentence 제거가 아닌 phrase 제거 — 나머지 의미 보존)
//   구조 기반 (case ID / field name hardcode X):
//     - buffer._change_* 존재 signal
//     - userInput change-keyword detection (재사용 _detectChangeDirection 호환 regex)
//     - intro phrase regex (다양한 활용형)
//   회귀 risk 최소화:
//     - user input에 변화 키워드 있으면 미발동 (정상 framing 보존)
//     - buffer에 _change_* 없으면 미발동 (single-slot 등 비교 outside)
//     - phrase 단위 strip — sentence 전체 제거 X (다른 정보 손실 회피)
// [L10-COL-LABEL-LEAK-OUTRO] outro 영역 column label echo strip (dual sync with nodeAgent.mjs + AgentSession.js) //
// 본질: detail/profile 류 응답에서 LLM이 표 row + outro에 동일 column 라벨/값 쌍을 재listing —
//   예) "[이름 | 성별 | 생년월일 | 전화번호 | 닉네임]" 표 + "1. 임지환 | 남 | ... | ImJihwan" row 뒤에
//       outro "이름: 임지환\n성별: 남성\n생년월일: 2005-05-27\n전화번호: ...\n닉네임: ImJihwan" listing
//   사용자 입장: 표 1줄에 이미 노출된 정보를 outro에 column label prefix 형태로 다시 노출 → 잉여 echo.
// 범용 (case ID/도메인 keyword 의존 X):
//   - 신호 1: text에 table header line(`[A | B | ...]`) 존재 — show_display 표 응답
//   - 신호 2: 마지막 numbered row 이후 outro 영역에 `(- |• )?{labelLike}: {value}` listing 패턴 ≥ 2줄
//   - labelLike: columnLabels(한국어 라벨) 또는 column key의 한국어 alias — Set match
//   - 매칭된 listing line 모두 drop. outro의 자연어 sentence(label:value 형태가 아닌)는 보존
//   - 단일 line 매칭 시 미발동 (follow-up 안내 false positive 회피)
// 다국어 호환: columnLabels에 등록된 라벨 prefix 매칭. 영문 컬럼 label도 동일 strip — 라벨 source-agnostic.
// 적용 시점: AgentSession.executeShowDisplay / nodeAgent.executeShowDisplay outro substitute 직후 +
//   finish/stop path 합성 후 (intro+table+outro 합성된 text에 적용)
export function stripColumnLabelEchoOutro(text, columnLabels, columns) {
  if (!text || typeof text !== 'string') return text;

  // labelLike set 구축 — columnLabels 한국어 라벨 + columns key(영문)
  const labelSet = new Set();
  if (columnLabels && typeof columnLabels === 'object') {
    for (const v of Object.values(columnLabels)) {
      if (typeof v === 'string' && v.length >= 1 && v.length <= 12) labelSet.add(v);
    }
  }
  if (Array.isArray(columns)) {
    for (const c of columns) {
      if (typeof c === 'string' && c.length >= 1 && c.length <= 20) labelSet.add(c);
    }
  }
  if (labelSet.size === 0) return text;

  // table header line 검출 — `[A | B | ...]` 형태가 단독 line
  // 없으면 미발동 (단순 chat 응답에는 column label echo 위험 낮음)
  const TABLE_HEADER_RE = /\n\[[^\]\n]*\|[^\]\n]*\]/;
  if (!TABLE_HEADER_RE.test(text)) return text;

  // 마지막 numbered row 위치 식별 — 그 이후가 outro
  const lines = text.split('\n');
  let lastRowIdx = -1;
  for (let i = lines.length - 1; i >= 0; i--) {
    if (/^\s*\d+\.\s/.test(lines[i])) { lastRowIdx = i; break; }
  }
  if (lastRowIdx === -1) return text;

  // outro 영역 추출 (마지막 row 다음 line부터 끝까지)
  const outroLines = lines.slice(lastRowIdx + 1);
  if (outroLines.length === 0) return text;

  // labelLike escape + listing pattern: `(- |• )?{label}:\s+{value}` (line start)
  const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const labelAlt = [...labelSet].map(esc).join('|');
  // 보수: label 뒤 콜론(:) 또는 한국어 '은/는/이/가' 같은 조사가 아니어야 함 — 조사형 자연어 sentence는 valid outro (예: "이름은 X이고")
  //   하지만 "이름: X" 형태는 raw column label echo로 listing 의도
  const LISTING_RE = new RegExp(`^\\s*(?:[-•*]\\s*)?(?:${labelAlt})\\s*[::]\\s*\\S`, 'u');

  // [F1 L10-OUTRO-NATURAL-SENTENCE-ECHO extension — M2 cover 외 sentence/comma 패턴]
  //   본질: 표 + outro에 (a) 컬럼라벨 쉼표 나열 ("이름, 성별, 전화번호") 또는
  //         (b) 자연어 sentence form 조사 echo ("프로필 사진도 등록돼 있어" / "이름은 X, 성별은 Y이야")
  //         형태로 컬럼 정보를 재표현하는 LLM stochastic 패턴.
  //         M2 LISTING_RE는 `label:`형식만 매칭 — sentence/comma 형은 미매칭 → 추가 detection.
  //   범용: 컬럼 라벨 set (M2 동일 labelSet) + 조사 set (은|는|이|가|도) — case ID/도메인 hardcode X.
  //   paraphrase 보강: multi-word label의 atomic token alias도 labelAliasSet에 포함
  //     (LLM이 "프로필" 컬럼을 "프로필 사진"으로 expand하거나 vice versa할 때 catch).
  //   안전성:
  //     - TABLE_HEADER 존재 시만 발동 (M2 동일 — 표 없으면 listing이 본 데이터)
  //     - 모드 B (comma echo): 라벨 ≥3 + 비라벨 토큰 ≤1 (값 없는 순수 라벨 enumeration만)
  //     - 모드 C (조사 echo): label/alias + 조사 매치 line의 점유율 ≥40% 또는 매칭 ≥2 (자연 sentence 보존)
  //     - 단독 추측성 자연어 ("정리해뒀어" 등 라벨 미포함)는 보존
  // 모드 B: 컬럼 라벨 comma-list (e.g. "이름, 성별, 생년월일, 전화번호, 닉네임, 프로필 사진")
  //   조건: 한 line의 token (쉼표/슬래시 split — space 보존 multi-word label) 중 라벨 ≥3개 + 비라벨 의미 token ≤1개
  //   space split 안 함 — "프로필 사진" 같은 multi-word label 보호
  const COMMA_SEP_RE = /[,\/]+/;
  // 모드 C-1 (strict): label + 조사 + value (e.g. "이름은 X이고")
  //   particle set: 은|는|이|가|도 (주격/대조 조사). 라벨 직후 0~1 space + 조사 + space + 값 token
  const PARTICLE_RE = new RegExp(`(?:${labelAlt})\\s*(?:은|는|이|가|도)\\s+\\S`, 'gu');
  // 모드 C-2 (paraphrase expand): label + (Korean paraphrase tail ≤6 char) + 조사 (e.g. "프로필 사진도", "회원 이름은")
  //   LLM이 column label을 약간 expand/paraphrase한 경우 catch.
  //   예: column "프로필" → outro "프로필 사진도" (사진 paraphrase + 도 조사)
  //   안전성: paraphrase tail은 Hangul + space, 최대 6 char (너무 멀면 별 sentence)
  //   strict 모드 못 잡은 경우 fallback (false positive 회피 위해 더 엄격한 발동 조건)
  const PARTICLE_PARAPHRASE_RE = new RegExp(
    `(?:${labelAlt})(?:\\s[가-힣]{1,5})?(?:은|는|이|가|도)\\s+\\S`,
    'gu',
  );
  // 모드 D (row echo): outro line이 `- value / value / value` 또는 `- value | value | value` 형태
  //   본질: 표 row 데이터를 outro에 verbatim 재나열 (slash/pipe separator)
  //   예: "- 김경호 / 2002-05-30 / 010-9753-4511 / 프로필 없음"
  //   안전성: dash/bullet prefix + slash/pipe separator + 토큰 ≥3개 → row 형태 강한 신호
  //   sentence form은 콜론/조사 패턴이므로 미매칭, comma-only 자연어 sentence는 미매칭
  //   row separator: `/`, `|`, ` · ` (공백 포함 가운뎃점)
  const ROW_ECHO_RE = /^\s*[-•*]\s*\S+\s*[\/|]\s*\S/u;
  const ROW_SEP_RE = /[\/|]/g;

  // 매칭된 line index 모음 (3 모드 통합)
  const matchedIdx = [];
  for (let i = 0; i < outroLines.length; i++) {
    const ln = outroLines[i];
    const trimmed = ln.trim();
    if (!trimmed) continue;

    // 모드 A (기존): `label: value` listing
    if (LISTING_RE.test(ln)) {
      matchedIdx.push(i);
      continue;
    }

    // 모드 B: comma-separated label enumeration (no values)
    //   token split (comma/slash) → trim → label/비라벨 카운트
    //   space 보존 — multi-word label ("프로필 사진") 보호
    const tokens = trimmed.split(COMMA_SEP_RE).map(t => t.trim()).filter(t => t.length > 0);
    if (tokens.length >= 3) {
      let labelHits = 0;
      let nonLabelMeaningful = 0;
      for (const tok of tokens) {
        if (labelSet.has(tok)) {
          labelHits += 1;
        } else if (tok.length >= 1 && tok !== '등' && tok !== '...' && tok !== '·') {
          // '등'/'...' 같은 enumeration filler 무시, 그 외 의미 토큰
          nonLabelMeaningful += 1;
        }
      }
      // 라벨 ≥3 + 비라벨 의미 토큰 ≤1 → pure column label enumeration
      if (labelHits >= 3 && nonLabelMeaningful <= 1) {
        matchedIdx.push(i);
        continue;
      }
    }

    // 모드 C-1: label + 조사 + value sentence echo (strict)
    //   매칭 횟수 + line 점유율 검사
    PARTICLE_RE.lastIndex = 0;
    let particleHits = 0;
    let particleSpan = 0;
    let m;
    while ((m = PARTICLE_RE.exec(ln)) !== null) {
      particleHits += 1;
      particleSpan += m[0].length;
      if (particleHits >= 10) break; // safety
    }
    if (particleHits >= 2) {
      // 2개 이상 매칭 → comma-separated sentence echo (e.g. "이름은 X, 성별은 Y이야")
      matchedIdx.push(i);
      continue;
    }
    if (particleHits === 1) {
      // 1개 매칭 — line이 짧고 라벨echo 점유율 높을 때만 (자연 sentence 보존)
      const lineChars = trimmed.length;
      // 점유율 ≥40% 또는 line이 ≤30자 단순 form ("이름은 X이고" 6자)
      if (lineChars > 0 && (particleSpan / lineChars >= 0.4 || lineChars <= 30)) {
        matchedIdx.push(i);
        continue;
      }
    }

    // 모드 C-2: paraphrase expand + 조사 (LLM이 column label을 약간 expand/paraphrase한 경우)
    //   예: "프로필 사진도 등록돼 있어" — label "프로필" + " 사진" expand + "도" 조사
    //   strict 모드 missed 시만 시도 (false positive 회피 위해 더 엄격한 조건)
    PARTICLE_PARAPHRASE_RE.lastIndex = 0;
    let paraHits = 0;
    let paraSpan = 0;
    while ((m = PARTICLE_PARAPHRASE_RE.exec(ln)) !== null) {
      paraHits += 1;
      paraSpan += m[0].length;
      if (paraHits >= 10) break;
    }
    if (paraHits >= 1) {
      // line ≤30자 + paraphrase span ≥40% → echo 강한 신호 (자연 sentence 보존)
      const lineChars = trimmed.length;
      if (lineChars > 0 && lineChars <= 30 && (paraSpan / lineChars >= 0.4)) {
        matchedIdx.push(i);
        continue;
      }
    }

    // 모드 D: row echo — outro line이 `- value / value / value` 또는 `- value | value | value` 형태
    //   본질: 표 row 데이터를 outro에 verbatim 재나열 (slash/pipe separator)
    //   예: "- 김경호 / 2002-05-30 / 010-9753-4511 / 프로필 없음"
    //   trigger: dash/bullet prefix + slash/pipe separator 2개 이상 (값 ≥3개)
    if (ROW_ECHO_RE.test(ln)) {
      ROW_SEP_RE.lastIndex = 0;
      let sepCount = 0;
      while (ROW_SEP_RE.exec(ln) !== null) sepCount += 1;
      if (sepCount >= 2) {
        // separator ≥2개 → 값 ≥3개 → row dump 강한 신호
        matchedIdx.push(i);
        continue;
      }
    }
  }

  // 2줄 미만이면 미발동 — 단, 모드 B(comma)/모드 C(particle) 단일 매칭은 발동
  //   M30/F1 패턴: 1줄 sentence/comma echo도 strip 대상 (M2 listing 모드 2줄 룰만 적용)
  if (matchedIdx.length === 0) return text;
  if (matchedIdx.length === 1) {
    // 단일 매칭: M2 모드 A(listing) 단독 X → 보존 (기존 룰 유지)
    // 모드 B/C 단독 → strip 발동 (sentence/comma는 1줄도 명백 echo)
    const onlyIdx = matchedIdx[0];
    const onlyLn = outroLines[onlyIdx];
    const onlyLnTrim = onlyLn.trim();
    // listing 모드 단독 발동 여부 — onlyLn이 LISTING_RE 매칭이면 모드 A 단독 → 보존
    if (LISTING_RE.test(onlyLn)) {
      // 모드 B 같은 라벨 enumeration도 동시 매칭은 거의 없으므로 모드 A 단독으로 간주
      // 단, comma/particle 검사 재확인:
      const tokensRecheck = onlyLnTrim.split(COMMA_SEP_RE).map(t => t.trim()).filter(t => t.length > 0);
      let _labelHitsRecheck = 0;
      for (const t of tokensRecheck) if (labelSet.has(t)) _labelHitsRecheck += 1;
      PARTICLE_RE.lastIndex = 0;
      const hasParticle = PARTICLE_RE.test(onlyLn);
      // 모드 B/C 신호 없으면 모드 A 단독 → 미발동 (기존 룰 유지)
      if (_labelHitsRecheck < 3 && !hasParticle) return text;
    }
  }

  // 매칭 line drop
  const matchedSet = new Set(matchedIdx);
  const survivingOutro = outroLines.filter((_, i) => !matchedSet.has(i));

  // outro가 모두 listing일 수도 있음 → trailing blank line 정리
  const result = lines.slice(0, lastRowIdx + 1).concat(survivingOutro).join('\n');
  // 연속 blank line 압축 + trailing whitespace 정리
  return result.replace(/\n{3,}/g, '\n\n').replace(/[ \t]+$/gm, '').trimEnd();
}

// [L10-DETAIL-SCHEDULE-001 INTRO_HALLUCINATION_DATE_REFLECT] intro 안 narrow date keyword hallucination strip (dual sync with nodeAgent.mjs) //
//   본질:
//     CHAIN MEMBER_DETAIL redirect 등으로 backend가 full window data를 반환했음에도
//     LLM이 planning context bias로 intro에 "이번달/이번주/오늘" 같은 narrow date keyword를 hallucinate.
//     사용자 input에는 narrow date keyword가 없는데도 intro만 좁게 framing → 데이터-intro mismatch (false ack).
//   해소:
//     1) user input에 explicit time keyword 있으면 정상 framing — strip 미발동
//     2) buffer items의 date field range가 narrow keyword가 의미하는 window (오늘=1일, 이번주=7일, 이번달=31일)보다 넓으면
//        intro의 narrow date phrase strip ("배대호 회원의 이번달 출석 현황" → "배대호 회원의 출석 현황")
//   구조 기반: case ID hardcode 없음, F6 CHAIN_CONTEXT_DATE_RESET의 `_TIME_KW_CHAIN` 패턴과 호환.
//   회귀 risk 최소화: user input keyword 있으면 미발동 + buffer item 부재 시 미발동.
export function stripIntroDateHallucination(text, buffer, userInput) {
  if (!text || typeof text !== 'string') return text;
  if (!buffer?.items?.length) return text;

  // user input에 explicit time keyword 있으면 정상 framing
  const _input = (userInput || '').toString();
  const _TIME_KW_USER = /오늘|어제|내일|모레|지난\s*주|이번\s*주|다음\s*주|금주|지난\s*달|이번\s*달|다음\s*달|금월|\d+\s*월|\d+\s*일|\d{4}-\d{2}-\d{2}|최근|지난\s*\d+|\d+\s*일\s*전|작년|올해|금년|내년|지난해|당월|금일/;
  if (_TIME_KW_USER.test(_input)) return text;

  // intro narrow date keyword 검출 — 가장 흔한 hallucination: 오늘/이번주/이번달/금일/금주/금월
  //   (필요시 확장 가능. user input 패턴 subset — 더 좁은 단위만 strip 대상)
  const NARROW_KW_RE = /(오늘(?:은|의|에|도)?|금일|이번\s*주(?:는|의|에|도)?|금주|이번\s*달(?:은|의|에|도)?|금월|이번\s*년(?:은|의|에|도)?|이번년도|금년|올해(?:는|의|에|도)?)/;
  const narrowMatch = text.match(NARROW_KW_RE);
  if (!narrowMatch) return text;

  // buffer items에서 date field 자동 탐지 — 첫 item의 'YYYY-MM-DD' 패턴 값을 가진 첫 key
  const DATE_VAL_RE = /^\d{4}-\d{2}-\d{2}/;
  const sample = buffer.items[0] || {};
  let dateField = null;
  for (const k of Object.keys(sample)) {
    const v = sample[k];
    if (typeof v === 'string' && DATE_VAL_RE.test(v)) {
      dateField = k;
      break;
    }
  }
  if (!dateField) return text;

  // min/max 계산 (item count cap 500 — performance)
  const limit = Math.min(buffer.items.length, 500);
  let minStr = null, maxStr = null;
  for (let i = 0; i < limit; i++) {
    const v = buffer.items[i]?.[dateField];
    if (typeof v !== 'string' || !DATE_VAL_RE.test(v)) continue;
    const d = v.slice(0, 10);
    if (minStr === null || d < minStr) minStr = d;
    if (maxStr === null || d > maxStr) maxStr = d;
  }
  if (!minStr || !maxStr) return text;
  const minDate = new Date(minStr);
  const maxDate = new Date(maxStr);
  if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) return text;
  const rangeDays = Math.round((maxDate.getTime() - minDate.getTime()) / 86400000);

  // narrow keyword가 의미하는 윈도우 (오늘=1, 이번주=7, 이번달=31, 올해/금년=365)
  const matched = narrowMatch[1];
  let expectedWindow = 1;
  if (/이번\s*주|금주/.test(matched)) expectedWindow = 7;
  else if (/이번\s*달|금월/.test(matched)) expectedWindow = 31;
  else if (/이번\s*년|이번년도|금년|올해/.test(matched)) expectedWindow = 366;

  // 데이터 range가 narrow window보다 크면 mismatch → strip
  if (rangeDays <= expectedWindow) return text;

  // narrow phrase 자체만 제거 + 잔여 공백/구두점 정리
  // 매칭 단어 앞 공백 1개 흡수 ("회원의 이번달 출석" → "회원의 출석")
  const STRIP_RE = new RegExp(`\\s*${matched.replace(/\s+/g, '\\s*')}\\s*`, '');
  return text.replace(STRIP_RE, ' ').replace(/\s{2,}/g, ' ').replace(/\s+([.,!?])/g, '$1').trim();
}

export function stripFalseChangeDirectionIntro(text, buffer, userInput) {
  if (!text || typeof text !== 'string') return text;
  if (!buffer?.items?.length) return text;

  // buffer 변화 차원 보유 검사 (_change_* prefix)
  const hasChangeField = buffer.items.some(it =>
    it && typeof it === 'object' && Object.keys(it).some(k => k.startsWith('_change_'))
  );
  if (!hasChangeField) return text;

  // user input에 변화 방향 키워드 있으면 정상 framing — strip 미발동
  // _detectChangeDirection과 동일 패턴 (활용형 다수)
  const _input = (userInput || '').toString();
  const userHasChangeKw = /줄(?:어|고|었|어든|어들|어드|어진|어졌|이고|이는)|감소|덜|낮(?:아|았|아지|아진|아졌)|떨(?:어|어졌|어진|어지)|적(?:어진|어졌|어지)|늘(?:어|고|었|어난|어들|어드|어나|어났)|증가|많(?:아진|아졌|아지|아져)|높(?:아|았|아지|아진|아졌)|올라(?:가|간|갔|감)?|상승/.test(_input);
  if (userHasChangeKw) return text;

  // intro/outro 안 "<word>(이|가) (줄어든|...)" phrase 패턴
  //   "출석률이 줄어든 회원", "매출이 늘어난 거래" 등
  //   word는 한국어 명사 1~10자 — 너무 짧/긴 매칭 회피
  //   particle (이|가) 또는 "(는|은)" 까지 허용 — "출석률은 줄어든" 패턴
  const CHANGE_PHRASE_RE = /([가-힣]{1,10})\s*(?:이|가|은|는)\s*(줄어든|줄어들었던|줄어드는|줄어든|감소한|감소된|적어진|낮아진|떨어진|떨어졌던|늘어난|늘어났던|늘어나는|증가한|증가된|많아진|높아진|올라간|상승한)\s+/g;

  if (!CHANGE_PHRASE_RE.test(text)) return text;

  // strip — phrase 자체만 제거하고 나머지는 보존 ("회원 51명이야" 등)
  return text.replace(CHANGE_PHRASE_RE, '').replace(/\s+/g, ' ').replace(/\n{3,}/g, '\n\n').replace(/^[\s.,]+/, '').trim();
}

// stripContradictorySuperlativeClaims — outro의 "가장/제일 X" 극값 주장 검증 (DEMO-12-PEAK B) //
// 본질: LLM outro가 (B1) 정렬 데이터의 실제 extreme과 다른 entity를 "가장 ~"로 지목하거나,
//        (B2) 데이터에 존재하지 않는 entity를 지목(fabrication)하는 stochastic 패턴.
//        예: 표 top=17:00-18:00(최저)인데 outro "07:00~08:00가 가장 한산해"(없는 시간대 fabrication).
// 설계 (범용·언어무관 안전판):
//   - entity 컬럼은 self-displaying(raw==표시) 값만 대상 — 시간대(HH:MM-HH:MM)처럼 outro에 verbatim 등장.
//     enum-mapped 값(dayOfWeek MON→"월")은 outro에 raw("MON")가 안 나타나므로 미발동 → false-positive 회피(residual T3).
//   - 테이블 라인은 superlative 키워드가 없어 보존 — outro 문장만 수술(라인 단위 → 문장 단위 strip).
//   - (B2) 토큰이 데이터에 없으면 항상 strip. (B1) 데이터엔 있으나 정렬 top이 아니면 정렬된 경우만 strip.
export function stripContradictorySuperlativeClaims(text, buffer) {
  if (!text || typeof text !== 'string') return text;
  const items = buffer?.items;
  if (!Array.isArray(items) || items.length < 2) return text;
  const sample = items[0];
  if (!sample || typeof sample !== 'object') return text;

  // entity(식별) 컬럼 추론: 값이 비-숫자 문자열인 첫 필드 (_ prefix 제외)
  let idCol = null;
  for (const k of Object.keys(sample)) {
    if (k.startsWith('_')) continue;
    const v = sample[k];
    if (typeof v === 'string' && v.trim() && isNaN(Number(v))) { idCol = k; break; }
  }
  if (!idCol) return text;

  const idValues = items.map(it => String(it?.[idCol] ?? '')).filter(Boolean);
  if (idValues.length < 2) return text;
  // self-displaying 시간대 형태만 대상 (raw==표시 보장 — verbatim 매칭 안전)
  const idIsTimeLike = idValues.filter(v => /\d{1,2}:\d{2}/.test(v)).length >= Math.ceil(idValues.length / 2);
  if (!idIsTimeLike) return text;  // 그 외 entity는 no-op (residual)

  const norm = t => String(t).replace(/\s+/g, '');
  const idNormSet = new Set(idValues.map(norm));
  const topNorm = norm(items[0][idCol] ?? '');
  const isSorted = !!buffer.appliedSort;
  const SUPERLATIVE_RE = /가장|제일|최고|최저|최다|최소|가장|busiest|deadest|most|least|highest|lowest/i;
  const TIME_TOKEN_G = /\d{1,2}:\d{2}\s*[-~]\s*\d{1,2}:\d{2}/g;
  const TIME_TOKEN = /\d{1,2}:\d{2}\s*[-~]\s*\d{1,2}:\d{2}/;

  let changed = false;
  const outLines = text.split('\n').map(line => {
    if (!SUPERLATIVE_RE.test(line) || !TIME_TOKEN.test(line)) return line;  // 테이블/일반 라인 보존
    // 라인 내 문장 분리 후 극값 주장 문장만 검사
    const sents = line.split(/(?<=[.!?])\s+/);
    const keptS = sents.filter(s => {
      if (!SUPERLATIVE_RE.test(s)) return true;
      const tokens = s.match(TIME_TOKEN_G) || [];
      if (!tokens.length) return true;  // 극값 주장이나 시간 토큰 없음 → 보존(판단 보류)
      for (const tk of tokens) {
        const tkN = norm(tk);
        if (!idNormSet.has(tkN)) { changed = true; return false; }                 // (B2) fabrication
        if (isSorted && tkN !== topNorm) { changed = true; return false; }         // (B1) 정렬 top 아님
      }
      return true;
    });
    return keptS.join(' ').trim();
  });
  if (!changed) return text;
  return outLines.join('\n').replace(/[ \t]{2,}/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
}

// injectPerMemberRevenueCapabilityDisclosure — per-member revenue + filter 의도에 대한 capability disclosure 자동 보강 //
// [L11-DERIVE-FILTER-001 fix] 본질:
//   사용자가 "1인당 매출/회원별 매출 + 평균 이상/이하/상위 N명 등" 요청 시
//   toolchain에 per-member revenue sum capability 부재 (aggregate field:"name"은 sum 아님, _avgGroupSize 가드).
// 정책 (aiPrompts.js:595 [PER-MEMBER REVENUE — CAPABILITY DISCLOSURE — STRICT ROUTING]):
//   CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD 호출 + finish에 (1) 객단가/총매출/결제고객수 +
//   (2) "회원별 매출 합계 기준 평균 이상만 추리는 기능은 현재 제공되지 않아요" disclosure 의무.
// LLM 변동성 (run history facts/facts-6~12 분석):
//   - facts (정답): 객단가 + disclosure 모두 포함
//   - facts-6/8/9/11/12 (부분): 객단가 + 총매출만, disclosure 누락
//   - facts-7 (부분): "평균 이상인 내역은 따로 없어요" 비표준 disclosure
//   - facts-10/run6 (wrong path): 거래 list / 회원별 거래 횟수 (semantic mismatch)
//   정책 prompt만으로는 ~30% disclosure 포함률 → deterministic 보강 필요.
// trigger 조건 (정책 line 595 keyword 그대로, case ID 하드코딩 X):
//   1. intention === 'CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD'
//   2. userInput에 per-member axis (1인당/회원별/회원당/회원 1명당) 포함
//   3. userInput에 filter qualifier (평균 이상/이하/이상만/이하만/상위 N명/하위 N명/N원 이상/이하) 포함
//   4. finishResponse에 disclosure phrase 부재 (제공되지 않/지원되지 않/기능은.*없/따로 없어요 등)
// 한국어 tone matching: finishResponse 마지막 종결어미 ("이야"/"이에요" 등)에 맞춰 disclosure 종결.
export function injectPerMemberRevenueCapabilityDisclosure(finishResponse, userInput, intention) {
  if (!finishResponse || typeof finishResponse !== 'string') return finishResponse;
  if (intention !== 'CHECK_SALES+AVERAGE+REVENUE_STATISTICS_BY+PERIOD') return finishResponse;
  const _input = (userInput || '').toString();
  if (!_input) return finishResponse;
  const _perMemberAxis = /(1\s*인\s*당|회원\s*별|회원\s*당|회원\s*1\s*명\s*당)/.test(_input);
  if (!_perMemberAxis) return finishResponse;
  const _filterQual = /(평균\s*이상|평균\s*이하|이상\s*만|이하\s*만|상위\s*\d+\s*명|하위\s*\d+\s*명|[0-9][0-9,]*\s*원\s*이상|[0-9][0-9,]*\s*원\s*이하)/.test(_input);
  if (!_filterQual) return finishResponse;
  const _alreadyDisclosed = /(제공\s*되지\s*않|지원\s*되지\s*않|기능\s*은\s*(?:현재\s*)?(?:없|제공))|추리는\s*기능[^.]{0,30}(없|않)|따로\s*없어요/.test(finishResponse);
  if (_alreadyDisclosed) return finishResponse;
  // tone matching: 응답 마지막에 polite ending (이에요/예요/었어요/입니다/해요) 있으면 polite,
  // casual ending (이야/이었어/있어/없어/었어) 있으면 casual. default polite (이에요체) — 더 안전.
  const _tail = finishResponse.replace(/\s+$/, '').slice(-30);
  const _isPolite = /(이에요|예요|었어요|이었어요|입니다|해요|에요)\.?\s*$/.test(_tail);
  const _isCasual = !_isPolite && /(이야|이었어|있어|없어|었어|어\.?\s*$)/.test(_tail);
  const _disclosure = _isCasual
    ? "다만, 회원별 매출 합계를 기준으로 '평균 이상만' 추리는 기능은 현재 제공되지 않아."
    : "다만, 회원별 매출 합계를 기준으로 '평균 이상만' 추리는 기능은 현재 제공되지 않아요.";
  const _base = finishResponse.replace(/\s+$/, '');
  return `${_base}\n${_disclosure}`;
}

// stripPlaceholderArtifacts 정의는 TL-1에서 src/Agent/Tools/placeholderSubstitute.js로 이동.
// 파일 상단 import + re-export 유지.

// detectRawNumericLeak — buffer에 _change_* 보유 + outro에 placeholder 0 + 변화 키워드 + raw 변화량 숫자 토큰이면 leak 판정 //
// - 변화량 outro 컨텍스트(_change_/_changeRate_ 보유 buffer)에서만 판정 — 일반 outro 영향 차단
// - placeholder({{CHANGE:...}}/{{CHANGE_RATE:...}}) 사용 시 leak 아님
// - 변화 키워드 매칭 시에만 raw 숫자 토큰 검출 (단순 메타 수치 노출 회피)
// - raw 숫자 패턴: 부호 접두("+5,080,000원", "-10,000원", "+152.9%") OR 천단위 콤마("5,080,000원") 필수
//   → 단순 카운트("3건", "50명") false positive 차단
// - sample은 최초 raw 숫자 토큰 1개 반환 (LLM 안내 메시지용)
export function detectRawNumericLeak(responseText, buffer) {
  if (!responseText || !buffer?.items?.length) return { detected: false, sample: null };
  const hasChangeField = buffer.items.some(it =>
    it && typeof it === 'object' && Object.keys(it).some(k => k.startsWith('_change_'))
  );
  if (!hasChangeField) return { detected: false, sample: null };
  if (/\{\{(?:CHANGE|CHANGE_RATE):/i.test(responseText)) return { detected: false, sample: null };
  const CHANGE_KW = /늘었|줄었|증가|감소|상승|하락|올랐|떨어졌|더\s*많|더\s*적|더\s*높|더\s*낮|차이/;
  if (!CHANGE_KW.test(responseText)) return { detected: false, sample: null };
  // 변화량 raw 숫자 — 부호 접두 OR 천단위 콤마 필수 (카운트 false positive 차단)
  const RAW_NUM_RE = /[+\-−]\s*\d+(?:[,.]\d+)*\s*(?:원|건|명|회|%|개)|\d{1,3}(?:,\d{3})+\s*(?:원|건|명|회|%|개)/;
  const m = responseText.match(RAW_NUM_RE);
  if (!m) return { detected: false, sample: null };
  return { detected: true, sample: m[0] };
}

// detectIntroOutroNumericHallucination — buffer 구조 기반 범용 수치 sanity check //
// - intro/outro에 등장하는 수치(콤마/만/억/% 포함)를 모두 추출
// - buffer items 각 row 값, 자동 sum/avg/min/max, buf.summary scalar, items.length으로 valid set 구축
// - 한국어 단위 normalize (만→×10^4, 억→×10^8, 콤마/단위 strip)
// - valid set 미매칭 시 detected (1회 한정 retry용)
// - _change_* 보유 케이스는 기존 detectRawNumericLeak 우선 → 본 함수 skip (이중 retry 방지)
// - placeholder({{X:field}}) 사용 시 skip (별도 치환 흐름)
// - 도메인 키워드 없음 (구조/numeric 기반만)
export function detectIntroOutroNumericHallucination(text, buffer) {
  // [DISABLED] hallucination 차단 가치 < 비용. placeholder 가이드 + LLM 자율 신뢰.
  // - 큰 raw 수치 hallucination(12,500,000원)은 placeholder 강제 가이드로 1차 차단
  // - 의미적 hallucination(맥락상 잘못된 metric)은 본 함수도 못 잡음 (valid set 통과)
  // - 정확한 raw value(19.4%) 잘못 strip하던 부작용 제거
  // - retry 비용(매번 system message inject) 절약
  return { detected: false, sample: null };
  // eslint-disable-next-line no-unreachable
  if (!text || typeof text !== 'string') return { detected: false, sample: null };
  if (!buffer || !buffer.items?.length) return { detected: false, sample: null };

  // _change_* 보유 시 기존 패턴 우선 (false negative 회피)
  const hasChangeField = buffer.items.some(it =>
    it && typeof it === 'object' && Object.keys(it).some(k => k.startsWith('_change_'))
  );
  if (hasChangeField) return { detected: false, sample: null };

  // placeholder 보유 시 skip
  if (/\{\{[A-Z_]+/i.test(text)) return { detected: false, sample: null };

  // STRICT layer — large numbers / Korean scales → placeholder mandatory (no valid set check)
  // - Catches L1-SALES-005 "12,500,000원", "1,250만원" patterns (rare hallucination magnitudes)
  // - % values moved to FLEXIBLE — backend rate values like "19.4%" must pass valid-set check (not blanket-blocked)
  const STRICT_NUM_RE = /[+\-−]?\s*\d{1,3}(?:,\d{3})+\s*(?:원|건|명|회|개)?|[+\-−]?\s*\d+(?:\.\d+)?\s*(?:만|억)\s*(?:원|건|명|회|개)?/;
  const strictMatch = text.match(STRICT_NUM_RE);
  if (strictMatch) return { detected: true, sample: strictMatch[0].trim() };

  // FLEXIBLE layer — small unit-suffixed numbers (including %) — valid-set check below
  const NUM_RE = /([+\-−])?\s*(\d+(?:\.\d+)?)\s*(원|건|명|회|개|%)/g;

  const matches = [];
  let m;
  while ((m = NUM_RE.exec(text)) !== null) {
    const numStr = m[2].replace(/,/g, '');
    const unit = m[3]; // 원/건/명/회/개 (NUM_RE now captures these only — large/%/만/억 handled by STRICT_NUM_RE above)
    if (!unit) continue;

    const num = parseFloat(numStr);
    if (!isFinite(num)) continue;

    matches.push({ raw: m[0].trim(), num, unit });
  }

  if (matches.length === 0) return { detected: false, sample: null };

  // Valid set 구축 (구조 기반)
  const validSet = new Set([0, 1]); // 0, 1은 자연어 자유도 (예외/없음 표현)
  validSet.add(buffer.items.length); // 전체 count

  // items 각 row의 모든 numeric 값 + 필드별 집계
  const numericFieldsMap = new Map();
  for (const item of buffer.items) {
    if (!item || typeof item !== 'object') continue;
    for (const [k, v] of Object.entries(item)) {
      if (k.startsWith('_')) continue;
      if (typeof v === 'number' && isFinite(v)) {
        validSet.add(Math.abs(v));
        if (!numericFieldsMap.has(k)) numericFieldsMap.set(k, []);
        numericFieldsMap.get(k).push(v);
      }
    }
  }

  // 필드별 sum/avg/min/max/count_nonzero/count_zero
  for (const vals of numericFieldsMap.values()) {
    if (vals.length === 0) continue;
    const sum = vals.reduce((a, b) => a + b, 0);
    validSet.add(Math.abs(sum));
    const avg = sum / vals.length;
    validSet.add(Math.abs(Math.round(avg)));
    validSet.add(Math.abs(parseFloat(avg.toFixed(1))));
    validSet.add(Math.abs(Math.max(...vals)));
    validSet.add(Math.abs(Math.min(...vals)));
    validSet.add(vals.filter(v => v !== 0).length);
    validSet.add(vals.filter(v => v === 0).length);
  }

  // buf.summary scalar 값
  if (buffer.summary && typeof buffer.summary === 'object') {
    for (const v of Object.values(buffer.summary)) {
      if (typeof v === 'number' && isFinite(v)) {
        validSet.add(Math.abs(v));
        validSet.add(Math.abs(Math.round(v)));
        validSet.add(Math.abs(parseFloat(v.toFixed(1))));
      }
    }
  }

  // 매칭 검증 — 정확/round/±0.5 오차
  for (const { raw, num } of matches) {
    const absNum = Math.abs(num);
    let matched = false;
    if (validSet.has(absNum)) matched = true;
    else if (validSet.has(Math.round(absNum))) matched = true;
    else {
      for (const v of validSet) {
        if (Math.abs(v - absNum) <= 0.5) { matched = true; break; }
      }
    }
    if (!matched) {
      return { detected: true, sample: raw };
    }
  }

  return { detected: false, sample: null };
}

// detectEntityCatalogMismatch — catalog 호출 후 specific entity 미매칭 fallback 가드 //
// [P2-E] 본 검증은 call_api(catalog) 직후 [CATALOG VALIDATION] guide로 LLM-as-judge가 수행 (nodeAgent/useAiChat).
// 이 함수는 LLM이 가이드 무시하고 show_display 호출 시 fallback 안전망 — 1회 retry → 2회차 auto-finish 변환.
// [B4-2] Entity mismatch guard — 다국어 패턴 테이블 + 언어 무관 판정 로직 //
// 언어 추가: _ENTITY_GUARD_PATTERNS 각 regex에 |newPattern 추가만 하면 됨
const _ENTITY_GUARD_PATTERNS = {
  interrogative: /^(?:어떤|무슨|어느|이런|그런|저런|what|which|any|some|how|どの|どんな|何の|哪个|什么)$/i,
  listIntent: /목록|리스트|종류|타입|카테고리|카탈로그|전체|모든|catalog|category|type|list|all|every|available|一覧|全部|すべて/i,
  verbEnding: /(?:있어|없어|있는|없는|있나|있는지|되는|하는|된|한|될|할|인지|인가|인데|have|has|show|tell|give|get|see|know)$/i,
  filterSort: /만$|순으로|순$|정렬|높은|낮은|오름차|내림차|비싼|싼|이상|이하|제외|빼고|빼줘|중에서|만\s+보여|sort|filter|above|below|more|less|only|exclude|以上|以下/i,
  intentVerb: /^(?:보여줘|알려줘|확인|조회|검색|찾아줘|뭐야|확인해줘|보여주세요|말해줘|알려주세요|show|tell|check|find|search|get|see|display|見せて|教えて|確認)$/i,
  categoryNoun: /^(?:회원|회원권|수업|매출|상품|문자|기기|운동|member|class|lesson|sales|product|message|device|workout|会員|授業|売上|商品)$/i,
  descriptor: /^(?:정보|내역|내용|현황|상세|통계|추천|info|detail|history|status|stats|summary|情報|詳細|履歴|統計)$/i,
  // MEMBER_DETAIL subjects (POINT/HOLDING/EXTEND/REFUND/LOCKER/CARD/ATTENDANCE/PROFILE/MEMBERSHIP/LESSON/PARTICIPATION 등)
  //   *_DETAIL intention prior_endpoint=CHECK_MEMBER_SEARCH 흐름에서 LLM이 member name 부재 시 subject 자체를 name 으로 hallucinate
  memberDetailSubject: /^(?:포인트|홀딩|연장|환불|락커|카드|출석|프로필|회원권|상품|수업|레슨|참여|참가|구매|결제|예약|영양|식단|가입|등록|이력|history|point|holding|extension|refund|locker|card|attendance|profile|membership|product|lesson|participation|class|reservation)$/i,
};

// [VAGUE MEMBER SEARCH] CHECK_MEMBER_SEARCH 호출 직전 params.name이 사람 이름이 아닌 도메인 keyword 일 때 catch.
//   - 본질: vague *_DETAIL Q ("회원 포인트 상세 알려줘" 등)에서 LLM이 prior_endpoint=CHECK_MEMBER_SEARCH 강제 흐름 + 사람 이름 부재 →
//     name 자리에 도메인 단어 ("회원"/"포인트"/"상세" 등) hallucinate → 0건 → "해당 회원을 찾지 못했어" false ack
//   - 검출: name 토큰이 categoryNoun / descriptor / memberDetailSubject 정규식 매칭
//   - return null: name 이 사람 이름으로 보임 (정상 진행)
//   - return string: 차단 사유 (call_api 가 LLM 에 nudge)
//   - 한국어 keyword regex는 prompt §2.2 예외 (user input 매칭 keyword) — 도메인 noun 식별용 ENTITY_GUARD_PATTERNS 와 동일 영역
export function detectVagueMemberSearchName(params) {
  if (!params || typeof params !== 'object') return null;
  const name = typeof params.name === 'string' ? params.name.trim() : '';
  const phone = typeof params.phone === 'string' ? params.phone.trim() : '';
  // phone 있으면 항상 정상 — 010 으로 시작하는 전화번호는 도메인 단어가 아님
  if (phone) return null;
  if (!name) return null;
  // 단일 token 추출 — name 이 공백/조사 포함 시 첫 token 만 검사
  const tokens = name.match(/[\p{L}\p{N}]{1,}/gu) || [name];
  for (const t of tokens) {
    if (_ENTITY_GUARD_PATTERNS.categoryNoun.test(t)) return `domain noun "${t}" (category)`;
    if (_ENTITY_GUARD_PATTERNS.descriptor.test(t)) return `domain noun "${t}" (descriptor)`;
    if (_ENTITY_GUARD_PATTERNS.memberDetailSubject.test(t)) return `domain noun "${t}" (subject)`;
  }
  return null;
}

// [VAGUE DETAIL INTENT] DETAIL intention 회원명 미지정 시 LIST fallback 차단 + 되묻기 finish 강제
//   - 본질: MEMBER_DETAIL category intention (CHECK_MEMBER+*_DETAIL)은 개별 회원 상세 — memberId 필수
//     LLM이 memberId 없이 호출 시 (a) DETAIL fallback to LIST → 전체 list 응답 → 사용자 "어느 회원?" 의도 mismatch
//                                  (b) DETAIL 직접 호출 → backend 0건 / hallucination
//   - cluster: CARD/POINT/PRODUCT/REFUND/HOLDING/EXTEND/LOCKER/ATTENDANCE/PROFILE/MEMBERSHIP/LESSON+PARTICIPATION DETAIL
//   - 패턴 일관성: cluster 5/6 case 가 VAGUE_MEMBER_SEARCH guard 로 자연어 되묻기 응답. HOLDING_DETAIL-2 만 LIST fallback 이상치
//   - generic 검출: userInput 에 DETAIL keyword (상세/내역/이력) + subject (홀딩/연장/환불/카드/포인트/락커/출석/상품/회원권) 매칭 + 사람 이름 부재
//   - return null: 정상 진행 (회원명 있음 or DETAIL keyword 없음)
//   - return string: 차단 사유 (call_api 가 LLM 에 finish 되묻기 nudge)
const _VAGUE_DETAIL_SUBJECT_RE = /(?:홀딩|연장|환불|카드|포인트|락커|출석|회원권|상품|구매|결제|예약|레슨|수업|참여|참가|프로필|이력)/;
const _VAGUE_DETAIL_KEYWORD_RE = /(?:상세|내역|이력|정보)/;
const _VAGUE_DETAIL_NAME_BLACKLIST = new Set([
  // 도메인 noun (subject/descriptor)
  '회원','회원권','홀딩','연장','환불','카드','포인트','락커','락카','출석','상품','구매','결제','예약',
  '레슨','수업','참여','참가','프로필','이력','상세','내역','정보','확인','조회','전체','모든','검색','목록','명단','현황',
  // 동사/요청 표현 (보여줘/알려줘/말해줘 등) — randomize 변형 포함
  '보여줘','알려줘','말해줘','확인해','조회해','보여줘','보여요','보여줄','알려주','말해주','보여주세요','알려주세요','말해주세요',
  '보내줘','보내주','보내주세요','확인해줘','조회해줘','확인해주세요','조회해주세요'
]);
const _VAGUE_DETAIL_VERB_ENDING_RE = /(?:보여|알려|말해|보내|확인|조회).{0,3}$/;
// CATALOG keyword (member-detail이 아닌 catalog 의도 명시) — 본 keyword 있으면 vague detail guard 통과
//   "종류/어떤/catalog" — catalog 명시 (회원권 종류 보여줘 / 어떤 회원권 있어)
//   "목록/명단/현황/리스트/list" — list 명시 (회원권 목록 알려줘)
const _CATALOG_LIST_KEYWORD_RE = /(?:목록|명단|현황|리스트|list|종류|어떤|catalog|카탈로그)/i;

export function detectVagueDetailIntent(userInput, intention) {
  if (!userInput || typeof userInput !== 'string') return null;
  if (!intention || typeof intention !== 'string') return null;
  // DETAIL intention 직접 호출 OR DETAIL-paired LIST (CHECK_MEMBER+HOLDING_LIST 같이 *_DETAIL 카운터파트 보유)
  // intention key 자체에 DETAIL keyword 포함 (PROFILE/MEMBERSHIP/HOLDING/EXTEND/REFUND/CARD/POINT/LOCKER/ATTENDANCE/PRODUCT/LESSON+PARTICIPATION + _DETAIL)
  const _isDetailIntention = /_DETAIL$/.test(intention) && /^CHECK_MEMBER[+_]/.test(intention);
  // DETAIL-paired LIST — HOLDING_LIST 의 DETAIL counterpart 가 존재. 본 가드는 LIST 진입 시도 DETAIL 의도 추정 catch
  const _isDetailPairedList = /^CHECK_MEMBER\+HOLDING_LIST$/.test(intention);
  // [SYN-CHECK_MEMBER+MEMBERSHIP_DETAIL-3] CATALOG_LIST hop — userInput "회원권 상세 보여줘" (no member name, no catalog keyword)
  //   LLM 이 DETAIL intent 를 catalog 로 fallback 하는 패턴 catch. _DETAIL counterpart (CHECK_MEMBER+MEMBERSHIP_DETAIL) 가 존재함.
  const _isCatalogDetailFallback = /^CHECK_MEMBERSHIP\+CATALOG_LIST$/.test(intention);
  if (!_isDetailIntention && !_isDetailPairedList && !_isCatalogDetailFallback) return null;
  // userInput 에 DETAIL keyword (상세/내역/이력/정보) + subject (홀딩/연장/환불/카드/포인트/락커/출석/회원권/상품/...)
  // 둘 다 매칭되어야 DETAIL intent 추정 — LIST/CATALOG keyword 보유 시 list/catalog 의도라 통과
  if (_CATALOG_LIST_KEYWORD_RE.test(userInput)) return null;
  if (!_VAGUE_DETAIL_KEYWORD_RE.test(userInput)) return null;
  if (!_VAGUE_DETAIL_SUBJECT_RE.test(userInput)) return null;
  // 사람 이름 후보 — 한글 2-4자 token 중 blacklist (도메인 noun + 동사 ending) 제외 + 영문 2+ token
  const _korCandidates = (userInput.match(/[가-힣]{2,4}/g) || [])
    .filter(w => !_VAGUE_DETAIL_NAME_BLACKLIST.has(w))
    .filter(w => !_VAGUE_DETAIL_VERB_ENDING_RE.test(w));
  const _engCandidates = userInput.match(/[A-Za-z]{2,20}/g) || [];
  if (_korCandidates.length > 0 || _engCandidates.length > 0) return null;
  // 전화번호 — phone token 있으면 통과
  if (/01\d[\- ]?\d{3,4}[\- ]?\d{4}/.test(userInput)) return null;
  // subject + keyword 둘 다 있고 회원명/전화번호 후보 0 → DETAIL intent + 회원명 부재
  const _subj = userInput.match(_VAGUE_DETAIL_SUBJECT_RE)?.[0] || '';
  // CATALOG_LIST fallback path 는 별도 메시지 — catalog vs member-detail 양쪽 옵션 명시 (member-detail 우선 권장)
  if (_isCatalogDetailFallback) {
    return `DETAIL intent ("${_subj} 상세/내역/정보") + 회원명/전화번호 부재 — 특정 회원의 ${_subj} 상세인지 ${_subj} 종류 catalog 인지 모호`;
  }
  return `DETAIL intent ("${_subj} 상세/내역/이력") + 회원명/전화번호 부재 — 어느 회원의 정보인지 모호`;
}

export function detectEntityCatalogMismatch(userInput, buffer, intention) {
  if (!userInput || typeof userInput !== 'string') return null;
  if (!buffer || !Array.isArray(buffer.items) || buffer.items.length === 0) return null;
  if (!intention || typeof intention !== 'string') return null;

  if (!/CATALOG_LIST/.test(intention)) return null;

  if (buffer.items[0] && typeof buffer.items[0] === 'object' && '_count' in buffer.items[0]) return null;

  if (_ENTITY_GUARD_PATTERNS.listIntent.test(userInput)) return null;

  if (_ENTITY_GUARD_PATTERNS.filterSort.test(userInput)) return null;

  const tokens = (userInput.match(/[\p{L}\p{N}]{2,}/gu) || []);

  const entityCandidates = tokens.filter(t => {
    if (_ENTITY_GUARD_PATTERNS.intentVerb.test(t)) return false;
    if (_ENTITY_GUARD_PATTERNS.categoryNoun.test(t)) return false;
    if (_ENTITY_GUARD_PATTERNS.descriptor.test(t)) return false;
    if (_ENTITY_GUARD_PATTERNS.interrogative.test(t)) return false;
    if (_ENTITY_GUARD_PATTERNS.verbEnding.test(t)) return false;

    for (let trim = 1; trim <= 2; trim++) {
      if (t.length > trim) {
        const stem = t.slice(0, -trim);

        if (stem.length >= 2 && _ENTITY_GUARD_PATTERNS.categoryNoun.test(stem)) return false;
      }
    }

    return true;
  });

  if (entityCandidates.length === 0) return null;

  const catalogTexts = buffer.items.map(it => {
    if (!it || typeof it !== 'object') return '';
    return Object.values(it)
      .filter(v => typeof v === 'string' && v.trim() && v.length < 100)
      .join(' ').toLowerCase();
  }).join(' | ');

  const matched = entityCandidates.filter(n => catalogTexts.includes(n.toLowerCase()));
  if (matched.length > 0) return null;

  const catalogPreview = buffer.items.slice(0, 7).map(it => {
    if (!it || typeof it !== 'object') return null;
    return Object.values(it).filter(v => typeof v === 'string' && v.trim() && v.length < 60)[0] || null;
  }).filter(Boolean);

  return { userNouns: entityCandidates, catalogPreview };
}

// ============================================
// Speech Register Detection (Korean: 반말/존댓말 감지)
// ============================================
// 사용자 입력의 speech register를 감지하여 LLM 응답 톤 일치를 유도.
// 반말 감지 시 동적 system message inject용.
// dual sync: nodeAgent.mjs + useAiChat.js (AgentSession.js 경유)

const INFORMAL_ENDINGS = /(?:야\??|어\??|지\??|냐\??|뭐야\??|해줘|알려줘|보여줘|줘\b|잘\s*돼\??|돼\??|거야\??|건데|인데|거든|잖아|하자|쓸래|볼래|할래|만큼|이야\??|래\??|나\??|다\??|대\??|게\??|데\??|거\??|감\??|함\??|임\??|됨\??|음\??|걸\??)$/;
const FORMAL_ENDINGS = /(?:요\??|습니다\??|니다\??|세요\??|시죠\??|십시오\??|주세요|합니다\??|됩니다\??|입니다\??|까요\??)$/;

export function detectSpeechRegister(input) {
  if (!input || typeof input !== 'string') return null;
  const trimmed = input.trim().replace(/[.!~]+$/, '');
  if (trimmed.length < 2) return null;

  if (INFORMAL_ENDINGS.test(trimmed)) return 'informal';
  if (FORMAL_ENDINGS.test(trimmed)) return 'formal';
  return null;
}

export function buildSpeechRegisterInject(input) {
  const register = detectSpeechRegister(input);
  if (register !== 'informal') return null;

  return `[SPEECH REGISTER — INFORMAL MODE ACTIVE] The user is speaking informally (Korean: 반말/구어체). You MUST respond informally throughout this turn:
- Use informal endings: -야/-어/-지/-거야/-인데 (NEVER -요/-습니다/-세요)
- intro example: "코치별 수업 성과 정리했어" (NOT "정리했어요")
- outro example: "전체 N명이야" (NOT "N명이에요/N명입니다")
- loading message example: "확인해볼게!" (NOT "확인해볼게요!")
- This applies to ALL text: intro, outro, finish response, and loading messages.
- Do NOT mix registers — every sentence must use informal endings.`
;
}

// 보이지 않는/제어 문자 판별 //
function isInvisibleChar(ch) {
  const cp = ch.codePointAt(0);
  if (cp <= 0x1F) return true;
  if (cp >= 0x7F && cp <= 0x9F) return true;
  if (cp === 0x00AD || cp === 0x034F || cp === 0x061C || cp === 0x180E || cp === 0xFEFF) return true;
  if (cp >= 0x200B && cp <= 0x200F) return true;
  if (cp >= 0x2028 && cp <= 0x202F) return true;
  if (cp >= 0x2060 && cp <= 0x206F) return true;

  return false;
}

// 비정상 입력 감지 — 깨진 한글 자모, 보이지 않는 문자 등으로만 구성된 입력 차단 //
export function isGarbledInput(text) {
  if (!text || typeof text !== 'string') return false;

  const trimmed = text.trim();

  if (trimmed.length === 0) return false;

  let visible = '';

  for (const ch of trimmed) {
    if (!isInvisibleChar(ch)) {
      visible += ch;
    }
  }

  visible = visible.trim();

  if (visible.length === 0) return true;

  const withoutNeutral = visible.replace(/[\s\d.,!?@#$%^&*()_+\-=\[\]{};':"\\|<>/~`]/g, '');

  if (withoutNeutral.length === 0) return false;

  const MEANINGFUL_CHAR = /[가-힣a-zA-Z一-鿿㐀-䶿]/;
  const EMOTICON_JAMO = /[ㅋㅎㅠㅜㅡㅇㄴ]/;
  const ANY_JAMO = /[ㄱ-ㅣ]/;

  let meaningfulCount = 0;
  let emoticonJamoCount = 0;
  let otherJamoCount = 0;
  let unknownCount = 0;

  for (const ch of withoutNeutral) {
    if (MEANINGFUL_CHAR.test(ch)) {
      meaningfulCount++;
    }
    else if (EMOTICON_JAMO.test(ch)) {
      emoticonJamoCount++;
    }
    else if (ANY_JAMO.test(ch)) {
      otherJamoCount++;
    }
    else {
      unknownCount++;
    }
  }

  const totalChars = meaningfulCount + emoticonJamoCount + otherJamoCount + unknownCount;

  if (meaningfulCount > 0) return false;

  if (emoticonJamoCount > 0 && otherJamoCount === 0 && unknownCount === 0) return false;

  if (totalChars > 0) return true;

  return false;
}

export const GARBLED_INPUT_MESSAGE = '입력이 깨진 것 같아요. 다시 입력해 주세요!';

// ============================================
// [WEEKLY FREQUENCY FILTER NORMALIZER]
// 사용자가 "주 N회 이상/이하" 의도로 출석 빈도를 묻는 경우, LLM이 attendedCount 절대값으로
// 잘못 환산하거나 ATTENDED 의도(min:1)을 빠뜨리는 본질을 코드 단계에서 보정.
// 사용 시점: applyFilter 진입부.
// 입력: { field, condition }, userInput(string), buf (dateRange 포함)
// 반환: { args: 변환된 args, applied: boolean, note: string|null }
// ============================================
export function normalizeAttendanceFilterArgs(args, userInput, buf) {
  if (!args || !args.field || !args.condition || !args.condition.type) {
    return { args, applied: false, note: null };
  }

  if (args.field !== 'attendedCount') return { args, applied: false, note: null };

  const text = typeof userInput === 'string' ? userInput : '';
  if (!text) return { args, applied: false, note: null };

  // "주 N회" / "주당 N회" / "주에 N회" / "주 N번" 패턴
  // 명시적 "주 N회 이상/이하/미만/초과" + "주 N회" + 함께 attended/zero 의도
  const weeklyMatch = text.match(/주\s*(\d+)\s*(?:회|번)/);
  const weeklyDoseMatch = text.match(/주\s*(\d+)\s*(?:회|번)\s*(이상|이하|미만|초과)/);
  if (!weeklyMatch) return { args, applied: false, note: null };

  const weeklyN = parseInt(weeklyMatch[1], 10);
  if (!Number.isFinite(weeklyN) || weeklyN <= 0) return { args, applied: false, note: null };

  const startDate = buf?.dateRange?.startDate;
  const endDate = buf?.dateRange?.endDate;

  let numberOfWeeks;
  if (startDate && endDate) {
    try {
      const startMs = new Date(startDate).getTime();
      const endMs = new Date(endDate).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
        return { args, applied: false, note: null };
      }
      const days = (endMs - startMs) / (1000 * 60 * 60 * 24) + 1;
      numberOfWeeks = Math.max(1, Math.round(days / 7));
    } catch {
      return { args, applied: false, note: null };
    }
  } else {
    // [FALLBACK] dateRange 미세팅 (LLM이 startDate/endDate 없이 call_api 호출) → defaultPeriod -30d~today 가정 (4주)
    // ATTENDANCE_LIST_BY+PERIOD의 backend defaultPeriod {start:'-30d', end:'today'} → round(31/7)=4
    numberOfWeeks = 4;
  }

  // 의도 분류
  // "이상/초과" → numeric_gte/gt, "이하/미만" → numeric_lte/lt
  const cond = args.condition;
  const expectedMax = weeklyN * numberOfWeeks;
  const direction = weeklyDoseMatch?.[2] || null; // 명시 키워드 없으면 condition.type에 의존

  // 1) numeric_lte/numeric_lt — "주 N회 이하/미만"
  // 2) numeric_gte/numeric_gt — "주 N회 이상/초과"
  // 3) range — 이미 정확히 표현된 경우는 통과
  // 4) exact — "주 0회" 같은 경우만 의미. 변환 대상 아님.

  let newCondition = null;
  let appliedNote = null;

  // "나온/출석한/다닌" — ATTENDED 의도 (zero 제외)
  const attendedIntent = /나온|출석한|다닌|참석한|왔/.test(text);
  // "안 나온/안 한/결석/미출석" — NOT ATTENDED
  const notAttendedIntent = /안\s*나온|안\s*나오|안\s*한|결석|미출석|출석.{0,3}안/.test(text);

  if (cond.type === 'numeric_lte' || cond.type === 'numeric_lt') {
    // value가 weeklyN과 같거나 더 작은 경우 → LLM이 환산 안 함, 보정 필요
    // value가 이미 weeklyN × numberOfWeeks 근처 (±1) → 환산됨, 보정 불필요
    const currentValue = cond.value;
    if (typeof currentValue === 'number') {
      const tolerance = 1; // ±1 허용 (round 오차)
      const alreadyConverted = Math.abs(currentValue - expectedMax) <= tolerance;

      if (!alreadyConverted && currentValue <= weeklyN + tolerance) {
        // 환산 안 됨: value = N×weeks로 보정
        if (attendedIntent && !notAttendedIntent) {
          newCondition = { type: 'range', min: 1, max: expectedMax };
          appliedNote = `[WEEKLY-FREQ NORMALIZE] "주 ${weeklyN}회 이하 (나온)" + ${numberOfWeeks}주 → range(min:1, max:${expectedMax}). 원본 condition=${JSON.stringify(cond)}.`;
        } else {
          newCondition = { type: 'numeric_lte', value: expectedMax };
          appliedNote = `[WEEKLY-FREQ NORMALIZE] "주 ${weeklyN}회 이하" + ${numberOfWeeks}주 → numeric_lte:${expectedMax}. 원본 condition=${JSON.stringify(cond)}.`;
        }
      } else if (attendedIntent && !notAttendedIntent) {
        // 환산은 정상이지만 ATTENDED 의도인데 0회 포함 → range로 보강
        newCondition = { type: 'range', min: 1, max: currentValue };
        appliedNote = `[WEEKLY-FREQ NORMALIZE] "주 ${weeklyN}회 이하 (나온)" → range(min:1) 보강. 원본 condition=${JSON.stringify(cond)}.`;
      }
    }
  } else if (cond.type === 'numeric_gte' || cond.type === 'numeric_gt') {
    const currentValue = cond.value;
    if (typeof currentValue === 'number') {
      const tolerance = 1;
      const alreadyConverted = Math.abs(currentValue - expectedMax) <= tolerance;
      if (!alreadyConverted && currentValue <= weeklyN + tolerance) {
        newCondition = { type: 'numeric_gte', value: expectedMax };
        appliedNote = `[WEEKLY-FREQ NORMALIZE] "주 ${weeklyN}회 이상" + ${numberOfWeeks}주 → numeric_gte:${expectedMax}. 원본 condition=${JSON.stringify(cond)}.`;
      }
    }
  } else if (cond.type === 'range') {
    // range는 이미 사용자가 의도대로 표현했을 가능성. 단, attended 의도 + min:0/null 시 min:1 보강
    if (attendedIntent && !notAttendedIntent && (cond.min == null || cond.min === 0)) {
      const newMax = (typeof cond.max === 'number' && cond.max <= weeklyN + 1) ? expectedMax : cond.max;
      newCondition = { type: 'range', min: 1, max: newMax };
      appliedNote = `[WEEKLY-FREQ NORMALIZE] "주 ${weeklyN}회 (나온)" range → min:1 보강. 원본 condition=${JSON.stringify(cond)}.`;
    }
  }

  if (newCondition) {
    return {
      args: { ...args, condition: newCondition },
      applied: true,
      note: appliedNote,
    };
  }

  return { args, applied: false, note: null };
}

// ============================================
// [LESSON METRIC FILTER NORMALIZER]
// per-LESSON aggregate intentions (e.g. CHECK_RESERVE+ATTENDANCE_STATISTICS groupBy=lesson)는
// 3개의 유사 metric field를 동시에 노출: totalSessions(수업 개설/진행 횟수, lesson-side) /
// attendedCount(출석 횟수, member-side) / totalReservations(예약 건수). 자연어 한국어에서 사용자가
// "참여/출석/수강 횟수 N회 이상"이라 말하면 attendedCount, "예약 건수 N건 이상"이면 totalReservations,
// "수업 개설/진행/열린 횟수 N회 이상"이면 totalSessions가 의도. LLM이 "참여 횟수"를 totalSessions로
// 잘못 매핑하는 INTENT_MISMATCH가 본질. prompt 가이드만으로는 미작동 (cycle 1 가이드 prompt 무효).
// 결정성 보장 위해 코드 단계에서 user input semantics와 filter field 일치 검사 → 불일치 시 redirect.
//
// 사용 시점: applyFilter 진입부 (normalizeAttendanceFilterArgs 직후).
// 입력: { field, condition }, userInput(string), buf
// 반환: { args: 변환된 args, applied: boolean, note: string|null }
// ============================================
export function normalizeLessonMetricFilterArgs(args, userInput, buf) {
  if (!args || !args.field || !args.condition) {
    return { args, applied: false, note: null };
  }

  const text = typeof userInput === 'string' ? userInput : '';
  if (!text) return { args, applied: false, note: null };

  // metric field group — per-lesson aggregate intentions에서 공존하는 3개 metric
  const METRIC_FIELDS = new Set(['totalSessions', 'attendedCount', 'totalReservations']);
  if (!METRIC_FIELDS.has(args.field)) return { args, applied: false, note: null };

  // user input semantics 분류 (한국어 자연어 매칭)
  // attendance-semantic: "참여/출석/수강 횟수" (member-side attendance count)
  const attendanceSemantic = /(참여|출석|수강|참석|나온|다닌)\s*(?:횟수|건수|수)/.test(text);
  // reservation-semantic: "예약 건수/예약 횟수" (booking transaction count)
  const reservationSemantic = /예약\s*(?:건수|횟수|수)/.test(text);
  // session-semantic: "수업 개설/진행/열린 횟수" (how many times class was held)
  const sessionSemantic = /(개설|진행|열린|개강|개최)\s*(?:횟수|건수|수)?|수업\s*(?:이|을|을\s*몇\s*번|이\s*몇\s*번)/.test(text)
    || /(?:몇\s*번|N\s*회)\s*(?:진행|개설|열린|열려|진행됐|진행했)/.test(text);

  // 분류 우선순위: session-semantic이 명시되면 신뢰 (예: "수업 진행 횟수 + 출석" → session 우선)
  // 그렇지 않으면 attendance/reservation semantic으로 redirect 후보
  let intendedField = null;
  let semanticLabel = null;

  if (sessionSemantic) {
    intendedField = 'totalSessions';
    semanticLabel = '수업 개설/진행 횟수 (lesson-side session count)';
  } else if (reservationSemantic) {
    intendedField = 'totalReservations';
    semanticLabel = '예약 건수 (booking transaction count)';
  } else if (attendanceSemantic) {
    intendedField = 'attendedCount';
    semanticLabel = '참여/출석 횟수 (member-side attendance count)';
  } else {
    // 매칭되는 한국어 semantic 없음 → 보정 안 함
    return { args, applied: false, note: null };
  }

  // 이미 user semantic과 일치하는 field 선택 — 보정 불필요
  if (args.field === intendedField) return { args, applied: false, note: null };

  // intendedField가 실제 데이터에 존재하는지 검증 (없으면 redirect 위험)
  const items = buf?.items || buf?.originalItems || [];
  if (!items.length) return { args, applied: false, note: null };
  const sample = items[0] || {};
  if (!(intendedField in sample)) return { args, applied: false, note: null };

  // 원래 field도 실제 데이터에 존재한다는 가정 (그렇지 않으면 fieldMissing path로 흐름) — 검증 보조
  // (fieldMissing path와 충돌 방지: 원래 field도 존재할 때만 swap. 둘 다 존재해야 redirect 안전)
  if (!(args.field in sample)) return { args, applied: false, note: null };

  const note = `[LESSON-METRIC NORMALIZE] user input "${text.slice(0, 40)}" semantics=${semanticLabel} → filter field "${args.field}" → "${intendedField}". 원본 field=${args.field}.`;

  return {
    args: { ...args, field: intendedField },
    applied: true,
    note,
  };
}

// ============================================
// normalizeEnumFilterValue — Korean → enum value 자동 변환
//
// 사용자가 한국어 어휘 ("남자/여자/활성/만료 등")로 enum 필드 filter exact 호출 시
// 실제 데이터의 영문/boolean enum 값으로 자동 swap. LLM이 한국어 그대로 전달했을 때
// 0건 매칭 + enum hint 텍스트가 finalText로 누출되는 문제 차단 (compacting 후 enumValues
// 잊어버려도 코드 단에서 보장).
//
// 본질: enum 카테고리는 시스템 단위 universal (gender, isActive, status 등) — 도메인 무관.
// 한국어→영문 매핑은 구조 기반 lookup (case-id/input-string hardcode 아님).
// 매칭 조건: condition.type='exact' + 단일 string value + buf.items에 field 존재 + actual enum에 사용자 value 매칭 X + 한국어→enum 후보 매칭 O + unique match.
//
// 사용 시점: applyFilter 진입부 (normalizeAttendanceFilterArgs 직후, exact 매칭 전).
// 입력: { field, condition }, buf
// 반환: { args: 변환된 args, applied: boolean, note: string|null }
// ============================================

// 한국어 → enum value 카테고리별 후보 lookup (universal, 시스템 단위 enum types).
// 다국어 비호환 발생 시 확장 가능 (예: 영어 사용자 "male/female" 직접 입력은 swap 불필요 — 매칭됨).
const ENUM_KOREAN_LOOKUP = {
  // gender enum: 영문 string
  gender: {
    male: ['남자', '남성', '남', '남자만', '남성만', '남자분', '남성분', '남자회원', '남성회원'],
    female: ['여자', '여성', '여', '여자만', '여성만', '여자분', '여성분', '여자회원', '여성회원'],
    // 백엔드 single-char enum form 대비
    M: ['남자', '남성', '남'],
    F: ['여자', '여성', '여'],
  },
  // boolean enum (isActive, isRefund, hasAttendance 등) — 공통 패턴
  // → 별도 일반화 (field name 무관 boolean)
};

// boolean Korean lookup (field 무관 — actual values가 [true, false]면 적용)
const BOOLEAN_KOREAN_LOOKUP = {
  true: ['활성', '유효', '진행', '진행중', '재직', '참여'],
  false: ['비활성', '만료', '종료', '미진행', '미참여'],
};

export function normalizeEnumFilterValue(args, buf) {
  if (!args || !args.field || !args.condition) {
    return { args, applied: false, note: null };
  }
  if (args.condition.type !== 'exact') return { args, applied: false, note: null };

  const userValue = args.condition.value;
  // single string only — array/number 등은 skip
  if (typeof userValue !== 'string' || !userValue) return { args, applied: false, note: null };

  const items = buf?.items || buf?.originalItems || [];
  if (!items.length) return { args, applied: false, note: null };

  // 실제 enum 값 추출 (field 또는 alias) — field 부재 시 skip (fieldMissing path가 처리)
  let fieldNames;
  try {
    // ENUM_KOREAN_LOOKUP과 같이 다양한 alias 가능 — getFieldNames import 가능
    fieldNames = typeof getFieldNames === 'function' ? getFieldNames(args.field) : [args.field];
  } catch (_) {
    fieldNames = [args.field];
  }
  const actualValues = [...new Set(
    items.flatMap(it => fieldNames.map(fn => it && it[fn]).filter(v => v != null))
  )];
  if (actualValues.length === 0) return { args, applied: false, note: null };

  // 사용자 value가 이미 actual enum 중 하나면 swap 불필요
  if (actualValues.includes(userValue)) return { args, applied: false, note: null };

  // 정규화 (공백/조사 strip)
  const userNorm = userValue.replace(/\s+/g, '').replace(/[을를이가은는도의로만에서와과]$/g, '');

  // 후보 매칭 — field-specific lookup 우선, 아니면 boolean lookup
  const candidates = [];

  // 1) field-specific Korean lookup (gender 등)
  // [PREFIX-STRIP] 비교/그룹 merge 후 date-prefixed 필드 (예: "4월_gender", "5월_gender") 대응 —
  //   "<prefix>_<base>" 형태이면 base 부분으로도 lookup 시도. 다국어/도메인 무관 universal pattern.
  //   compare_period_prefix, _change_, _changeRate_ 등 기존 merge convention과 호환.
  const baseFieldCandidates = new Set([args.field]);
  const _underscoreMatch = typeof args.field === 'string' ? args.field.match(/^(.+?)_(.+)$/) : null;
  if (_underscoreMatch) {
    // "<prefix>_<base>" → base 추가 (예: "4월_gender" → "gender")
    baseFieldCandidates.add(_underscoreMatch[2]);
    // "_change_<base>" / "_changeRate_<base>" 패턴: 첫 segment "_change" 같은 reserved prefix는 skip
    const _stripChange = args.field.replace(/^_(change|changeRate)_/, '');
    if (_stripChange !== args.field) baseFieldCandidates.add(_stripChange);
  }
  let fieldLookup = null;
  for (const _candField of baseFieldCandidates) {
    if (ENUM_KOREAN_LOOKUP[_candField]) {
      fieldLookup = ENUM_KOREAN_LOOKUP[_candField];
      break;
    }
  }
  if (fieldLookup) {
    for (const [enumVal, korTerms] of Object.entries(fieldLookup)) {
      // 실제 데이터에 존재하는 enum 값만 후보로
      if (!actualValues.includes(enumVal)) continue;
      if (korTerms.some(t => userNorm === t || userNorm.includes(t))) {
        candidates.push(enumVal);
      }
    }
  }

  // 2) boolean enum (universal — field name 무관, actual values가 boolean 집합이면)
  if (candidates.length === 0) {
    const hasTrue = actualValues.includes(true);
    const hasFalse = actualValues.includes(false);
    if (hasTrue && hasFalse) {
      if (BOOLEAN_KOREAN_LOOKUP.true.some(t => userNorm === t || userNorm.includes(t))) candidates.push(true);
      if (BOOLEAN_KOREAN_LOOKUP.false.some(t => userNorm === t || userNorm.includes(t))) candidates.push(false);
    }
  }

  // unique match만 적용 (다중 후보면 ambiguity — 원본 유지)
  const uniqueCandidates = [...new Set(candidates)];
  if (uniqueCandidates.length !== 1) return { args, applied: false, note: null };

  const translated = uniqueCandidates[0];
  const note = `[ENUM-FILTER NORMALIZE] field=${args.field}, user value="${userValue}" → actual enum value=${JSON.stringify(translated)}. actual values: [${actualValues.slice(0, 5).join(', ')}]`;

  return {
    args: { ...args, condition: { ...args.condition, value: translated } },
    applied: true,
    note,
  };
}
